/**
 * !Note[DND]: This file uses JSX elements also that's why *tsx is being used an extension
 */

import Highcharts from "highcharts";
import { FiEye, FiTruck } from "react-icons/fi";
import { ReactComponent as AiIcon } from "@images/ai_icon.svg";
import {
    BsBarChart,
    BsBarChartLine,
    BsBasket2,
    BsBoxes,
    BsBoxSeam,
    BsColumns,
    BsGraphUpArrow,
    BsPieChart,
    BsUiChecksGrid,
} from "react-icons/bs";
import { SlLocationPin } from "react-icons/sl";
import { VscSymbolKeyword } from "react-icons/vsc";
import {
    RevstackPoCategoryAnalysisWidget,
    RevstackCategoryPerformanceWidget,
    RevstackBrandwiseDistributionWidget,
    RevstackTablePerformanceWidget,
    RevstackSovTrendsWidget,
    RevstackSovDistributionWidget,
    RevstackSovPieWidget,
    RevstackGenericParentContainer,
} from "@components/widgets";
import {
    RevstackCASectionIds,
    RevstackCubeIds,
    RevstackDashboardModules,
    RevstackDashboardSubModules,
    WidgetType,
    CommonWidgetMetrics,
    Channels,
    CA_DD_MODULES,
    WidgetDimensions,
    RevstackFilterTypes,
    GraphTypes,
    TablePerfColDefType,
    OptionValues,
    RevstackFilterKey,
    SubModule,
    Platforms,
    TSelectedDateRange,
    REVSTACK_INFO_NOTES,
} from "@ts/common-types";
import {
    ALL_OPTIONS,
    REVSTACK_CHART_COLORS,
    REVSTACK_MEASURES,
    REVSTACK_TRENDS_CHART_COLORS,
    REVSTACK_TRENDS_LIGHT_CHART_COLORS,
    REVSTACK_W7_TABLE_NO_DATA_MESSAGES,
} from "@ts/constants";
import { capitalizeSpecificSubstring, titleCase } from "@ts/utils";
import { map } from "underscore";
import { updateRevstackDashboardTabs } from "@/features/revstackWidgetsSlice";
import store from "@/store";
import {
    getRevstackDDTableColDefs,
    getTablePerformanceColDefs,
    getTablePerformanceExpandedColDefs,
} from "./constants";
import { getPreparedMetric } from "./helpers";
import RevstackGenericChartParentContainer from "@/components/widgets/RevstackGenericChartParentContainer";
import {
    productAnalysisMultiRowTooltipConfig,
    skuCDTooltipFormatter,
    skuTooltipFormatter,
    trendsChartConfig,
} from "./ui-utils";

const getTabConfig = (
    tabs: (RevstackDashboardSubModules | CA_DD_MODULES)[]
) => {
    const aiInsights = {
        key: CA_DD_MODULES.AI_INSIGHTS,
        label: (
            <div
                className="d-flex align-items-baseline ai-insights-tab"
                style={{
                    minWidth: "fit-content",
                    whiteSpace: "nowrap",
                }}
            >
                <AiIcon
                    style={{
                        height: "16px",
                        width: "16px",
                        flexShrink: 0,
                        marginTop: "2px",
                    }}
                />
                <span className="ai-tab-text">AI Insights</span>
            </div>
        ),
    };

    const baseTabs = map(
        tabs,
        (tab: RevstackDashboardSubModules | CA_DD_MODULES) => {
            if (tab === CA_DD_MODULES.AI_INSIGHTS) {
                return aiInsights;
            }
            return {
                key: tab,
                label: titleCase(tab),
            };
        }
    );

    return baseTabs;
};

const createBaseConfig = (
    filterToAppend: string,
    metricType: string,
    module: RevstackDashboardModules | SubModule,
    subModule:
        | RevstackDashboardSubModules
        | SubModule = RevstackDashboardSubModules.OVERVIEW,
    currencySymbol = "",
    selectedSku = "",
    selectedDateRange: TSelectedDateRange
) => {
    const metricTypeLabel = metricType === "by_value" ? "Value" : "Volume";

    const configSections = {
        [RevstackCASectionIds.PO]: {
            icon: <></>,
            title: "Performance Overview",
            sectionId: RevstackCASectionIds.PO,
            widget: RevstackPoCategoryAnalysisWidget,
            config: {
                layout: {
                    type: "container",
                    rowSpan: 1,
                    colSpan: 24,
                    className: "mb-3",
                },
                isTrendsButtonRequired: false,
            },
            widgets: [
                [
                    {
                        identifier: "po-ms",
                        title: "Market Share",
                        subTitle: titleCase(metricType),
                        type: WidgetType.W1,
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        infoMessage:
                            REVSTACK_MEASURES[CommonWidgetMetrics.MARKET_SHARE]
                                .description,
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "po-ov-sov",
                        title: "Overall SOV",
                        type: WidgetType.W1,
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        infoMessage:
                            REVSTACK_MEASURES[CommonWidgetMetrics.OVERALL_SOV]
                                .description,
                        measures: [CommonWidgetMetrics.OVERALL_SOV],
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "po-wa",
                        title: "Wt. Availability%",
                        type: WidgetType.W1,
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        infoMessage:
                            REVSTACK_MEASURES[
                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY
                            ].description,
                        measures: [CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                        excludeFilter: ["brand_id"],
                    },
                ],
                [
                    {
                        identifier: "po-traffic-laggards",
                        type: WidgetType.W2,
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        title: "Traffic",
                        icon: <FiEye size={16} />,
                        config: {
                            drilldown: {
                                title: "Top 3 laggards",
                                dimensions: [
                                    WidgetDimensions.VALUE,
                                    WidgetDimensions.CHANGE,
                                    WidgetDimensions.NAME,
                                ],
                                limit: 3,
                                hasFilter: true,
                            },
                            filterOptions: [
                                {
                                    label: "Overall SOV",
                                    value: CommonWidgetMetrics.OVERALL_SOV,
                                    excludeAsRow: true,
                                    unit: "%",
                                },
                                {
                                    label: "Organic SOV",
                                    value: CommonWidgetMetrics.ORGANIC_SOV,
                                    unit: "%",
                                },
                                {
                                    label: "Paid SOV",
                                    value: CommonWidgetMetrics.PAID_SOV,
                                    unit: "%",
                                },
                                {
                                    label: "Branded Search",
                                    value: CommonWidgetMetrics.BRANDED_SEARCH,
                                    unit: "%",
                                },
                            ],
                            defaultFilterOption: {
                                label: "Overall SOV",
                                value: CommonWidgetMetrics.OVERALL_SOV,
                                unit: "%",
                            },
                            drilldownEntryKey: "subcategory",
                        },
                        measures: [
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                        ],
                        queryDimensions: ["subcategory"],
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "po-conversion-laggards",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W2,
                        title: "Conversion",
                        icon: <BsBasket2 size={16} />,
                        config: {
                            drilldown: {
                                title: "Top 3 laggards",
                                dimensions: [
                                    WidgetDimensions.VALUE,
                                    WidgetDimensions.CHANGE,
                                    WidgetDimensions.NAME,
                                ],
                                limit: 3,
                                hasFilter: true,
                            },
                            filterOptions: [
                                {
                                    label: "RPI",
                                    value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    unit: "",
                                },
                                {
                                    label: "Rating",
                                    value: CommonWidgetMetrics.AVERAGE_RATING,
                                    unit: "",
                                },
                                {
                                    label: "Content Score",
                                    value: CommonWidgetMetrics.CONTENT_SCORE,
                                    unit: "",
                                },
                            ],
                            defaultFilterOption: {
                                label: "RPI",
                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                unit: "%",
                            },
                            drilldownEntryKey: "subcategory",
                        },
                        measures: [
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                        ],
                        queryDimensions: ["subcategory"],
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "po-operations-laggards",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W2,
                        title: "Operations",
                        icon: <FiTruck size={16} />,
                        config: {
                            drilldown: {
                                title: "Top 3 laggards",
                                dimensions: [
                                    WidgetDimensions.VALUE,
                                    WidgetDimensions.CHANGE,
                                    WidgetDimensions.NAME,
                                ],
                                limit: 3,
                                hasFilter: true,
                            },
                            filterOptions: [
                                {
                                    label: "Wt. Availability",
                                    value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    excludeAsRow: true,
                                },
                                {
                                    label: "Stock in Hand",
                                    value: CommonWidgetMetrics.STOCK_IN_HAND,
                                    unit: "Day(s)",
                                },
                                {
                                    label: "Fill Rate",
                                    value: CommonWidgetMetrics.FILL_RATE,
                                    unit: "%",
                                },
                                {
                                    label: "Listing Status",
                                    value: CommonWidgetMetrics.LISTING_STATUS,
                                    unit: "%",
                                },
                            ],
                            defaultFilterOption: {
                                label: "Wt. Availability",
                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                unit: "%",
                            },
                            drilldownEntryKey: "subcategory",
                        },
                        measures: [
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.LISTING_STATUS,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                        ],
                        queryDimensions: ["subcategory"],
                        excludeFilter: ["brand_id"],
                    },
                ],
            ],
        },
        [RevstackCASectionIds.CP]: {
            icon: <BsColumns size={16} />,
            title: "Brand(s) Performance",
            sectionId: RevstackCASectionIds.CP,
            widget: RevstackCategoryPerformanceWidget,
            config: {
                layout: {
                    type: "container",
                    rowSpan: 1,
                    colSpan: 12,
                    className: "mb-3",
                },
                filters: [
                    {
                        type: RevstackFilterTypes.CHART_TYPE,
                        defaultFilter: GraphTypes.TREND,
                        filterOptions: [
                            {
                                value: GraphTypes.TREND,
                                icon: (
                                    <BsGraphUpArrow size={16} color="#009FE3" />
                                ),
                            },
                            {
                                value: GraphTypes.BAR,
                                icon: (
                                    <BsBarChartLine size={16} color="#009FE3" />
                                ),
                            },
                        ],
                    },
                ],
                isTrendsButtonRequired: false,
            },
            widgets: [
                [
                    {
                        identifier: "cp-trends",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W3,
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        queryDimensions: ["category"],
                        queryMetaInfo: {
                            resultKey: "category_overview_trends_data",
                            primaryDimension: "category",
                        },
                        relativePeriodConfig: {
                            granularity: "month",
                            value: 6,
                        },
                        excludeFilter: ["brand_id"],
                        config: {
                            graphAttributes: {
                                yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                yAxisUnit: "%",
                                hideXAxisTitle: true,
                            },
                            chartDimensions: {
                                minHeight: 100,
                                maxHeight: 220,
                                // The heightPercent controls the chart's height in the above sepcified min and max height,
                                // taking the viewPort height into account
                                heightPercent: 0.26,
                            },
                        },
                    },
                    {
                        identifier: "cp-bar",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W4,
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        queryDimensions: ["category"],
                        excludeFilter: ["brand_id"],
                        queryMetaInfo: {
                            primaryDimension: "category",
                            resultKey: "category_overview_bar_chart_data",
                            isPreviousComparisonRequired: true,
                        },
                        config: {
                            subTitle: `By ${metricTypeLabel}`,

                            graphAttributes: {
                                yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                yAxisUnit: "%",
                                hideXAxisTitle: true,
                            },
                            chartDimensions: {
                                minHeight: 100,
                                maxHeight: 270,
                                heightPercent: 0.36,
                            },
                        },
                    },
                ],
            ],
        },
        [RevstackCASectionIds.BD]: {
            icon: <BsPieChart size={16} />,
            title: "Brandwise Distribution",
            sectionId: RevstackCASectionIds.BD,
            widget: RevstackBrandwiseDistributionWidget,
            config: {
                layout: {
                    type: "container",
                    rowSpan: 1,
                    colSpan: 12,
                    className: "mb-3",
                },
                filters: [
                    {
                        type: RevstackFilterTypes.RADIO,
                        defaultFilter: GraphTypes.PIE,
                        filterOptions: [
                            {
                                value: GraphTypes.PIE,
                                icon: <BsPieChart size={16} color="#009FE3" />,
                            },
                            {
                                value: GraphTypes.TREND,
                                icon: (
                                    <BsGraphUpArrow size={16} color="#009FE3" />
                                ),
                            },
                        ],
                    },
                ],
            },
            widgets: [
                [
                    {
                        identifier: "bd-pie",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W5,
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        config: {
                            dataKeyMap: {
                                brand: "name",
                            },
                            showList: true,
                        },
                        queryDimensions: ["brand", "brand_id"],
                        queryMetaInfo: {
                            primaryDimension: "brand",
                            filterToSkip: "brand_id",
                            resultKey: "brand_pie_chart_data",
                            listLimit: 4,
                            responseConfig: {
                                primaryKey: "Selected Brand(s)",
                                secondaryKey: "Others",
                            },
                        },
                        isExcludeDimension: true,
                    },
                    {
                        identifier: "bd-trends",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W3,
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        queryDimensions: ["brand", "brand_id"],
                        queryMetaInfo: {
                            filterToSkip: "brand_id",
                            primaryDimension: "brand",
                            resultKey: "brand_overview_trends_data",
                            listLimit: 4,
                            responseConfig: {
                                primaryKey: "Selected Brand(s)",
                                secondaryKey: "Others",
                            },
                        },
                        relativePeriodConfig: {
                            granularity: "month",
                            value: 6,
                        },
                        isExcludeDimension: true,
                        config: {
                            chartDimensions: {
                                minHeight: 100,
                                maxHeight: 240,
                                heightPercent: 0.25,
                            },
                            graphAttributes: {
                                hideXAxisTitle: true,
                            },
                        },
                    },
                ],
            ],
        },
        [RevstackCASectionIds.SP]: {
            icon: <BsBoxes size={16} />,
            title: "Sub Category Performance",
            sectionId: RevstackCASectionIds.SP,
            drilldownId: RevstackCASectionIds.SP,
            widget: RevstackTablePerformanceWidget,
            config: {
                layout: {
                    type: "container",
                    rowSpan: 1,
                    colSpan: 24,
                    className: "mb-3",
                },
                isTrendsButtonRequired: false,
                isDrillDownButtonRequired: true,
                module: RevstackDashboardModules.CATEGORY_ANALYSIS,
            },
            widgets: [
                [
                    {
                        identifier: "scp-cards",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W6,
                        isTopCardRequired: true,
                        config: {
                            card: {
                                cardType: "subcategory",
                                cardId: "subcategory_id",
                                primaryMetricLabel: "Mkt. Share",
                                primaryMetricKey: "mktShare",
                                primaryMetricUnit: "%",
                                secondaryMetricLabel: "Cat. Cont.",
                                secondaryMetricKey: "catCont",
                                secondaryMetricUnit: "%",
                                isSortFilterRequired: true,
                                topCard: {
                                    id: ALL_OPTIONS.value,
                                    name: `${ALL_OPTIONS.label} Subcategories`,
                                    color: "#919191",
                                    primaryMetricKey: `market_share${filterToAppend}`,
                                },
                            },
                            dataKeyMap: {
                                mktShare: CommonWidgetMetrics.MARKET_SHARE,
                                catCont: `category_contribution${filterToAppend}`,
                            },
                            relatedComponentDataKeys: [
                                "subcategory_sku_table_data_non_percent",
                                "subcategory_sku_table_data_percent",
                            ],
                        },
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        queryDimensions: [
                            "subcategory",
                            "subcategory_id",
                            "category",
                        ],
                        queryMetaInfo: {
                            resultKey: "brand_subcat_list",
                            sortMeasure: CommonWidgetMetrics.MARKET_SHARE,
                            contributionMeasures: [
                                `category_contribution${filterToAppend}`,
                            ],
                        },
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "scp-table",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W7,
                        config: {
                            table: {
                                colDefs:
                                    getTablePerformanceColDefs(filterToAppend)[
                                        `ca_${subModule}_subcategory` as TablePerfColDefType
                                    ],
                                expandedColDefs:
                                    getTablePerformanceExpandedColDefs(
                                        filterToAppend
                                    )[
                                        `ca_${subModule}_subcategory` as TablePerfColDefType
                                    ],
                                style: {
                                    height: 400,
                                    expandedHeight: 360,
                                },
                                isExpandable: true,
                                isTitleRequired: true,
                                type: `ca_${subModule}_subcategory`,
                                filters: [
                                    {
                                        type: RevstackFilterTypes.RADIO,
                                        defaultFilter: "laggards",
                                        filterOptions: [
                                            {
                                                label: "Laggards",
                                                value: "laggards",
                                                sortOrder: "asc",
                                                filterConfig: {
                                                    condition: {
                                                        operater: "lessThan",
                                                        value: 0,
                                                    },
                                                    field: `market_share${filterToAppend}_change`,
                                                },
                                            },
                                            {
                                                label: "Leaders",
                                                value: "leaders",
                                                sortOrder: "desc",
                                                filterConfig: {
                                                    condition: {
                                                        operater: "greaterThan",
                                                        value: 0,
                                                    },
                                                    field: `market_share${filterToAppend}_change`,
                                                },
                                            },
                                        ],
                                    },
                                ],
                                dataKeyMap: {
                                    sku_id: "sku",
                                    product: "sku_title",
                                    category: "category",
                                    product_url: "product_url",
                                    thumbnail: "thumbnail",
                                    isPowerSku: "power_sku",
                                    subcategoryId: "subcategory_id",
                                    marketShare: `market_share${filterToAppend}_value`,
                                    marketShareChange: `market_share${filterToAppend}_change`,
                                    overallSOV: `overall_sov_value`,
                                    overallSOVChange: `overall_sov_change`,
                                    wtAvailability: `weighted_availability_value`,
                                    wtAvailabilityChange: `weighted_availability_change`,
                                },
                                drilldown: {
                                    dataKeyMap: {
                                        traffic: [
                                            {
                                                name: "Organic SOV",
                                                measure:
                                                    CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                name: "Paid SOV",
                                                measure:
                                                    CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            {
                                                name: "Branded Search",
                                                measure:
                                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                                unit: "%",
                                            },
                                        ],
                                        conversion: [
                                            {
                                                name: "RPI",
                                                measure:
                                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                name: "Rating",
                                                measure:
                                                    CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                name: "Content Score",
                                                measure:
                                                    CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        operations: [
                                            {
                                                name: "Stock in Hand",
                                                measure:
                                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "day",
                                            },
                                            {
                                                name: "Listing Status",
                                                measure:
                                                    CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        },
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                        ],
                        queryDimensions: [
                            "category",
                            "sku",
                            "sku_title",
                            "product_url",
                            "thumbnail",
                        ],
                        queryMetaInfo: {
                            baseFilter: "overall_sov_value",
                            resultKey: "subcategory_sku_table_data",
                            recordKey: "sku",
                            sortConfig: {
                                sortKey: `market_share${filterToAppend}_change`,
                                limit: 5,
                            },
                        },
                        excludeFilter: ["brand_id"],
                        isExcludeDimension: true,
                    },
                ],
            ],
        },
        [RevstackCASectionIds.RWP]: {
            icon: <SlLocationPin size={16} />,
            title: "Regional Level Performance",
            sectionId: RevstackCASectionIds.RWP,
            widget: RevstackTablePerformanceWidget,
            config: {
                layout: {
                    type: "container",
                    rowSpan: 1,
                    colSpan: 24,
                    className: "mb-3",
                },
                isTrendsButtonRequired: false,
                isDrillDownButtonRequired: false,
            },
            widgets: [
                [
                    {
                        identifier: "rwp-cards",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W6,
                        isTopCardRequired: true,
                        config: {
                            card: {
                                type: `${subModule}_regional`,
                                cardType: "regional",
                                cardId: "region",
                                primaryMetricLabel: "Mkt. Share",
                                primaryMetricKey: "mktShare",
                                primaryMetricUnit: "%",
                                secondaryMetricLabel: "Reg. Cont.",
                                secondaryMetricKey: "regCont",
                                secondaryMetricUnit: "%",
                                isSortFilterRequired: false,
                                topCard: {
                                    id: ALL_OPTIONS.value,
                                    name: "NATIONAL",
                                    color: "#919191",
                                    primaryMetricKey: `market_share${filterToAppend}`,
                                },
                            },
                            dataKeyMap: {
                                mktShare: CommonWidgetMetrics.MARKET_SHARE,
                                regCont: `region_contribution${filterToAppend}`,
                                regional: "region",
                            },
                            relatedComponentDataKeys: [
                                "region_subcategory_table_data_non_percent",
                                "region_subcategory_table_data_percent",
                            ],
                        },
                        measures: [CommonWidgetMetrics.MARKET_SHARE],
                        queryDimensions: ["region"],
                        queryMetaInfo: {
                            resultKey: "region_list",
                            sortMeasure: CommonWidgetMetrics.MARKET_SHARE,
                            contributionMeasures: [
                                `region_contribution${filterToAppend}`,
                            ],
                            measureId: "region",
                            dimValueToTruncate: "-Region",
                        },
                        excludeFilter: ["brand_id"],
                    },
                    {
                        identifier: "rwp-table",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        type: WidgetType.W7,
                        config: {
                            table: {
                                type: `ca_${subModule}_regional`,
                                colDefs:
                                    getTablePerformanceColDefs(filterToAppend)[
                                        `ca_${subModule}_regional` as TablePerfColDefType
                                    ],
                                expandedColDefs:
                                    getTablePerformanceExpandedColDefs(
                                        filterToAppend
                                    )[
                                        `ca_${subModule}_regional` as TablePerfColDefType
                                    ],
                                style: {
                                    height: 400,
                                    expandedHeight: 360,
                                },
                                isExpandable: true,
                                isTitleRequired: true,
                                filters: [
                                    {
                                        type: RevstackFilterTypes.RADIO,
                                        defaultFilter: "laggards",
                                        filterOptions: [
                                            {
                                                label: "Laggards",
                                                value: "laggards",
                                                sortOrder: "asc",
                                                filterConfig: {
                                                    condition: {
                                                        operater: "lessThan",
                                                        value: 0,
                                                    },
                                                    field: `market_share${filterToAppend}_change`,
                                                },
                                            },
                                            {
                                                label: "Leaders",
                                                value: "leaders",
                                                sortOrder: "desc",
                                                filterConfig: {
                                                    condition: {
                                                        operater: "greaterThan",
                                                        value: 0,
                                                    },
                                                    field: `market_share${filterToAppend}_change`,
                                                },
                                            },
                                        ],
                                    },
                                ],
                                dataKeyMap: {
                                    sku_id: "sku",
                                    subCategory: "subcategory",
                                    category: "category",
                                    link: "link",
                                    isPowerSku: "power_sku",
                                    subcategoryId: "subcategory_id",
                                    categoryShare: `category_contribution${filterToAppend}_value`,
                                    categoryShareChange: `category_contribution${filterToAppend}_change`,
                                    marketShare: `market_share${filterToAppend}_value`,
                                    marketShareChange: `market_share${filterToAppend}_change`,
                                    overallSOV: `overall_sov_value`,
                                    overallSOVChange: `overall_sov_change`,
                                    wtAvailability: `weighted_availability_value`,
                                    wtAvailabilityChange: `weighted_availability_change`,
                                },
                                drilldown: {
                                    dataKeyMap: {
                                        traffic: [
                                            {
                                                name: "Organic SOV",
                                                measure:
                                                    CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                name: "Paid SOV",
                                                measure:
                                                    CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                        ],
                                        conversion: [
                                            {
                                                name: "RPI",
                                                measure:
                                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                name: "Rating",
                                                measure:
                                                    CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                name: "Content Score",
                                                measure:
                                                    CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        operations: [
                                            {
                                                name: "Stock in Hand",
                                                measure:
                                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "day",
                                            },
                                            {
                                                name: "Listing Status",
                                                measure:
                                                    CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        },
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                        ],
                        queryDimensions: ["category", "subcategory"],
                        queryMetaInfo: {
                            laggardConfig: {
                                condition: {
                                    operater: "lessThan",
                                    value: 0,
                                },
                                field: `market_share${filterToAppend}_change`,
                            },
                            leaderConfig: {
                                condition: {
                                    operater: "greaterThan",
                                    value: 0,
                                },
                                field: `market_share${filterToAppend}_change`,
                            },
                            resultKey: "region_subcategory_table_data",
                            baseFilter: "overall_sov_value",
                            recordKey: "subcategory",
                            sortConfig: {
                                sortKey: `market_share${filterToAppend}_change`,
                                limit: 5,
                            },
                            contributionMeasures: [
                                `category_contribution${filterToAppend}`,
                            ],
                        },
                        excludeFilter: ["brand_id"],
                    },
                ],
            ],
        },
    };

    if (module === RevstackDashboardModules.CATEGORY_ANALYSIS) {
        store.dispatch(
            updateRevstackDashboardTabs({
                module: module,
                tabs: getTabConfig([
                    RevstackDashboardSubModules.OVERVIEW,
                    RevstackDashboardSubModules.TRAFFIC,
                    RevstackDashboardSubModules.CONVERSION,
                    RevstackDashboardSubModules.OPERATIONS,
                    RevstackDashboardSubModules.AI_INSIGHTS,
                ]),
            })
        );

        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return configSections;
        }
        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                [RevstackCASectionIds.TO]: {
                    ...configSections[RevstackCASectionIds.PO],
                    title: "Traffic Overview",
                    widgets: [
                        [
                            {
                                identifier: "to-olsov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Overall SOV%",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.OVERALL_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Overall SOV",
                                        value: CommonWidgetMetrics.OVERALL_SOV,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "to-pov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Paid SOV%",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.PAID_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.PAID_SOV],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Paid SOV",
                                            value: CommonWidgetMetrics.PAID_SOV,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Paid SOV",
                                        value: CommonWidgetMetrics.PAID_SOV,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "to-osov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Organic SOV%",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.ORGANIC_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.ORGANIC_SOV],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Organic SOV",
                                            value: CommonWidgetMetrics.ORGANIC_SOV,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Organic SOV",
                                        value: CommonWidgetMetrics.ORGANIC_SOV,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.SOVT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.SOVT,
                    icon: <BsGraphUpArrow size={16} />,
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        defaultYAxisFilter: CommonWidgetMetrics.All,
                    },
                    widgets: [
                        [
                            {
                                identifier: "to-sov-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                ],
                                queryDimensions: ["category"],
                                relativePeriodConfig: {
                                    granularity: "month",
                                    value: 6,
                                },
                                queryMetaInfo: {
                                    resultKey: "category_traffic_trends_data",
                                    primaryDimension: "category",
                                    //to generate consolidated data for all categories combined
                                    pivotConfig: {
                                        pivotKey: "category",
                                    },
                                },
                                excludeFilter: ["brand_id"],
                                config: {
                                    graphAttributes: {
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                    },
                                    allFilterOptionKey: "Categories",
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                    metricKey: "name",
                                    filterOptionsOn: "category",
                                    graphColors: REVSTACK_TRENDS_CHART_COLORS,
                                },
                            },
                        ],
                    ],
                    title: "SOV Trends",
                },
                [RevstackCASectionIds.SOVD]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.SOVD,
                    icon: <BsBarChart size={16} />,
                    widget: RevstackSovDistributionWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "to-sovd-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                ],
                                queryDimensions: ["category"],
                                excludeFilter: ["brand_id"],
                                queryMetaInfo: {
                                    primaryDimension: "category",
                                    resultKey:
                                        "category_traffic_bar_chart_data",
                                    isPreviousComparisonRequired: false,
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "SOV%",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.32,
                                    },
                                },
                            },
                        ],
                    ],
                    title: "SOV Distribution",
                },
                [RevstackCASectionIds.SV]: {
                    ...configSections[RevstackCASectionIds.BD],
                    sectionId: RevstackCASectionIds.SV,
                    title: "Search Volume",
                    icon: <BsPieChart size={16} />,
                    widget: RevstackSovPieWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "to-sv-pie",
                                cubeId: RevstackCubeIds.KA_NATIONAL,
                                type: WidgetType.W5,
                                measures: [
                                    CommonWidgetMetrics.SEARCH_INDEX,
                                    CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
                                    CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
                                    CommonWidgetMetrics.KEYWORD_PAID_SOV,
                                ],
                                config: {
                                    dataKeyMap: {
                                        brand: "name",
                                    },
                                    showList: false,
                                    filterKey: "Categories",
                                    customTooltip: true,
                                    customToolTipConfig: {
                                        tooltipDataKey: "sovData",
                                        toolTipTitleSuffix: "Search",
                                    },
                                },
                                queryDimensions: ["intent"],
                                queryMetaInfo: {
                                    primaryDimension: "intent",
                                    primaryMeasure:
                                        CommonWidgetMetrics.SEARCH_INDEX,
                                    resultKey: "traffic_sv_pie_chart_data",
                                    tooltipKey: "sovData",
                                },
                                excludeFilter: ["brand_filter"],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.MBVC]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "Competition Trends",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: {
                            ...configSections[RevstackCASectionIds.CP].config
                                .filters,
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "mbvc-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                queryDimensions: ["brand", "brand_id"],
                                queryMetaInfo: {
                                    filterToSkip: "brand_id",
                                    primaryDimension: "brand",
                                    resultKey: "brand_traffic_trends_data",
                                    listLimit: 4,
                                    responseConfig: {
                                        primaryKey: "Selected Brand(s)",
                                        secondaryKey: "Others",
                                    },
                                },
                                relativePeriodConfig: {
                                    granularity: "month",
                                    value: 6,
                                },
                                isExcludeDimension: true,
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "Overall SOV%",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "mbvc-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                ],
                                queryDimensions: ["brand", "brand_id"],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    filterToSkip: "brand_id",
                                    primaryDimension: "brand",
                                    resultKey:
                                        "brand_vs_comp_traffic_bar_chart_data",
                                    listLimit: 4,
                                    isPreviousComparisonRequired: false,
                                    responseConfig: {
                                        primaryKey: "Selected Brand(s)",
                                        secondaryKey: "Others",
                                    },
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "SOV%",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.SP]: {
                    ...configSections[RevstackCASectionIds.SP],
                    widgets: [
                        [
                            {
                                identifier: "scp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        cardType: "subcategory",
                                        cardId: "subcategory_id",
                                        primaryMetricLabel: "Ovr. SOV",
                                        primaryMetricKey: "ovrSOV",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: true,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: `${ALL_OPTIONS.label} Subcategories`,
                                            color: "#919191",
                                            primaryMetricKey:
                                                CommonWidgetMetrics.OVERALL_SOV,
                                            secondaryMetricKey:
                                                CommonWidgetMetrics.MARKET_SHARE,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        ovrSOV: CommonWidgetMetrics.OVERALL_SOV,
                                    },
                                    relatedComponentDataKeys: [
                                        "subcategory_sku_sov_table_data_non_percent",
                                        "subcategory_sku_sov_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: [
                                    "subcategory",
                                    "subcategory_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: "brand_subcat_list_sov",
                                    sortMeasure:
                                        CommonWidgetMetrics.OVERALL_SOV,
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "scp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_subcategory`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        isTitleRequired: true,
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            product: "sku_title",
                                            category: "category",
                                            product_url: "product_url",
                                            thumbnail: "thumbnail",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            organicSOV: `organic_sov_value`,
                                            organicSOVChange: `organic_sov_change`,
                                            paidSOV: `paid_sov_value`,
                                            paidSOVChange: `paid_sov_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Branded Search",
                                                        measure:
                                                            CommonWidgetMetrics.BRANDED_SEARCH,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "Average Pricing",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_PRICE,
                                                        unit: "₹",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                ],
                                queryDimensions: [
                                    "category",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                ],
                                queryMetaInfo: {
                                    baseFilter: "overall_sov_value",
                                    resultKey: "subcategory_sku_sov_table_data",
                                    recordKey: "sku",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                        limit: 5,
                                    },
                                },
                                excludeFilter: ["brand_id"],
                                isExcludeDimension: true,
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.KP]: {
                    icon: <VscSymbolKeyword size={16} />,
                    title: "Keyword Performance",
                    sectionId: RevstackCASectionIds.KP,
                    drilldownId: RevstackCASectionIds.KP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        isTrendsButtonRequired: false,
                        isTableOnlyMode: true,
                        filters: [
                            {
                                type: RevstackFilterTypes.SELECT,
                                defaultFilter: OptionValues.ALL,
                                dataKey: "intent",
                                filterOptions: [
                                    {
                                        label: "All Keywords",
                                        value: OptionValues.ALL,
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "kp-table",
                                cubeId: RevstackCubeIds.KA_NATIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_keyword`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_keyword` as TablePerfColDefType
                                            ],
                                        isExpandable: false,
                                        style: {
                                            height: 340,
                                        },
                                        dataKeyMap: {
                                            keyword: "keyword",
                                            keywordType: "intent",
                                            indexedSearchVolume: `search_index_value`,
                                            overallSOV: `keyword_overall_sov_value`,
                                            overallSOVChange: `keyword_overall_sov_change`,
                                            paidSOV: `keyword_paid_sov_value`,
                                            paidSOVChange: `keyword_paid_sov_change`,
                                            organicSOV: `keyword_organic_sov_value`,
                                            organicSOVChange: `keyword_organic_sov_change`,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.SEARCH_INDEX,
                                    CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
                                    CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
                                    CommonWidgetMetrics.KEYWORD_PAID_SOV,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: [
                                    "keyword",
                                    "subcategory",
                                    "intent",
                                ],
                                queryMetaInfo: {
                                    baseFilter: "keyword_overall_sov_value",
                                    resultKey: `keyword_${subModule}_table_data`,
                                    recordKey: "keyword",
                                    sortConfig: {
                                        sortKey: `search_index_value`,
                                    },
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    ...configSections[RevstackCASectionIds.RWP],
                    title: "Regional Level Overall Visibility Performance",
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: `${subModule}_regional`,
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Ovr. SOV",
                                        primaryMetricKey: "ovrSOV",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey: `overall_sov`,
                                            secondaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        ovrSOV: CommonWidgetMetrics.OVERALL_SOV,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_subcategory_sov_table_data_non_percent",
                                        "region_subcategory_sov_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    resultKey: "region_list_sov",
                                    sortMeasure:
                                        CommonWidgetMetrics.OVERALL_SOV,
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        isTitleRequired: true,
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            subCategory: "subcategory",
                                            category: "category",
                                            link: "link",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            categoryShare: `category_contribution${filterToAppend}_value`,
                                            categoryShareChange: `category_contribution${filterToAppend}_change`,
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            organicSOV: `organic_sov_value`,
                                            organicSOVChange: `organic_sov_change`,
                                            paidSOV: `paid_sov_value`,
                                            paidSOVChange: `paid_sov_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Branded Search",
                                                        measure:
                                                            CommonWidgetMetrics.BRANDED_SEARCH,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "Average Pricing",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_PRICE,
                                                        unit: "₹",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                ],
                                queryDimensions: ["category", "subcategory"],
                                queryMetaInfo: {
                                    resultKey:
                                        "region_subcategory_sov_table_data",
                                    recordKey: "subcategory",
                                    baseFilter: "overall_sov_value",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                    },
                                    customMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                [RevstackCASectionIds.CO]: {
                    ...configSections[RevstackCASectionIds.PO],
                    title: "Conversion Overview",
                    widgets: [
                        [
                            {
                                identifier: "co-rpi",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "RPI",
                                subTitle: "By PPU",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "co-ap",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Avg Price",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.AVERAGE_PRICE
                                    ].description,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "co-wd",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Wt. Discount",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                ],
                                excludeFilter: ["brand_id"],
                            },
                        ],
                        [
                            {
                                identifier: "co-rpi-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "RPI",
                                subTitle: "Overall",
                                icon: <FiEye size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX
                                    ].description,
                                hasfilters: false,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "RPI",
                                        value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "co-ar-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "Avg. Rating",
                                icon: <BsBasket2 size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.AVERAGE_RATING
                                    ].description,
                                hasfilters: false,
                                measures: [CommonWidgetMetrics.AVERAGE_RATING],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Avg. Rating",
                                            value: CommonWidgetMetrics.AVERAGE_RATING,
                                            unit: "",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Avg. Rating",
                                        value: CommonWidgetMetrics.AVERAGE_RATING,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "co-cs-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "Content Score",
                                icon: <FiTruck size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.CONTENT_SCORE
                                    ].description,
                                hasFilters: false,
                                measures: [CommonWidgetMetrics.CONTENT_SCORE],
                                excludeFilter: ["brand_id"],
                                config: {
                                    isComingSoon: true,
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Content Score",
                                            value: CommonWidgetMetrics.CONTENT_SCORE,
                                            unit: "",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Content Score",
                                        value: CommonWidgetMetrics.CONTENT_SCORE,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CRT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.CRT,
                    // widget: RevstackSovTrendsWidget,
                    widgets: [
                        [
                            {
                                identifier: "co-crt-w3",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                queryDimensions: ["category"],
                                relativePeriodConfig: {
                                    granularity: "month",
                                    value: 6,
                                },
                                queryMetaInfo: {
                                    resultKey:
                                        "category_conversion_trends_data",
                                    primaryDimension: "category",
                                },
                                excludeFilter: ["brand_id"],
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: `RPI (Overall)`,
                                        yAxisUnit: "",
                                        hideXAxisTitle: true,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        // The heightPercent controls the chart's height in the above sepcified min and max height,
                                        // taking the viewPort height into account
                                        heightPercent: 0.29,
                                    },
                                },
                            },
                            {
                                identifier: "co-crt-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                queryDimensions: ["category"],
                                excludeFilter: ["brand_id"],
                                queryMetaInfo: {
                                    primaryDimension: "category",
                                    resultKey:
                                        "category_conversion_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: `RPI (Overall)`,
                                        yAxisUnit: "",
                                        hideXAxisTitle: true,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                },
                            },
                        ],
                    ],
                    title: "Category RPI Trends",
                },
                [RevstackCASectionIds.COMPT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.COMPT,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                    },
                    widget: RevstackBrandwiseDistributionWidget,
                    widgets: [
                        [
                            {
                                identifier: "co-compt-line",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                queryDimensions: ["brand_id", "brand"],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    filterToSkip: "brand_id",
                                    primaryDimension: "brand",
                                    resultKey: "brand_conversion_trends_data",
                                    isAvgAggregationQuery: true,
                                    listLimit: 4,
                                    responseConfig: {
                                        primaryKey: "Selected Brand(s)",
                                        secondaryKey: "Others",
                                    },
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: `RPI (Overall)`,
                                        yAxisUnit: "",
                                        hideXAxisTitle: true,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 220,
                                        heightPercent: 0.26,
                                    },
                                },
                            },
                        ],
                    ],
                    title: "Competition Trends",
                },
                [RevstackCASectionIds.SP]: {
                    ...configSections[RevstackCASectionIds.SP],
                    widgets: [
                        [
                            {
                                identifier: "scp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        cardType: "subcategory",
                                        cardId: "subcategory_id",
                                        primaryMetricLabel: "RPI",
                                        primaryMetricKey:
                                            CommonWidgetMetrics.RPI,
                                        primaryMetricUnit: "",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: true,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: `${ALL_OPTIONS.label} Subcategories`,
                                            color: "#919191",
                                            primaryMetricKey:
                                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            secondaryMetricKey:
                                                CommonWidgetMetrics.MARKET_SHARE,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        [CommonWidgetMetrics.RPI]:
                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    },
                                    relatedComponentDataKeys: [
                                        "subcategory_sku_rpi_table_data_non_percent",
                                        "subcategory_sku_rpi_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                queryDimensions: [
                                    "subcategory",
                                    "subcategory_id",
                                ],
                                queryMetaInfo: {
                                    resultKey:
                                        "brand_subcat_list_rpi_operations",
                                    sortMeasure:
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "scp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_subcategory`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        isTitleRequired: true,
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            product: "sku_title",
                                            category: "category",
                                            product_url: "product_url",
                                            thumbnail: "thumbnail",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            rpi: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_value`,
                                            rpiChange: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`,
                                            avgPricing: `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            avgPricingChange: `${CommonWidgetMetrics.AVERAGE_PRICE}_change`,
                                            avgRating: `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            wtDiscount: `${CommonWidgetMetrics.WEIGHTED_DISCOUNT}_value`,
                                            wtDiscountChange: `${CommonWidgetMetrics.WEIGHTED_DISCOUNT}_change`,
                                            avgRatingChange: `${CommonWidgetMetrics.AVERAGE_RATING}_change`,
                                            contentScore: `${CommonWidgetMetrics.CONTENT_SCORE}_value`,
                                            contentScoreChange: `${CommonWidgetMetrics.CONTENT_SCORE}_change`,
                                        },
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "RPI (by PPU)",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                traffic: [
                                                    {
                                                        name: "Overall SOV",
                                                        measure:
                                                            CommonWidgetMetrics.OVERALL_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Fill Rate",
                                                        measure:
                                                            CommonWidgetMetrics.FILL_RATE,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: [
                                    "category",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                ],
                                queryMetaInfo: {
                                    baseFilter: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`,
                                    resultKey: "subcategory_sku_rpi_table_data",
                                    recordKey: "sku",
                                    sortConfig: {
                                        sortKey: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`,
                                        limit: 5,
                                    },
                                },
                                excludeFilter: ["brand_id"],
                                isExcludeDimension: true,
                            },
                        ],
                    ],
                },

                [RevstackCASectionIds.RWP]: {
                    ...configSections[RevstackCASectionIds.RWP],
                    title: "Regional Level Overall Visibility Performance",
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: `${subModule}_regional`,
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "RPI",
                                        primaryMetricKey:
                                            CommonWidgetMetrics.RPI,
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey:
                                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            secondaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        rpi: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_subcategory_rpi_table_data_non_percent",
                                        "region_subcategory_rpi_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    resultKey: "region_list_rpi_operations",
                                    sortMeasure:
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            subCategory: "subcategory",
                                            category: "category",
                                            link: "link",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            categoryShare: `category_contribution${filterToAppend}_value`,
                                            categoryShareChange: `category_contribution${filterToAppend}_change`,
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            rpi: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_value`,
                                            rpiChange: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`,
                                            avgPricing: `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            avgPricingChange: `${CommonWidgetMetrics.AVERAGE_PRICE}_change`,
                                            avgRating: `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            avgRatingChange: `${CommonWidgetMetrics.AVERAGE_RATING}_change`,
                                            contentScore: `${CommonWidgetMetrics.CONTENT_SCORE}_value`,
                                            contentScoreChange: `${CommonWidgetMetrics.CONTENT_SCORE}_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "RPI (by PPU)",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                traffic: [
                                                    {
                                                        name: "Overall SOV",
                                                        measure:
                                                            CommonWidgetMetrics.OVERALL_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Fill Rate",
                                                        measure:
                                                            CommonWidgetMetrics.FILL_RATE,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: ["category", "subcategory"],
                                queryMetaInfo: {
                                    resultKey:
                                        "region_subcategory_rpi_table_data",
                                    recordKey: "subcategory",
                                    baseFilter: "overall_sov_value",
                                    sortConfig: {
                                        sortKey: `${CommonWidgetMetrics.RELATIVE_PRICE_INDEX}_change`,
                                    },
                                    customMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                [RevstackCASectionIds.OO]: {
                    ...configSections[RevstackCASectionIds.PO],
                    title: "Operations Overview",
                    widgets: [
                        [
                            [
                                {
                                    identifier: "op-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Wt. Availability",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics
                                                .WEIGHTED_AVAILABILITY
                                        ].description,

                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "op-ls",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Listing Status",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.LISTING_STATUS
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                            [
                                {
                                    identifier: "op-sih",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Stock in Hand",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.STOCK_IN_HAND
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "op-fr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.FILL_RATE
                                        ].description,
                                    title: "Fill Rate",
                                    icon: <FiEye size={16} />,
                                    hasfilters: false,
                                    measures: [CommonWidgetMetrics.FILL_RATE],
                                    queryDimensions: ["sku"],
                                    laggardField: "sku",
                                    excludeFilter: ["brand_id"],
                                    config: {
                                        isComingSoon: true,
                                        isComingSoonMessage: "Enable access",
                                    },
                                },
                            ],
                        ],
                        [
                            {
                                identifier: "co-rpi-w2",
                                type: WidgetType.W1,
                                title: "RPI",
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX
                                    ].description,
                                subTitle: "Overall",
                                icon: <FiEye size={16} />,
                                hasfilters: false,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                                config: {
                                    enableMetricAndGraph: false,
                                    drilldown: {
                                        title: "Top laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 5,
                                        hasFilter: true,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                        {
                                            label: "Listing Status",
                                            value: CommonWidgetMetrics.LISTING_STATUS,
                                            unit: "%",
                                        },
                                        {
                                            label: "Stock In Hand",
                                            value: CommonWidgetMetrics.STOCK_IN_HAND,
                                            unit: "%",
                                        },
                                        {
                                            label: "Fill Rate",
                                            value: CommonWidgetMetrics.FILL_RATE,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Wt. Availability",
                                        value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CRT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.CRT,
                    // widget: RevstackSovTrendsWidget,
                    widgets: [
                        [
                            {
                                identifier: "op-crt",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: ["category"],
                                relativePeriodConfig: {
                                    granularity: "month",
                                    value: 6,
                                },
                                excludeFilter: ["brand_id"],
                                queryMetaInfo: {
                                    resultKey: "category_operation_trends_data",
                                    primaryDimension: "category",
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "Wt. Availability (%)",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        heightPercent: 0.29,
                                    },
                                },
                            },
                            {
                                identifier: "op-crt-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: ["category"],
                                queryMetaInfo: {
                                    primaryDimension: "category",
                                    resultKey:
                                        "category_operation_bar_chart_data",
                                },
                                excludeFilter: ["brand_id"],
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "Wt. Availability (%)",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                },
                            },
                        ],
                    ],
                    title: "Category Availability Trends",
                },
                [RevstackCASectionIds.COMPT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    config: {
                        ...configSections[RevstackCASectionIds.CP].config,
                        filters: [] as any[],
                    },
                    sectionId: RevstackCASectionIds.COMPT,
                    widget: RevstackBrandwiseDistributionWidget,
                    widgets: [
                        [
                            {
                                identifier: "op-compt-line",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    CommonWidgetMetrics.LINE_AVL_PCT,
                                    CommonWidgetMetrics.LINE_AVG_7D_REVENUE,
                                ],
                                queryDimensions: ["brand_id", "brand"],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    filterToSkip: "brand_id",
                                    primaryDimension: "brand",
                                    resultKey: "brand_operation_trends_data",
                                    sortMeasure:
                                        CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    listLimit: 4,
                                    measureToAdjust:
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    responseConfig: {
                                        primaryKey: "Selected Brand(s)",
                                        secondaryKey: "Others",
                                    },
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "Wt. Availability (%)",
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                        maxYaxisValue: 100,
                                    },
                                    filterOptionsOn: "name",
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        heightPercent: 0.26,
                                    },
                                },
                            },
                        ],
                    ],
                    title: "Competition Trends",
                },
                [RevstackCASectionIds.SP]: {
                    ...configSections[RevstackCASectionIds.SP],
                    widgets: [
                        [
                            {
                                identifier: "scp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        cardType: "subcategory",
                                        cardId: "subcategory_id",
                                        primaryMetricLabel: "Wt. Availability",
                                        primaryMetricKey: "wtAvl",
                                        primaryMetricUnit: "",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: true,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: `${ALL_OPTIONS.label} Subcategories`,
                                            color: "#919191",
                                            primaryMetricKey:
                                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            secondaryMetricKey:
                                                CommonWidgetMetrics.MARKET_SHARE,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        wtAvl: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    },
                                    relatedComponentDataKeys: [
                                        "subcategory_sku_operations_table_data_non_percent",
                                        "subcategory_sku_operations_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: [
                                    "subcategory",
                                    "subcategory_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: "brand_subcat_list_wa",
                                    sortMeasure:
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "scp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                isTitleRequired: true,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_subcategory`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_subcategory` as TablePerfColDefType
                                            ],
                                        isTitleRequired: true,
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            product: "sku_title",
                                            category: "category",
                                            product_url: "product_url",
                                            thumbnail: "thumbnail",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            wtAvailability: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                                            wtAvailabilityChange: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`,
                                            listingStatus: `${CommonWidgetMetrics.LISTING_STATUS}_value`,
                                            listingStatusChange: `${CommonWidgetMetrics.LISTING_STATUS}_change`,
                                            stockInHand: `${CommonWidgetMetrics.STOCK_IN_HAND}_value`,
                                            stockInHandChange: `${CommonWidgetMetrics.STOCK_IN_HAND}_change`,
                                            fillRate: `${CommonWidgetMetrics.FILL_RATE}_value`,
                                            fillRateChange: `${CommonWidgetMetrics.FILL_RATE}_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                ],
                                                traffic: [
                                                    {
                                                        name: "Overall SOV",
                                                        measure:
                                                            CommonWidgetMetrics.OVERALL_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Avg. Price",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_PRICE,
                                                        unit: "₹",
                                                    },
                                                    {
                                                        name: "Wt. Discount",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                ],
                                queryDimensions: [
                                    "category",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                ],
                                queryMetaInfo: {
                                    baseFilter: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                                    resultKey:
                                        "subcategory_sku_operations_table_data",
                                    recordKey: "sku",
                                    sortConfig: {
                                        sortKey: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`,
                                        limit: 5,
                                    },
                                },
                                excludeFilter: ["brand_id"],
                                isExcludeDimension: true,
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    ...configSections[RevstackCASectionIds.RWP],
                    title: "Regional Level Overall Visibility Performance",
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: `${subModule}_regional`,
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Wt. Availability",
                                        primaryMetricKey: "wtAvl",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Mkt. Share",
                                        secondaryMetricKey: "mktShare",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey:
                                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            secondaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        wtAvl: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        mktShare: `market_share${filterToAppend}`,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_subcategory_wa_table_data_non_percent",
                                        "region_subcategory_wa_table_data_percent",
                                    ],
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    resultKey: "region_list_wa_operations",
                                    sortMeasure:
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        isExpandable: true,
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        dataKeyMap: {
                                            sku_id: "sku",
                                            subCategory: "subcategory",
                                            category: "category",
                                            link: "link",
                                            isPowerSku: "power_sku",
                                            subcategoryId: "subcategory_id",
                                            categoryShare: `category_contribution${filterToAppend}_value`,
                                            categoryShareChange: `category_contribution${filterToAppend}_change`,
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            wtAvailability: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_value`,
                                            wtAvailabilityChange: `${CommonWidgetMetrics.WEIGHTED_AVAILABILITY}_change`,
                                            listingStatus: `${CommonWidgetMetrics.LISTING_STATUS}_value`,
                                            listingStatusChange: `${CommonWidgetMetrics.LISTING_STATUS}_change`,
                                            stockInHand: `${CommonWidgetMetrics.STOCK_IN_HAND}_value`,
                                            stockInHandChange: `${CommonWidgetMetrics.STOCK_IN_HAND}_change`,
                                            fillRate: `${CommonWidgetMetrics.FILL_RATE}_value`,
                                            fillRateChange: `${CommonWidgetMetrics.FILL_RATE}_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                overview: [
                                                    {
                                                        name: "Wt. Availability",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                        unit: "%",
                                                    },
                                                ],
                                                traffic: [
                                                    {
                                                        name: "Overall SOV",
                                                        measure:
                                                            CommonWidgetMetrics.OVERALL_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Avg. Price",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_PRICE,
                                                        unit: "₹",
                                                    },
                                                    {
                                                        name: "Wt. Discount",
                                                        measure:
                                                            CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                ],
                                queryDimensions: ["category", "subcategory"],
                                queryMetaInfo: {
                                    resultKey:
                                        "region_subcategory_wa_table_data",
                                    recordKey: "subcategory",
                                    baseFilter: "overall_sov_value",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                    },
                                    customMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
            };
        }
    }

    if (module === RevstackDashboardModules.PRODUCT_ANALYSIS) {
        store.dispatch(
            updateRevstackDashboardTabs({
                module: module,
                tabs: getTabConfig([
                    RevstackDashboardSubModules.OVERVIEW,
                    RevstackDashboardSubModules.TRAFFIC,
                    RevstackDashboardSubModules.CONVERSION,
                    RevstackDashboardSubModules.OPERATIONS,
                    RevstackDashboardSubModules.AI_INSIGHTS,
                ]),
            })
        );

        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.BANNER]: {
                    icon: <></>,
                    title: "Product Analytics",
                    sectionId: RevstackCASectionIds.BANNER,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                            sticky: true,
                        },
                        isTrendsButtonRequired: false,
                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                    },
                    widgets: [
                        {
                            identifier: "po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            relativeTabKey: CA_DD_MODULES.OVERVIEW,
                            type: WidgetType.W0,
                            config: {
                                currencySymbol: currencySymbol,
                                banner: {
                                    formatterParams: {
                                        isCrossChannelComponent: true,
                                        metricsToAvg: [
                                            `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                        ],
                                        metricsToSum: ["reviews_count"],
                                    },
                                },
                            },
                            queryDimensions: [
                                CommonWidgetMetrics.SKU,
                                CommonWidgetMetrics.CHANNEL_ID,
                                CommonWidgetMetrics.CATEGORY,
                                CommonWidgetMetrics.SUB_CATEGORY,
                                CommonWidgetMetrics.BRAND,
                                CommonWidgetMetrics.SKU_TITLE,
                                CommonWidgetMetrics.PRODUCT_URL,
                                CommonWidgetMetrics.THUMBNAIL,
                            ],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.AVERAGE_PRICE,
                                CommonWidgetMetrics.AVERAGE_MRP,
                                CommonWidgetMetrics.AVERAGE_RATING,
                            ],
                            excludeFilter: [
                                CommonWidgetMetrics.BRAND_ID,
                                CommonWidgetMetrics.SKU,
                            ],
                            isExcludeDimension: true,
                            queryMetaInfo: {
                                resultKey: `sku_banner_data`,
                                recordKey: "sku",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.PO]: {
                    icon: <></>,
                    title: "Performance Overview",
                    sectionId: RevstackCASectionIds.PO,
                    widget: RevstackPoCategoryAnalysisWidget,
                    config: {
                        isTrendsButtonRequired: false,
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "po-ms",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Market Share",
                                subTitle: titleCase(metricType),
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.MARKET_SHARE
                                    ].description,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-ov-sov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Overall SOV",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.OVERALL_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Availability",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics
                                            .WEIGHTED_AVAILABILITY
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                        [
                            {
                                identifier: "po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Traffic",
                                icon: <FiEye size={16} />,
                                config: {
                                    disableDrillDown: true,
                                },
                                measures: [
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                ],
                                queryDimensions: ["sku"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Conversion",
                                icon: <BsBasket2 size={16} />,
                                config: {
                                    disableDrillDown: true,
                                },
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                ],
                                queryDimensions: ["sku"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Operations",
                                icon: <FiTruck size={16} />,
                                config: {
                                    disableDrillDown: true,
                                },
                                measures: [
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                                queryDimensions: ["sku"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.PP]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.SOVT,
                    icon: <VscSymbolKeyword size={16} />,
                    title: "Product Performance",
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        // !NOTE: To be changed during data integration
                        defaultYAxisFilter:
                            REVSTACK_MEASURES[CommonWidgetMetrics.OVERALL_SOV]
                                ?.label,
                        defaultOppositeYAxisFilter:
                            REVSTACK_MEASURES[CommonWidgetMetrics.PAID_SOV]
                                ?.label,
                    },
                    widgets: [
                        [
                            {
                                identifier: "pp-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT
                                ],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "product_performance_trends_data",
                                    primaryDimension: "sku",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    yAxisTitle: "Overall SOV%",
                                    filterKey: "Categories",
                                    dateFormat: "D/MM/YY",
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                    metricKey: "name",
                                    filterOptionsOn: "name",
                                    graphColors: [
                                        REVSTACK_CHART_COLORS.primary[700],
                                    ],
                                    filterYaxisAttribute: "name",
                                    filterYaxisOppositeAttribute: "name",
                                    graphAttributes: {
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.MSVC]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MSVC,
                    title: "My SKU vs Competition",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "msvc-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU,
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension: CommonWidgetMetrics.SKU,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_overview_bar_chart_data",
                                    listLimit: 5,
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    // category here refers to the chart data categories or names on the x-axis
                                    isCategoryFormattingRequired: false,
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    graphAttributes: {
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                        yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                        chartCategoriesDimension:
                                            CommonWidgetMetrics.SKU_TITLE,
                                        tooltipFormatter: function (
                                            this: Highcharts.TooltipFormatterContextObject
                                        ) {
                                            return skuTooltipFormatter.call(
                                                this,
                                                currencySymbol
                                            );
                                        },
                                        xAxisLabelMaxLength: 10,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        tooltipCategoryDimension:
                                            CommonWidgetMetrics.SKU,
                                        tooltipMetrics: [
                                            {
                                                label: "Overall SOV",
                                                key: "overall_sov_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Wt. Availability",
                                                key: "weighted_availability_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Price",
                                                key: "average_price_value",
                                                unit: currencySymbol,
                                            },
                                            {
                                                label: "Wt. Discount",
                                                key: "weighted_discount_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "RPI",
                                                key: "rpi_value",
                                                unit: "",
                                            },
                                        ],
                                        widgetConfig: [
                                            {
                                                identifier: "sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.OVERALL_SOV,
                                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                    CommonWidgetMetrics.RPI,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.SKU,
                                                    CommonWidgetMetrics.SKU_TITLE,
                                                    CommonWidgetMetrics.THUMBNAIL,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.SKU,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                        CommonWidgetMetrics.BRAND_ID,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.SKU,
                                                    recordKey:
                                                        CommonWidgetMetrics.SKU,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.SKU,
                                                ],
                                                isExcludeDimension: true,
                                            },
                                        ],
                                    },
                                },
                            },
                            {
                                identifier: "msvc-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension:
                                        CommonWidgetMetrics.SKU_TITLE,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_overview_trends_data",
                                    listLimit: 5,
                                    responseConfig: {
                                        primaryKey: "Selected SKU",
                                    },
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                config: {
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        hideXAxisTitle: true,
                                        yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                        tooltip:
                                            productAnalysisMultiRowTooltipConfig(
                                                "sku",
                                                selectedSku,
                                                "%"
                                            ),
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CD]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "Channel Distribution",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "cd-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: [
                                    CommonWidgetMetrics.CHANNEL_ID,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension:
                                        CommonWidgetMetrics.CHANNEL_ID,
                                    resultKey: "cd_overview_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                config: {
                                    formatForChannelId: true,
                                    subTitle: `By ${metricTypeLabel}`,
                                    graphAttributes: {
                                        yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                        yAxisUnit: "%",
                                        hideXAxisTitle: true,
                                        tooltipFormatter: skuCDTooltipFormatter,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        showPoints: false, // Set to true to show colored points in tooltip
                                        widgetConfig: [
                                            {
                                                identifier:
                                                    "cd_sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.OVERALL_SOV,
                                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                    CommonWidgetMetrics.RPI,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.CHANNEL_ID,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `cd_sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.BRAND_ID,
                                                        CommonWidgetMetrics.SKU,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    recordKey:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                ],
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: "market_share_by_value_mrp_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Overall SOV",
                                                key: "overall_sov_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Wt. Availability",
                                                key: "weighted_availability_value",
                                                unit: "%",
                                            },
                                            {
                                                type: "divider",
                                            },
                                            {
                                                label: "Price",
                                                key: "average_price_value",
                                                unit: currencySymbol,
                                            },
                                            {
                                                label: "Avg Discount",
                                                key: "weighted_discount_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "RPI",
                                                key: "rpi_value",
                                                unit: "",
                                            },
                                        ],
                                    },
                                },
                            },
                            {
                                identifier: "cd-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: [
                                    CommonWidgetMetrics.CHANNEL_ID,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension:
                                        CommonWidgetMetrics.CHANNEL_ID,
                                    resultKey: "cd_overview_trends_data",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                config: {
                                    formatForChannelId: true,
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        hideXAxisTitle: true,
                                        yAxisTitle: `Market Share (By ${metricTypeLabel})`,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    icon: <SlLocationPin size={16} />,
                    title: "Regional Level Performance",
                    sectionId: RevstackCASectionIds.RWP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        isDrillDownButtonRequired: true,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            defaultTitle: true,
                                        },
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            filterDataKey:
                                                CommonWidgetMetrics.REGION,
                                            filterValue: ["all"],
                                            fetchOptions: true,
                                            requestParams: {
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                queryId: "any-id-can-be-added",
                                                queryDimensions: [
                                                    RevstackFilterKey.REGION,
                                                ],
                                                isExcludeDimension: false,
                                                excludeFilter: [
                                                    RevstackFilterKey.BRAND,
                                                ],
                                                filterToSkip: "",
                                            },
                                            allOptions: {
                                                label: "National",
                                                value: OptionValues.ALL,
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: "regional",
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Mkt. Share",
                                        primaryMetricKey: "mktShare",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Reg. Cont.",
                                        secondaryMetricKey: "regCont",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        regCont: `region_contribution${filterToAppend}`,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_pa_overview_table_data_non_percent",
                                        "region_pa_overview_table_data_percent",
                                    ],
                                },
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "region_pa_list_overview",
                                    sortMeasure:
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    contributionMeasures: [
                                        `region_contribution${filterToAppend}`,
                                    ],
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `pa_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `pa_${subModule}_regional` as TablePerfColDefType
                                            ],
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        isExpandable: true,
                                        isTitleRequired: true,
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            city: "city",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            wtAvailability: `weighted_availability_value`,
                                            wtAvailabilityChange: `weighted_availability_change`,
                                            avgPrice: `average_price_value`,
                                            avgPriceChange: `average_price_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                traffic: [
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                ],
                                queryDimensions: ["city"],
                                queryMetaInfo: {
                                    laggardConfig: {
                                        condition: {
                                            operater: "lessThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    leaderConfig: {
                                        condition: {
                                            operater: "greaterThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    resultKey: "region_pa_overview_table_data",
                                    baseFilter: "overall_sov_value",
                                    recordKey: "city",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                        limit: 5,
                                    },
                                    contributionMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                [RevstackCASectionIds.BANNER]: {
                    icon: <></>,
                    title: "Product Analytics",
                    sectionId: RevstackCASectionIds.BANNER,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                            sticky: true,
                        },
                        isTrendsButtonRequired: false,
                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                    },
                    widgets: [
                        {
                            identifier: "po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            relativeTabKey: CA_DD_MODULES.OVERVIEW,
                            type: WidgetType.W0,
                            config: {
                                currencySymbol: currencySymbol,
                                banner: {
                                    formatterParams: {
                                        isCrossChannelComponent: true,
                                        metricsToAvg: [
                                            `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                        ],
                                        metricsToSum: ["reviews_count"],
                                    },
                                },
                            },
                            queryDimensions: [
                                "cross_channel_sku_id",
                                "channel_id",
                                "category",
                                "brand",
                                "sku",
                                "sku_title",
                                "product_url",
                                "thumbnail",
                            ],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.AVERAGE_PRICE,
                                CommonWidgetMetrics.AVERAGE_MRP,
                                CommonWidgetMetrics.AVERAGE_RATING,
                            ],
                            excludeFilter: [
                                CommonWidgetMetrics.BRAND_ID,
                                CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                            ],
                            isExcludeDimension: true,
                            queryMetaInfo: {
                                resultKey: `sku_banner_data`,
                                recordKey: "cross_channel_sku_id##channel_id",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.TO]: {
                    icon: <></>,
                    title: "Traffic Overview",
                    sectionId: RevstackCASectionIds.TO,
                    widget: RevstackPoCategoryAnalysisWidget,
                    config: {
                        isTrendsButtonRequired: false,
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "po-ms",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Overall SOV%",
                                subTitle: titleCase(metricType),
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.OVERALL_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-ov-sov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Paid SOV%",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.PAID_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.PAID_SOV],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Organic SOV%",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.ORGANIC_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.ORGANIC_SOV],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.SOVT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.SOVT,
                    icon: <BsGraphUpArrow size={16} />,
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        disableYaxisFilter: true,
                    },
                    widgets: [
                        [
                            {
                                identifier: "to-sov-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                ],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "traffic_trends_sovt_data",
                                    primaryDimension: "sku",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                    graphAttributes: {
                                        yAxisTitle: "SOV(%)",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    metricKey: "name",
                                    graphColors: [
                                        REVSTACK_CHART_COLORS.gold[500],
                                        REVSTACK_CHART_COLORS.primary[700],
                                        REVSTACK_CHART_COLORS.secondary[500],
                                    ],
                                },
                            },
                        ],
                    ],
                    title: "SOV Trends",
                },
                [RevstackCASectionIds.KTT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.SOVD,
                    icon: <BsBarChart size={16} />,
                    title: "Keyword Type Trends",
                    widget: RevstackSovDistributionWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "ktt-bar",
                                cubeId: RevstackCubeIds.KA_NATIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.KEYWORD_OVERALL_SOV_SKU,
                                ],
                                queryDimensions: ["intent"],
                                excludeFilter: [CommonWidgetMetrics.SKU_FILTER],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    primaryDimension: "intent",
                                    resultKey:
                                        "keyword_type_trends_bar_chart_data",
                                    isPreviousComparisonRequired: false,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    isTimePeriodChart: true,
                                },
                                relativePeriodConfig: {
                                    granularity: "month",
                                    value: 6,
                                },
                                config: {
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    graphAttributes: {
                                        yAxisTitle: "SOV (%)",
                                        hideXAxisTitle: true,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.MSVC]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "My SKU vs Competition",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "msvc-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension:
                                        CommonWidgetMetrics.SKU_TITLE,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_traffic_trends_data",
                                    listLimit: 5,
                                    responseConfig: {
                                        primaryKey: "Selected SKU",
                                    },
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisTitle: "Overall SOV (%)",
                                        hideXAxisTitle: true,
                                        tooltip:
                                            productAnalysisMultiRowTooltipConfig(
                                                "sku",
                                                selectedSku,
                                                "%"
                                            ),
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "msvc-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU,
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension: CommonWidgetMetrics.SKU,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_traffic_bar_chart_data",
                                    listLimit: 5,
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    isCategoryFormattingRequired: false,
                                    yAxisTitle: "SOV%",
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    graphAttributes: {
                                        yAxisTitle: "Overall SOV (%)",
                                        hideXAxisTitle: true,
                                        xAxisLabelMaxLength: 10,
                                        tooltipFormatter: function (
                                            this: Highcharts.TooltipFormatterContextObject
                                        ) {
                                            return skuTooltipFormatter.call(
                                                this,
                                                currencySymbol
                                            );
                                        },
                                        yAxisUnit: "%",
                                        chartCategoriesDimension:
                                            CommonWidgetMetrics.SKU_TITLE,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        tooltipCategoryDimension:
                                            CommonWidgetMetrics.SKU,
                                        widgetConfig: [
                                            {
                                                identifier: "sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.PAID_SOV,
                                                    CommonWidgetMetrics.ORGANIC_SOV,
                                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.SKU,
                                                    CommonWidgetMetrics.SKU_TITLE,
                                                    CommonWidgetMetrics.THUMBNAIL,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.SKU,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                        CommonWidgetMetrics.BRAND_ID,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.SKU,
                                                    recordKey:
                                                        CommonWidgetMetrics.SKU,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.SKU,
                                                ],
                                                isExcludeDimension: true,
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                key: `paid_sov_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                key: `organic_sov_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                key: `branded_search_value`,
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CD]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "Channel Distribution",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "cd-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_traffic_trends_data",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                config: {
                                    dateFormat: "D/MM/YY",
                                    formatForChannelId: true,
                                    graphAttributes: {
                                        yAxisTitle: "Overall SOV (%)",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "cd-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_traffic_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                config: {
                                    formatForChannelId: true,
                                    graphAttributes: {
                                        yAxisTitle: "Overall SOV (%)",
                                        hideXAxisTitle: true,
                                        yAxisUnit: "%",
                                        tooltipFormatter: skuCDTooltipFormatter,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        showTooltipPoints: true,
                                        widgetConfig: [
                                            {
                                                identifier:
                                                    "cd_sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.PAID_SOV,
                                                    CommonWidgetMetrics.ORGANIC_SOV,
                                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.CHANNEL_ID,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `cd_sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.BRAND_ID,
                                                        CommonWidgetMetrics.SKU,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    recordKey:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                ],
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                key: "paid_sov_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                key: "organic_sov_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                key: "branded_search_value",
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.KP]: {
                    icon: <VscSymbolKeyword size={16} />,
                    title: "Keyword Performance",
                    sectionId: RevstackCASectionIds.KP,
                    drilldownId: RevstackCASectionIds.KP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        isTrendsButtonRequired: false,
                        isTableOnlyMode: true,
                        filters: [
                            {
                                type: RevstackFilterTypes.SELECT,
                                defaultFilter: OptionValues.ALL,
                                dataKey: "intent",
                                filterOptions: [
                                    {
                                        label: "All Keywords",
                                        value: OptionValues.ALL,
                                    },
                                ],
                            },
                        ],
                        isDeepDiveButtonRequired: true,
                    },
                    widgets: [
                        [
                            {
                                identifier: "kp-table",
                                cubeId: RevstackCubeIds.KA_NATIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_keyword`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `ca_${subModule}_keyword` as TablePerfColDefType
                                            ],
                                        isExpandable: false,
                                        style: {
                                            height: 380,
                                        },
                                        dataKeyMap: {
                                            keyword: "keyword",
                                            keywordType: "intent",
                                            indexedSearchVolume: `search_index_value`,
                                            overallSOV: `keyword_overall_sov_sku_value`,
                                            overallSOVChange: `keyword_overall_sov_sku_change`,
                                            paidSOV: `keyword_paid_sov_sku_value`,
                                            paidSOVChange: `keyword_paid_sov_sku_change`,
                                            organicSOV: `keyword_organic_sov_sku_value`,
                                            organicSOVChange: `keyword_organic_sov_sku_change`,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.SEARCH_INDEX,
                                    CommonWidgetMetrics.KEYWORD_OVERALL_SOV_SKU,
                                    CommonWidgetMetrics.KEYWORD_ORGANIC_SOV_SKU,
                                    CommonWidgetMetrics.KEYWORD_PAID_SOV_SKU,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                ],
                                queryDimensions: [
                                    "keyword",
                                    "subcategory",
                                    "intent",
                                ],
                                queryMetaInfo: {
                                    baseFilter: "keyword_overall_sov_sku_value",
                                    resultKey: `pa_keyword_${subModule}_table_data`,
                                    recordKey: "keyword",
                                    sortConfig: {
                                        sortKey: `search_index_value`,
                                    },
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    icon: <SlLocationPin size={16} />,
                    title: "Regional Level Performance",
                    sectionId: RevstackCASectionIds.RWP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        isDrillDownButtonRequired: true,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            defaultTitle: true,
                                        },
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            filterDataKey:
                                                CommonWidgetMetrics.REGION,
                                            filterValue: ["all"],
                                            fetchOptions: true,
                                            requestParams: {
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                queryId: "any-id-can-be-added",
                                                queryDimensions: [
                                                    RevstackFilterKey.REGION,
                                                ],
                                                isExcludeDimension: false,
                                                excludeFilter: [
                                                    RevstackFilterKey.BRAND,
                                                ],
                                                filterToSkip: "",
                                            },
                                            allOptions: {
                                                label: "National",
                                                value: OptionValues.ALL,
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: "regional",
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Mkt. Share",
                                        primaryMetricKey: "mktShare",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Reg. Cont.",
                                        secondaryMetricKey: "regCont",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        regCont: `region_contribution${filterToAppend}`,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_pa_overview_table_data_non_percent",
                                        "region_pa_overview_table_data_percent",
                                    ],
                                },
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "region_pa_list_overview",
                                    sortMeasure:
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    contributionMeasures: [
                                        `region_contribution${filterToAppend}`,
                                    ],
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        isExpandable: true,
                                        isTitleRequired: true,
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            city: "city",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            wtAvailability: `weighted_availability_value`,
                                            wtAvailabilityChange: `weighted_availability_change`,
                                            avgPrice: `average_price_value`,
                                            avgPriceChange: `average_price_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                traffic: [
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                ],
                                queryDimensions: ["city"],
                                queryMetaInfo: {
                                    laggardConfig: {
                                        condition: {
                                            operater: "lessThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    leaderConfig: {
                                        condition: {
                                            operater: "greaterThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    resultKey: "region_pa_overview_table_data",
                                    baseFilter: "overall_sov_value",
                                    recordKey: "city",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                        limit: 5,
                                    },
                                    contributionMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                [RevstackCASectionIds.BANNER]: {
                    icon: <></>,
                    title: "Product Analytics",
                    sectionId: RevstackCASectionIds.BANNER,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                            sticky: true,
                        },
                        isTrendsButtonRequired: false,
                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                    },
                    widgets: [
                        {
                            identifier: "po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            relativeTabKey: CA_DD_MODULES.OVERVIEW,
                            type: WidgetType.W0,
                            config: {
                                currencySymbol: currencySymbol,
                                banner: {
                                    formatterParams: {
                                        isCrossChannelComponent: true,
                                        metricsToAvg: [
                                            `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                        ],
                                        metricsToSum: ["reviews_count"],
                                    },
                                },
                            },
                            queryDimensions: [
                                "cross_channel_sku_id",
                                "channel_id",
                                "category",
                                "brand",
                                "sku",
                                "sku_title",
                                "product_url",
                                "thumbnail",
                            ],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.AVERAGE_PRICE,
                                CommonWidgetMetrics.AVERAGE_MRP,
                                CommonWidgetMetrics.AVERAGE_RATING,
                            ],
                            excludeFilter: [
                                CommonWidgetMetrics.BRAND_ID,
                                CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                            ],
                            isExcludeDimension: true,
                            queryMetaInfo: {
                                resultKey: `sku_banner_data`,
                                recordKey: "cross_channel_sku_id##channel_id",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.CO]: {
                    ...configSections[RevstackCASectionIds.PO],
                    title: "Conversion Overview",
                    widgets: [
                        [
                            {
                                identifier: "co-rpi",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "RPI",
                                subTitle: "By PPU",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "co-ap",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Avg Price",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.AVERAGE_PRICE
                                    ].description,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "co-wd",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Wt. Discount",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                        [
                            {
                                identifier: "co-rpi-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "RPI",
                                subTitle: "Overall",
                                icon: <FiEye size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX
                                    ].description,
                                hasfilters: false,
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "RPI",
                                        value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "co-ar-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "Avg. Rating",
                                icon: <BsBasket2 size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.AVERAGE_RATING
                                    ].description,
                                hasfilters: false,
                                measures: [CommonWidgetMetrics.AVERAGE_RATING],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                config: {
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Avg. Rating",
                                            value: CommonWidgetMetrics.AVERAGE_RATING,
                                            unit: "",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Avg. Rating",
                                        value: CommonWidgetMetrics.AVERAGE_RATING,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                            {
                                identifier: "co-cs-w2",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                title: "Content Score",
                                icon: <FiTruck size={16} />,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.CONTENT_SCORE
                                    ].description,
                                hasFilters: false,
                                measures: [CommonWidgetMetrics.CONTENT_SCORE],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                config: {
                                    isComingSoon: true,
                                    drilldown: {
                                        title: "Top 3 laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Content Score",
                                            value: CommonWidgetMetrics.CONTENT_SCORE,
                                            unit: "",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Content Score",
                                        value: CommonWidgetMetrics.CONTENT_SCORE,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "subcategory",
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.PT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.CRT,
                    title: "Pricing Trends",
                    widgets: [
                        [
                            {
                                identifier: "co-crt-w3",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "sku_pricing_trends_data",
                                    primaryDimension: "sku",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    minorTicksLabelLimit: 3,
                                    isCustomLegendRequired: false,
                                    graphAttributes: {
                                        yAxisTitle: "Avg Pricing",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: currencySymbol,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        heightPercent: 0.29,
                                    },
                                    graphColors: [
                                        REVSTACK_CHART_COLORS.primary[700],
                                    ],
                                },
                            },
                            {
                                identifier: "co-crt-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: ["sku"],
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "sku",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "sku_pricing_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    graphAttributes: {
                                        yAxisTitle: "Avg Pricing",
                                        hideXAxisTitle: true,
                                        yAxisUnit: currencySymbol,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                        },
                                    },
                                    filterKey: "Categories",
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.DT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.DT,
                    title: "Discount Trends",
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        disableYaxisFilter: true,
                        isDateRangeRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "co-crt-w3",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                ],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    primaryDimension: "sku",
                                    resultKey: "sku_discount_trends_data",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    isCustomLegendRequired: false,
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisTitle: "Wt. Discount",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        heightPercent: 0.28,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                        },
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.MSVC]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "My SKU vs Competition",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "mbvc-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension:
                                        CommonWidgetMetrics.SKU_TITLE,
                                    resultKey: "sku_vs_comp_conv_trends_data",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    listLimit: 5,
                                    responseConfig: {
                                        primaryKey: "Selected SKU",
                                    },
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisTitle: "Avg Pricing",
                                        hideXAxisTitle: true,
                                        tooltip:
                                            productAnalysisMultiRowTooltipConfig(
                                                "sku",
                                                selectedSku,
                                                currencySymbol
                                            ),
                                        minDatelabels: 6,
                                        yAxisUnit: currencySymbol,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "mbvc-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU,
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension: CommonWidgetMetrics.SKU,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_conv_bar_chart_data",
                                    listLimit: 5,
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    isCategoryFormattingRequired: false,
                                    graphAttributes: {
                                        yAxisTitle: "Avg Price",
                                        hideXAxisTitle: true,
                                        xAxisLabelMaxLength: 10,
                                        tooltipFormatter: function (
                                            this: Highcharts.TooltipFormatterContextObject
                                        ) {
                                            return skuTooltipFormatter.call(
                                                this,
                                                currencySymbol
                                            );
                                        },
                                        yAxisUnit: currencySymbol,
                                        chartCategoriesDimension:
                                            CommonWidgetMetrics.SKU_TITLE,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 300,
                                        tooltipCategoryDimension:
                                            CommonWidgetMetrics.SKU,
                                        widgetConfig: [
                                            {
                                                identifier: "sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                    CommonWidgetMetrics.AVERAGE_RATING,
                                                    CommonWidgetMetrics.CONTENT_SCORE,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.SKU,
                                                    CommonWidgetMetrics.SKU_TITLE,
                                                    CommonWidgetMetrics.THUMBNAIL,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.SKU,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                        CommonWidgetMetrics.BRAND_ID,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.SKU,
                                                    recordKey:
                                                        CommonWidgetMetrics.SKU,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.SKU,
                                                ],
                                                isExcludeDimension: true,
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "WT. Discount",
                                                key: `weighted_discount_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Rating",
                                                key: `avg_rating_value`,
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                key: `content_score_weighted_value`,
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CD]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MBVC,
                    title: "Channel Distribution",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "cd-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_conversion_trends_data",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                config: {
                                    formatForChannelId: true,
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisTitle: "Avg Pricing",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: currencySymbol,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "cd-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_conversion_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                config: {
                                    formatForChannelId: true,
                                    graphAttributes: {
                                        yAxisTitle: "Avg Pricing",
                                        hideXAxisTitle: true,
                                        yAxisUnit: currencySymbol,
                                        tooltipFormatter: function (
                                            this: Highcharts.TooltipFormatterContextObject
                                        ) {
                                            return skuCDTooltipFormatter.call(
                                                this,
                                                currencySymbol
                                            );
                                        },
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        showTooltipPoints: true,
                                        widgetConfig: [
                                            {
                                                identifier:
                                                    "cd_sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                                    CommonWidgetMetrics.AVERAGE_RATING,
                                                    CommonWidgetMetrics.CONTENT_SCORE,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.CHANNEL_ID,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `cd_sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.BRAND_ID,
                                                        CommonWidgetMetrics.SKU,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    recordKey:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                ],
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "WT. Discount",
                                                key: "weighted_discount_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Rating",
                                                key: "avg_rating_value",
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                key: "content_score_weighted_value",
                                                unit: "%",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    icon: <SlLocationPin size={16} />,
                    title: "Regional Level Performance",
                    sectionId: RevstackCASectionIds.RWP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        isDrillDownButtonRequired: true,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            defaultTitle: true,
                                        },
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            filterDataKey:
                                                CommonWidgetMetrics.REGION,
                                            filterValue: ["all"],
                                            fetchOptions: true,
                                            requestParams: {
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                queryId: "any-id-can-be-added",
                                                queryDimensions: [
                                                    RevstackFilterKey.REGION,
                                                ],
                                                isExcludeDimension: false,
                                                excludeFilter: [
                                                    RevstackFilterKey.BRAND,
                                                ],
                                                filterToSkip: "",
                                            },
                                            allOptions: {
                                                label: "National",
                                                value: OptionValues.ALL,
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: "regional",
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Mkt. Share",
                                        primaryMetricKey: "mktShare",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Reg. Cont.",
                                        secondaryMetricKey: "regCont",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        regCont: `region_contribution${filterToAppend}`,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_pa_overview_table_data_non_percent",
                                        "region_pa_overview_table_data_percent",
                                    ],
                                },
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "region_pa_list_overview",
                                    sortMeasure:
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    contributionMeasures: [
                                        `region_contribution${filterToAppend}`,
                                    ],
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        isExpandable: true,
                                        isTitleRequired: true,
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            city: "city",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            wtAvailability: `weighted_availability_value`,
                                            wtAvailabilityChange: `weighted_availability_change`,
                                            avgPrice: `average_price_value`,
                                            avgPriceChange: `average_price_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                traffic: [
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                ],
                                queryDimensions: ["city"],
                                queryMetaInfo: {
                                    laggardConfig: {
                                        condition: {
                                            operater: "lessThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    leaderConfig: {
                                        condition: {
                                            operater: "greaterThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    resultKey: "region_pa_overview_table_data",
                                    baseFilter: "overall_sov_value",
                                    recordKey: "city",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                        limit: 5,
                                    },
                                    contributionMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                [RevstackCASectionIds.BANNER]: {
                    icon: <></>,
                    title: "Product Analytics",
                    sectionId: RevstackCASectionIds.BANNER,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                            sticky: true,
                        },
                        isTrendsButtonRequired: false,
                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                    },
                    widgets: [
                        {
                            identifier: "po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            relativeTabKey: CA_DD_MODULES.OVERVIEW,
                            type: WidgetType.W0,
                            config: {
                                currencySymbol: currencySymbol,
                                banner: {
                                    formatterParams: {
                                        isCrossChannelComponent: true,
                                        metricsToAvg: [
                                            `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                        ],
                                        metricsToSum: ["reviews_count"],
                                    },
                                },
                            },
                            queryDimensions: [
                                "cross_channel_sku_id",
                                "channel_id",
                                "category",
                                "brand",
                                "sku",
                                "sku_title",
                                "product_url",
                                "thumbnail",
                            ],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.AVERAGE_PRICE,
                                CommonWidgetMetrics.AVERAGE_MRP,
                                CommonWidgetMetrics.AVERAGE_RATING,
                            ],
                            excludeFilter: [
                                CommonWidgetMetrics.BRAND_ID,
                                CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                            ],
                            isExcludeDimension: true,
                            queryMetaInfo: {
                                resultKey: `sku_banner_data`,
                                recordKey: "cross_channel_sku_id##channel_id",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.OO]: {
                    ...configSections[RevstackCASectionIds.PO],
                    title: "Operations Overview",
                    widgets: [
                        [
                            {
                                identifier: "op-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Availability",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics
                                            .WEIGHTED_AVAILABILITY
                                    ].description,

                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "op-ls",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Listing Status",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.LISTING_STATUS
                                    ].description,
                                measures: [CommonWidgetMetrics.LISTING_STATUS],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "op-sih",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Stock in Hand",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.STOCK_IN_HAND
                                    ].description,
                                measures: [CommonWidgetMetrics.STOCK_IN_HAND],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                        [
                            {
                                identifier: "op-fr",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.FILL_RATE
                                    ].description,
                                title: "Fill Rate",
                                icon: <FiEye size={16} />,
                                hasfilters: false,
                                measures: [CommonWidgetMetrics.FILL_RATE],
                                queryDimensions: ["sku"],
                                laggardField: "sku",
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                config: {
                                    isComingSoon: true,
                                    isComingSoonMessage: "Enable access",
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.AT]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.AT,
                    title: "Availability Trends",
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        disableYaxisFilter: true,
                        isDateRangeRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "op-crt",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "avl_trends_data",
                                    primaryDimension: "sku",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    isCustomLegendRequired: false,
                                    graphAttributes: {
                                        yAxisTitle: "Availability (%)",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        heightPercent: 0.29,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                        },
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.LS]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.LS,
                    title: "Listing Status",
                    widget: RevstackSovTrendsWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        disableYaxisFilter: true,
                        isDateRangeRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "co-crt-w3",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [CommonWidgetMetrics.LISTING_STATUS],
                                queryDimensions: [CommonWidgetMetrics.SKU],
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "listing_status_trends_data",
                                    primaryDimension: "sku",
                                },
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                isExcludeDimension: true,
                                config: {
                                    dateFormat: "D/MM/YY",
                                    isCustomLegendRequired: false,
                                    graphAttributes: {
                                        yAxisTitle: "Listing Status",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 240,
                                        // The heightPercent controls the chart's height in the above sepcified min and max height,
                                        // taking the viewPort height into account
                                        heightPercent: 0.29,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                        },
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.MSVC]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.MSVC,
                    title: "My SKU vs Competition",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "msvc-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension:
                                        CommonWidgetMetrics.SKU_TITLE,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_operation_trends_data",
                                    listLimit: 5,
                                    responseConfig: {
                                        primaryKey: "Selected SKU",
                                    },
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                config: {
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisTitle: "Availability (%)",
                                        hideXAxisTitle: true,
                                        tooltip:
                                            productAnalysisMultiRowTooltipConfig(
                                                "sku",
                                                selectedSku,
                                                "%"
                                            ),
                                        minDatelabels: 6,
                                        yAxisUnit: "%",
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "msvc-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: [
                                    CommonWidgetMetrics.SKU,
                                    CommonWidgetMetrics.SKU_TITLE,
                                ],
                                isExcludeDimension: true,
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filterToSkip: "sku",
                                    primaryDimension: CommonWidgetMetrics.SKU,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey:
                                        "sku_vs_comp_operations_bar_chart_data",
                                    listLimit: 5,
                                    isPreviousComparisonRequired: true,
                                },
                                config: {
                                    isCategoryFormattingRequired: false,
                                    graphAttributes: {
                                        yAxisUnit: "%",
                                        yAxisTitle: "Availability (%)",
                                        hideXAxisTitle: true,
                                        xAxisLabelMaxLength: 10,
                                        tooltipFormatter: skuTooltipFormatter,
                                        maxYaxisValue: 100,
                                        chartCategoriesDimension:
                                            CommonWidgetMetrics.SKU_TITLE,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 300,
                                        tooltipCategoryDimension:
                                            CommonWidgetMetrics.SKU,
                                        widgetConfig: [
                                            {
                                                identifier: "sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.LISTING_STATUS,
                                                    CommonWidgetMetrics.FILL_RATE,
                                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.SKU,
                                                    CommonWidgetMetrics.SKU_TITLE,
                                                    CommonWidgetMetrics.THUMBNAIL,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.SKU,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                        CommonWidgetMetrics.BRAND_ID,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.SKU_TITLE,
                                                    recordKey:
                                                        CommonWidgetMetrics.SKU,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.SKU,
                                                ],
                                                isExcludeDimension: true,
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                key: `listing_status_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Fill Rate",
                                                key: `fill_rate_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Stock in Hand",
                                                key: `weighted_avg_stock_in_hand_value`,
                                                unit: " D",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.CD]: {
                    ...configSections[RevstackCASectionIds.CP],
                    sectionId: RevstackCASectionIds.CD,
                    title: "Channel Distribution",
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 12,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        filters: [
                            {
                                type: RevstackFilterTypes.CHART_TYPE,
                                defaultFilter: GraphTypes.BAR,
                                filterOptions: [
                                    {
                                        value: GraphTypes.BAR,
                                        icon: (
                                            <BsBarChartLine
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                    {
                                        value: GraphTypes.TREND,
                                        icon: (
                                            <BsGraphUpArrow
                                                size={16}
                                                color="#009FE3"
                                            />
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    widgets: [
                        [
                            {
                                identifier: "cd-trends",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W3,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_operations_trends_data",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                relativePeriodConfig: {
                                    isDynamicPeriodValue: true,
                                },
                                isExcludeDimension: true,
                                config: {
                                    formatForChannelId: true,
                                    dateFormat: "D/MM/YY",
                                    graphAttributes: {
                                        yAxisUnit: "%",
                                        yAxisTitle: "Availability (%)",
                                        hideXAxisTitle: true,
                                        minDatelabels: 6,
                                        maxYaxisValue: 100,
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 230,
                                        heightPercent: 0.28,
                                    },
                                },
                            },
                            {
                                identifier: "cd-bar",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W4,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                queryDimensions: ["channel_id"],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    primaryDimension: "channel_id",
                                    resultKey: "cd_operations_bar_chart_data",
                                    isPreviousComparisonRequired: true,
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.SKU,
                                    ],
                                },
                                config: {
                                    formatForChannelId: true,
                                    graphAttributes: {
                                        yAxisTitle: "Availability (%)",
                                        hideXAxisTitle: true,
                                        yAxisUnit: "%",
                                        tooltipFormatter: function (
                                            this: Highcharts.TooltipFormatterContextObject
                                        ) {
                                            return skuCDTooltipFormatter.call(
                                                this,
                                                currencySymbol
                                            );
                                        },
                                        maxYaxisValue: 100,
                                    },
                                    graphColors: {
                                        shades: {
                                            color1: REVSTACK_CHART_COLORS
                                                .primary[700],
                                            color2: REVSTACK_CHART_COLORS
                                                .secondary[500],
                                            color3: REVSTACK_CHART_COLORS
                                                .gold[500],
                                        },
                                    },
                                    chartDimensions: {
                                        minHeight: 100,
                                        maxHeight: 270,
                                        heightPercent: 0.36,
                                    },
                                    tooltipConfig: {
                                        enableDataFetch: true,
                                        fetchDelay: 100,
                                        showTooltipPoints: true,
                                        widgetConfig: [
                                            {
                                                identifier:
                                                    "cd_sku_metrics_data",
                                                type: WidgetType.W8,
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                measures: [
                                                    CommonWidgetMetrics.MARKET_SHARE,
                                                    CommonWidgetMetrics.LISTING_STATUS,
                                                    CommonWidgetMetrics.FILL_RATE,
                                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                                ],
                                                queryDimensions: [
                                                    CommonWidgetMetrics.CHANNEL_ID,
                                                ],
                                                queryMetaInfo: {
                                                    resultKey: `cd_sku_metrics_data`,
                                                    // Filter to replace for popover data
                                                    popoverFilter:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    filtersToIgnore: [
                                                        CommonWidgetMetrics.BRAND_ID,
                                                        CommonWidgetMetrics.SKU,
                                                    ],
                                                    primaryDimension:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                    recordKey:
                                                        CommonWidgetMetrics.CHANNEL_ID,
                                                },
                                                excludeFilter: [
                                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                                ],
                                            },
                                        ],
                                        tooltipMetrics: [
                                            {
                                                label: "MS%",
                                                key: `market_share${filterToAppend}_value`,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                key: "listing_status_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Fill Rate",
                                                key: "fill_rate_value",
                                                unit: "%",
                                            },
                                            {
                                                label: "Stock in Hand",
                                                key: "weighted_avg_stock_in_hand_value",
                                                unit: " D",
                                            },
                                        ],
                                    },
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.RWP]: {
                    icon: <SlLocationPin size={16} />,
                    title: "Regional Level Performance",
                    sectionId: RevstackCASectionIds.RWP,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        isDrillDownButtonRequired: true,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            defaultTitle: true,
                                        },
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            filterDataKey:
                                                CommonWidgetMetrics.REGION,
                                            filterValue: ["all"],
                                            fetchOptions: true,
                                            requestParams: {
                                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                                queryId: "any-id-can-be-added",
                                                queryDimensions: [
                                                    RevstackFilterKey.REGION,
                                                ],
                                                isExcludeDimension: false,
                                                excludeFilter: [
                                                    RevstackFilterKey.BRAND,
                                                ],
                                                filterToSkip: "",
                                            },
                                            allOptions: {
                                                label: "National",
                                                value: OptionValues.ALL,
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "rwp-cards",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W6,
                                isTopCardRequired: true,
                                config: {
                                    card: {
                                        type: "regional",
                                        cardType: "regional",
                                        cardId: "region",
                                        primaryMetricLabel: "Mkt. Share",
                                        primaryMetricKey: "mktShare",
                                        primaryMetricUnit: "%",
                                        secondaryMetricLabel: "Reg. Cont.",
                                        secondaryMetricKey: "regCont",
                                        secondaryMetricUnit: "%",
                                        isSortFilterRequired: false,
                                        topCard: {
                                            id: ALL_OPTIONS.value,
                                            name: "NATIONAL",
                                            color: "#919191",
                                            primaryMetricKey: `market_share${filterToAppend}`,
                                        },
                                    },
                                    dataKeyMap: {
                                        mktShare:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                        regCont: `region_contribution${filterToAppend}`,
                                        regional: "region",
                                    },
                                    relatedComponentDataKeys: [
                                        "region_pa_overview_table_data_non_percent",
                                        "region_pa_overview_table_data_percent",
                                    ],
                                },
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                queryDimensions: ["region"],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "region_pa_list_overview",
                                    sortMeasure:
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    contributionMeasures: [
                                        `region_contribution${filterToAppend}`,
                                    ],
                                    measureId: "region",
                                    dimValueToTruncate: "-Region",
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                            {
                                identifier: "rwp-table",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `ca_${subModule}_regional`,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `pa_overview_regional` as TablePerfColDefType
                                            ],
                                        style: {
                                            height: 400,
                                            expandedHeight: 360,
                                        },
                                        isExpandable: true,
                                        isTitleRequired: true,
                                        filters: [
                                            {
                                                type: RevstackFilterTypes.RADIO,
                                                defaultFilter: "laggards",
                                                filterOptions: [
                                                    {
                                                        label: "Laggards",
                                                        value: "laggards",
                                                        sortOrder: "asc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "lessThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                    {
                                                        label: "Leaders",
                                                        value: "leaders",
                                                        sortOrder: "desc",
                                                        filterConfig: {
                                                            condition: {
                                                                operater:
                                                                    "greaterThan",
                                                                value: 0,
                                                            },
                                                            field: `market_share${filterToAppend}_change`,
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                        dataKeyMap: {
                                            city: "city",
                                            marketShare: `market_share${filterToAppend}_value`,
                                            marketShareChange: `market_share${filterToAppend}_change`,
                                            overallSOV: `overall_sov_value`,
                                            overallSOVChange: `overall_sov_change`,
                                            wtAvailability: `weighted_availability_value`,
                                            wtAvailabilityChange: `weighted_availability_change`,
                                            avgPrice: `average_price_value`,
                                            avgPriceChange: `average_price_change`,
                                        },
                                        drilldown: {
                                            dataKeyMap: {
                                                traffic: [
                                                    {
                                                        name: "Organic SOV",
                                                        measure:
                                                            CommonWidgetMetrics.ORGANIC_SOV,
                                                        unit: "%",
                                                    },
                                                    {
                                                        name: "Paid SOV",
                                                        measure:
                                                            CommonWidgetMetrics.PAID_SOV,
                                                        unit: "%",
                                                    },
                                                ],
                                                conversion: [
                                                    {
                                                        name: "RPI",
                                                        measure:
                                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Rating",
                                                        measure:
                                                            CommonWidgetMetrics.AVERAGE_RATING,
                                                        unit: "",
                                                    },
                                                    {
                                                        name: "Content Score",
                                                        measure:
                                                            CommonWidgetMetrics.CONTENT_SCORE,
                                                        unit: "",
                                                    },
                                                ],
                                                operations: [
                                                    {
                                                        name: "Stock in Hand",
                                                        measure:
                                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                                        unit: "day",
                                                    },
                                                    {
                                                        name: "Listing Status",
                                                        measure:
                                                            CommonWidgetMetrics.LISTING_STATUS,
                                                        unit: "%",
                                                    },
                                                ],
                                            },
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                ],
                                queryDimensions: ["city"],
                                queryMetaInfo: {
                                    laggardConfig: {
                                        condition: {
                                            operater: "lessThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    leaderConfig: {
                                        condition: {
                                            operater: "greaterThan",
                                            value: 0,
                                        },
                                        field: `market_share${filterToAppend}_change`,
                                    },
                                    resultKey: "region_pa_overview_table_data",
                                    baseFilter: "overall_sov_value",
                                    recordKey: "city",
                                    sortConfig: {
                                        sortKey: `market_share${filterToAppend}_change`,
                                        limit: 5,
                                    },
                                    contributionMeasures: [
                                        `category_contribution${filterToAppend}`,
                                    ],
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                excludeFilter: ["brand_id", "sku"],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.PRODUCT_SELECTION_LIST) {
            return {
                [RevstackCASectionIds.INOTE]: {
                    icon: <></>,
                    title: "Note",
                    sectionId: RevstackCASectionIds.INOTE,
                    config: {
                        message: REVSTACK_INFO_NOTES.SKU_SELECTION_LIST,
                        type: "info",
                        backgroundColor: "#E5F8FF",
                        color: "#000000",
                        fontSize: "14px",
                    },
                },
                [RevstackCASectionIds.PSL]: {
                    icon: <BsBoxes size={16} />,
                    title: "Product List",
                    tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                    moduleType: "table",
                    sectionId: RevstackCASectionIds.DDSSTA,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        widgetCard: {
                            isBodyPaddingReq: false,
                            isCardRequired: false,
                        },
                        isTableOnlyMode: true,
                        isHeaderRequired: false,
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd_ca_ov_sp_sku",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        colDefs:
                                            getRevstackDDTableColDefs(
                                                filterToAppend
                                            )["sku_selection"],
                                        isGroupedTable: false,
                                        isSearchReq: true,
                                        isDrilldownTable: true,
                                        isCompetitionButtonReq: false,
                                        isLayoutButtonReq: true,
                                        isDownloadButtonReq: true,
                                        style: {
                                            reduceHeightBy: 490,
                                        },
                                        // additionalFilters: [
                                        //     {
                                        //         section: "",
                                        //         key: CommonWidgetMetrics.CATEGORY,
                                        //         type: RevstackFilterTypes.SELECT,
                                        //         props: {
                                        //             options: [CAMPAIGN_CREATION_DEFAULT_OPTIONS.category],
                                        //             onChange: (value: string) => {},
                                        //             defaultValue: CommonWidgetMetrics.All,
                                        //             width: 138,
                                        //         },
                                        //     },
                                        //     {
                                        //         section: "",
                                        //         key: CommonWidgetMetrics.SUB_CATEGORY,
                                        //         type: RevstackFilterTypes.SELECT,
                                        //         props: {
                                        //             options: [CAMPAIGN_CREATION_DEFAULT_OPTIONS.subcategory],
                                        //             onChange: (value: string) => {},
                                        //             defaultValue: CommonWidgetMetrics.All,
                                        //             width: 162,
                                        //         },
                                        //     },
                                        // ],
                                    },
                                },
                                queryDimensions: [
                                    "category",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                    "brand",
                                    "brand_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: "sku_table_data",
                                    recordKey: "sku",
                                },
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                infoMessage: "",
                                isMeasureAppendRequired: false,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                ],
                            },
                        ],
                    ],
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.PRODUCT_CHANGE_LIST) {
            return {
                [RevstackCASectionIds.PSCL]: {
                    icon: <BsBoxes size={16} />,
                    title: "SKU List",
                    tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                    moduleType: "table",
                    sectionId: RevstackCASectionIds.DDSSTA,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        widgetCard: {
                            isBodyPaddingReq: false,
                            isCardRequired: false,
                        },
                        isTableOnlyMode: true,
                        isHeaderRequired: false,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: RevstackFilterTypes.SELECT,
                                            filterKey: "subcategory",
                                            props: {
                                                onClick: () => {},
                                                onChange: () => {},
                                                onOpenChange: () => {},
                                                options: [] as any[],
                                                defaultValue: [] as any[],
                                                value: "",
                                                mode: "",
                                                className: "",
                                                placeholder: "",
                                                styles: {},
                                                subTitle: "" as any,
                                            },
                                        },
                                        // {
                                        //     placement: "suffix",
                                        //     type: "detailed-button",
                                        //     props: {
                                        //         name: "Detailed View",
                                        //         onClick:()=>{},
                                        //         className: "",
                                        //         styles: {},
                                        //     }

                                        // }
                                    ],
                                },
                            ],
                        },
                        tabs: {
                            isTrendsViewRequired: true,
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd_ca_ov_sp_sku",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        colDefs:
                                            getRevstackDDTableColDefs(
                                                filterToAppend
                                            )["change_sku"],
                                        isGroupedTable: false,
                                        isSearchReq: true,
                                        isDrilldownTable: true,
                                        isCompetitionButtonReq: false,
                                        isLayoutButtonReq: false,
                                        isDownloadButtonReq: true,
                                        style: {
                                            height: "51vh",
                                            expandedHeight: "51vh",
                                        },
                                    },
                                },
                                queryDimensions: [
                                    "category",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                    "brand",
                                    "brand_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: "sku_selection_table_data",
                                    recordKey: "sku",
                                },
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                infoMessage: "",
                                isMeasureAppendRequired: false,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                ],
                            },
                        ],
                    ],
                },
            };
        }
    }

    if (module === RevstackDashboardModules.NAVIGATOR) {
        store.dispatch(
            updateRevstackDashboardTabs({
                module: module,
                tabs: getTabConfig([
                    RevstackDashboardSubModules.OVERVIEW,
                    RevstackDashboardSubModules.AI_INSIGHTS,
                ]),
            })
        );

        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.PO]: {
                    icon: <></>,
                    title: "Performance Overview",
                    sectionId: RevstackCASectionIds.PO,
                    widget: RevstackPoCategoryAnalysisWidget,
                    config: {
                        isTrendsButtonRequired: true,
                        isTrendsButtonComingSoon: false,
                        hideTrendsTabSection: true,
                        showCategorySelect: true,
                        showRegionSelect: true,
                        defaultTab: CA_DD_MODULES.CROSS_CHANNEL,
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        module: RevstackDashboardModules.NAVIGATOR,
                    },
                    widgets: [
                        [
                            {
                                identifier: "po-wms",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Market Share",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.MARKET_SHARE
                                    ].description,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        noDataText: REVSTACK_W7_TABLE_NO_DATA_MESSAGES.LAGGARDS.NAVIGATOR_PO_CARDS_LAGGARDS
                                    },
                                    filterOptions: [
                                        {
                                            label: "Market Share",
                                            value: CommonWidgetMetrics.MARKET_SHARE,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Market Share",
                                        value: CommonWidgetMetrics.MARKET_SHARE,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "channel_id",
                                },
                                queryDimensions: ["channel_id"],
                            },
                            {
                                identifier: "po-wva",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Wt. Availability",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics
                                            .WEIGHTED_AVAILABILITY
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        noDataText: REVSTACK_W7_TABLE_NO_DATA_MESSAGES.LAGGARDS.NAVIGATOR_PO_CARDS_LAGGARDS
                                    },
                                    filterOptions: [
                                        {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Wt. Availability",
                                        value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "channel_id",
                                },
                                queryDimensions: ["channel_id"],
                            },
                            {
                                identifier: "po-wosov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Wt. Overall SOV",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.OVERALL_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                excludeFilter: ["brand_id"],
                                config: {
                                    drilldown: {
                                        title: "Top laggards",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        noDataText: REVSTACK_W7_TABLE_NO_DATA_MESSAGES.LAGGARDS.NAVIGATOR_PO_CARDS_LAGGARDS
                                    },
                                    filterOptions: [
                                        {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Overall SOV",
                                        value: CommonWidgetMetrics.OVERALL_SOV,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "channel_id",
                                },
                                queryDimensions: ["channel_id"],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.COMV]: {
                    icon: <BsUiChecksGrid size={16} />,
                    title: "Comparison Overview",
                    sectionId: RevstackCASectionIds.COMV,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "card",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                            isBodyPaddingReq: false,
                        },
                        isTrendsButtonRequired: true,
                        isTrendsButtonComingSoon: false,
                        isSelectFilterRequired: true,
                        hideTrendsTabSection: false,
                        module: RevstackDashboardModules.NAVIGATOR,
                        componentFilterType: "ma_category",
                        showCategorySelect: true,
                        showRegionSelect: true,
                        defaultTab: CA_DD_MODULES.OVERVIEW,
                        trendsFiltersConfig: {
                            secondaryFilters: [
                                {
                                    filterKey: CommonWidgetMetrics.CATEGORY,
                                    filterDataKey: `${CommonWidgetMetrics.CATEGORY}_id`,
                                    filterValue: ["all"],
                                    relatedColDataKey:
                                        CommonWidgetMetrics.CATEGORY,
                                    allOptions: {
                                        label: "All Categories",
                                        value: OptionValues.ALL,
                                    },
                                },
                                {
                                    filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                    filterDataKey: CommonWidgetMetrics.REGION,
                                    filterValue: ["all"],
                                    fetchOptions: true,
                                    requestParams: {
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        queryId: "any-id-can-be-added",
                                        queryDimensions: [
                                            RevstackFilterKey.REGION,
                                        ],
                                        isExcludeDimension: false,
                                        excludeFilter: [
                                            RevstackFilterKey.BRAND,
                                        ],
                                        filterToSkip: "",
                                    },
                                    allOptions: {
                                        label: "National",
                                        value: OptionValues.ALL,
                                    },
                                },
                            ],
                        },
                        filters: [
                            {
                                type: RevstackFilterTypes.MULTI_SELECT,
                                defaultFilter: [OptionValues.ALL],
                                dataKey: "category",
                                actions: {
                                    route: "market-analysis/category",
                                    onChange: "filter",
                                    onOpenChange: "get-data",
                                },
                                filterOptions: [
                                    {
                                        label: "All Categories",
                                        value: OptionValues.ALL,
                                    },
                                ],
                                showOnlyAllLabelWhenAllSelected: true,
                            },
                        ],
                        relatedComponentDataKeys: [
                            "cwp_performance_non_percent",
                            "cwp_performance_percent",
                            "consolidated_cwp_performance_non_percent",
                            "consolidated_cwp_performance_percent",
                        ],
                    },
                    widgets: [
                        {
                            identifier: "cwp",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Channel wise Performance",
                            type: WidgetType.W9,
                            excludeFilter: ["brand_id"],
                            config: {
                                container: {
                                    style: {
                                        height: 400,
                                    },
                                },
                                sections: [
                                    {
                                        title: "",
                                        metrics: [
                                            {
                                                key: `${getPreparedMetric(
                                                    CommonWidgetMetrics.MARKET_SHARE
                                                )}`,
                                                label: "Market Share",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                label: "Wt. Availability",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.OVERALL_SOV,
                                                label: "Overall SOV",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                        ],
                                    },
                                    {
                                        title: "Traffic",
                                        metrics: [
                                            {
                                                key: CommonWidgetMetrics.ORGANIC_SOV,
                                                label: "Organic SOV",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.PAID_SOV,
                                                label: "Paid SOV",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.BRANDED_SEARCH,
                                                label: "Branded Search %",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                        ],
                                    },
                                    {
                                        title: "Conversion",
                                        metrics: [
                                            {
                                                key: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                label: "RPI",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.AVERAGE_RATING,
                                                label: "Rating",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.CONTENT_SCORE,
                                                label: "Content Score",
                                                showChange: true,
                                            },
                                        ],
                                    },
                                    {
                                        title: "Operations",
                                        metrics: [
                                            {
                                                key: CommonWidgetMetrics.LISTING_STATUS,
                                                label: "Listing Status",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.FILL_RATE,
                                                label: "Fill Rate",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.STOCK_IN_HAND,
                                                label: "Stock in Hand",
                                                showChange: true,
                                            },
                                            {
                                                key: CommonWidgetMetrics.BUYBOX,
                                                label: "BuyBox%",
                                                suffix: "%",
                                                showChange: true,
                                            },
                                        ],
                                    },
                                ],
                            },
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                CommonWidgetMetrics.OVERALL_SOV,
                                CommonWidgetMetrics.ORGANIC_SOV,
                                CommonWidgetMetrics.PAID_SOV,
                                CommonWidgetMetrics.BRANDED_SEARCH,
                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                CommonWidgetMetrics.AVERAGE_RATING,
                                CommonWidgetMetrics.CONTENT_SCORE,
                                CommonWidgetMetrics.LISTING_STATUS,
                                CommonWidgetMetrics.FILL_RATE,
                                CommonWidgetMetrics.STOCK_IN_HAND,
                            ],
                            queryDimensions: ["channel_id"],
                            queryMetaInfo: {
                                resultKey: `cwp_performance`,
                                consolidatedResultKey:
                                    "consolidated_cwp_performance",
                                recordKey: "channel_id",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.PS]: {
                    icon: <BsGraphUpArrow size={16} />,
                    title: "Performance Summary",
                    sectionId: RevstackCASectionIds.PS,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        isTabsRequired: true,
                        isColumnGroupSelectReq: true,
                        isColumnGroupSelectSameForAllTabs: true,
                        isTrendsButtonRequired: false,
                        isTrendsButtonComingSoon: false,
                        module: RevstackDashboardModules.NAVIGATOR,
                        defaultTab: CA_DD_MODULES.CROSS_CHANNEL,
                        hideTrendsTabSection: true,
                        isTableOnlyMode: true,
                        showCategorySelect: true,
                        showRegionSelect: true,
                        defaultParentTabKey: "subcategory-analysis",
                    },
                    widgets: [
                        [
                            {
                                icon: <BsColumns size={16} />,
                                tabKey: "category-analysis",
                                title: "Category Analysis",
                                identifier: "ps-table",
                                type: WidgetType.W7,
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                config: {
                                    table: {
                                        type: `mi_${subModule}_ps_ca`,
                                        isGroupedTable: true,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_ca` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_ca` as TablePerfColDefType
                                            ],
                                        expandedTableDataKey: "category_id",
                                        // isExpandable: true,
                                        // showExpandTableHeader: false,
                                        isLayoutButtonReq: false,
                                        isDownloadButtonReq: true,
                                        // isExpandRowsButtonReq: true,
                                        isSearchReq: true,
                                        formatterParams: {
                                            groupDataParams: [
                                                {
                                                    groupDataKey: "category",
                                                    groupDataId: "category_id",
                                                },
                                            ],
                                            expandedGroupDataParams: [
                                                {
                                                    groupDataKey: "subcategory",
                                                    groupDataId:
                                                        "subcategory_id",
                                                },
                                            ],
                                            consolidationDataKey: "channels",
                                            consolidationDataId: "channel_id",
                                            consolidationAggregateDataKey:
                                                "aggregates",
                                            consolidateDataIdentifier: 0,
                                        },
                                        style: {
                                            height: 300,
                                            expandedHeight: 280,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                ],
                                queryDimensions: [
                                    "category",
                                    "category_id",
                                    "channel_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: `category_perf_summary`,
                                    recordKey: "category_id##channel_id",
                                    consolidatedQueryConfig: {
                                        queryDimensions: [
                                            "category",
                                            "category_id",
                                        ],
                                        consolidatedColumnValue: 0,
                                        resultKey: `category_perf_summary_consolidated`,
                                    },
                                },
                                expandRowQueryConfig: {
                                    queryDimensions: [
                                        "subcategory_id",
                                        "subcategory",
                                        "category_id",
                                        "channel_id",
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `category_perf_summary_expanded`,
                                        recordKey:
                                            "category_id##subcategory_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "subcategory_id",
                                                "subcategory",
                                                "category_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `category_perf_summary_expanded_consolidated`,
                                        },
                                    },
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                icon: <BsBoxes size={16} />,
                                tabKey: "subcategory-analysis",
                                title: "Sub Category Analysis",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                identifier: "cpt-table",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `mi_${subModule}_ps_sa`,
                                        isGroupedTable: true,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_sa` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_sa` as TablePerfColDefType
                                            ],
                                        expandedTableDataKey: "subcategory_id",
                                        // isExpandable: true,
                                        // showExpandTableHeader: false,
                                        isLayoutButtonReq: false,
                                        isDownloadButtonReq: true,
                                        // isExpandRowsButtonReq: true,
                                        isSearchReq: true,
                                        formatterParams: {
                                            groupDataParams: [
                                                {
                                                    groupDataKey: "subcategory",
                                                    groupDataId:
                                                        "subcategory_id",
                                                },
                                            ],
                                            expandedGroupDataParams: [
                                                {
                                                    groupDataKey: "sku_title",
                                                    groupDataId:
                                                        "cross_channel_sku_id",
                                                },
                                            ],
                                            consolidationDataKey: "channels",
                                            consolidationDataId: "channel_id",
                                            consolidationAggregateDataKey:
                                                "aggregates",
                                            consolidateDataIdentifier: 0,
                                            expandedConsolidationDataFilters: [
                                                {
                                                    field: "cross_channel_sku_id",
                                                    condition: {
                                                        operater: "notEquals",
                                                        value: null as any,
                                                    },
                                                },
                                            ],
                                        },
                                        style: {
                                            height: 300,
                                            expandedHeight: 280,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                ],
                                queryDimensions: [
                                    "subcategory",
                                    "subcategory_id",
                                    "category",
                                    "channel_id",
                                ],
                                queryMetaInfo: {
                                    resultKey: `subcat_perf_summary`,
                                    recordKey: "subcategory_id##channel_id",
                                    consolidatedQueryConfig: {
                                        queryDimensions: [
                                            "subcategory",
                                            "subcategory_id",
                                            "category",
                                        ],
                                        consolidatedColumnValue: 0,
                                        resultKey: `subcat_perf_summary_consolidated`,
                                    },
                                },
                                expandRowQueryConfig: {
                                    queryDimensions: [
                                        "subcategory_id",
                                        "cross_channel_sku_id",
                                        "channel_id",
                                        "category",
                                        "brand",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `subcat_perf_summary_expanded`,
                                        recordKey:
                                            "subcategory_id##cross_channel_sku_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "cross_channel_sku_id",
                                                "subcategory_id",
                                                "category",
                                                "brand",
                                                "sku_title",
                                                "product_url",
                                                "thumbnail",
                                            ],
                                            isExcludeDimension: true,
                                            consolidatedColumnValue: 0,
                                            resultKey: `subcat_perf_summary_expanded_consolidated`,
                                        },
                                    },
                                    excludeDimensions: ["brand_id"],
                                    universeDimensions: ["channel_id"],
                                },
                                excludeFilter: ["brand_id"],
                            },
                            {
                                icon: <BsBoxSeam size={16} />,
                                tabKey: "product-analysis",
                                title: "Product Analysis",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                identifier: "ps-table",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `mi_${subModule}_ps_pa`,
                                        isGroupedTable: true,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_pa` as TablePerfColDefType
                                            ],
                                        // isExpandable: false,
                                        // showExpandTableHeader: false,
                                        isLayoutButtonReq: false,
                                        isDownloadButtonReq: true,
                                        // isExpandRowsButtonReq: true,
                                        isSearchReq: true,
                                        formatterParams: {
                                            groupDataParams: [
                                                {
                                                    groupDataKey:
                                                        "cross_channel_sku_id",
                                                    groupDataId:
                                                        "cross_channel_sku_id",
                                                },
                                            ],
                                            consolidationDataKey: "channels",
                                            consolidationDataId: "channel_id",
                                            consolidationAggregateDataKey:
                                                "aggregates",
                                            consolidateDataIdentifier: 0,
                                            consolidationDataFilters: [
                                                {
                                                    field: "cross_channel_sku_id",
                                                    condition: {
                                                        operater: "notEquals",
                                                        value: null as any,
                                                    },
                                                },
                                            ],
                                        },
                                        style: {
                                            height: 300,
                                            expandedHeight: 280,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                ],
                                queryDimensions: [
                                    "cross_channel_sku_id",
                                    "channel_id",
                                    "category",
                                    "brand",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                ],
                                queryMetaInfo: {
                                    resultKey: `sku_perf_summary`,
                                    recordKey:
                                        "cross_channel_sku_id##channel_id",
                                    consolidatedQueryConfig: {
                                        queryDimensions: [
                                            "cross_channel_sku_id",
                                            "subcategory_id",
                                            "brand_id",
                                        ],
                                        isExcludeDimension: true,
                                        consolidatedColumnValue: 0,
                                        resultKey: `sku_perf_summary_consolidated`,
                                    },
                                },
                                excludeFilter: ["brand_id"],
                                universeDimensions: ["channel_id"],
                            },
                            {
                                icon: <SlLocationPin size={16} />,
                                tabKey: "regional-analysis",
                                title: "Regional Analysis",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                identifier: "ps-table",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        type: `mi_${subModule}_ps_ra`,
                                        isGroupedTable: true,
                                        colDefs:
                                            getTablePerformanceColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_ra` as TablePerfColDefType
                                            ],
                                        expandedColDefs:
                                            getTablePerformanceExpandedColDefs(
                                                filterToAppend
                                            )[
                                                `mi_${subModule}_ps_ra` as TablePerfColDefType
                                            ],
                                        expandedTableDataKey: "region",
                                        // isExpandable: true,
                                        // showExpandTableHeader: false,
                                        isLayoutButtonReq: false,
                                        isDownloadButtonReq: true,
                                        // isExpandRowsButtonReq: true,
                                        isSearchReq: true,
                                        formatterParams: {
                                            groupDataParams: [
                                                {
                                                    groupDataKey: "region",
                                                    groupDataId: "region",
                                                },
                                            ],
                                            expandedGroupDataParams: [
                                                {
                                                    groupDataKey: "city",
                                                    groupDataId: "city",
                                                },
                                            ],
                                            consolidationDataKey: "channels",
                                            consolidationDataId: "channel_id",
                                            consolidationAggregateDataKey:
                                                "aggregates",
                                            consolidateDataIdentifier: 0,
                                        },
                                        style: {
                                            height: 300,
                                            expandedHeight: 280,
                                        },
                                    },
                                },
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                ],
                                queryDimensions: ["region", "channel_id"],
                                queryMetaInfo: {
                                    resultKey: `regional_perf_summary`,
                                    recordKey: "region##channel_id",
                                    consolidatedQueryConfig: {
                                        queryDimensions: ["region"],
                                        consolidatedColumnValue: 0,
                                        resultKey: `regional_perf_summary_consolidated`,
                                    },
                                },
                                expandRowQueryConfig: {
                                    queryDimensions: [
                                        "city",
                                        "region",
                                        "channel_id",
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `regional_perf_summary_expanded`,
                                        recordKey: "city##region##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: ["city", "region"],
                                            consolidatedColumnValue: 0,
                                            resultKey: `regional_perf_summary_expanded_consolidated`,
                                        },
                                    },
                                },
                                excludeFilter: ["brand_id"],
                            },
                        ],
                    ],
                },
            };
        }
    }
};

const createBaseDrilldownConfig = (
    filterToAppend: string,
    metricType: string,
    isAllSubcatSelected: boolean,
    module: RevstackDashboardModules,
    subModule = RevstackDashboardSubModules.OVERVIEW,
    currencySymbol: string
) => {
    const sections = {
        [RevstackCASectionIds.DDSO]: {
            icon: <></>,
            title: "Overview",
            tabKey: CA_DD_MODULES.OVERVIEW,
            moduleType: isAllSubcatSelected ? "table" : "default",
            sectionId: RevstackCASectionIds.DDSO,
            widget: isAllSubcatSelected
                ? RevstackTablePerformanceWidget
                : RevstackPoCategoryAnalysisWidget,
            config: {
                widgetCard: {
                    type: "drilldown",
                    isCardRequired: false,
                    isBodyPaddingReq: isAllSubcatSelected ? false : true,
                },
                isTableOnlyMode: true,
                isHeaderRequired: !isAllSubcatSelected,
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "subcategory",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as any[],
                                        defaultValue: [] as any[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                                // {
                                //     placement: "suffix",
                                //     type: "detailed-button",
                                //     props: {
                                //         name: "Detailed View",
                                //         onClick:()=>{},
                                //         className: "",
                                //         styles: {},
                                //     }

                                // }
                            ],
                        },
                    ],
                },
                tabs: {
                    isTrendsViewRequired: true,
                },
            },
            widgets: isAllSubcatSelected
                ? [
                    [
                        {
                            identifier: "dd_ca_ov_sp_ov",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "",
                            subTitle: "",
                            type: WidgetType.W7,
                            config: {
                                table: {
                                    colDefs: getRevstackDDTableColDefs(filterToAppend)["dd_ca_ov_sp_ov"],
                                    isGroupedTable: true,
                                    isSearchReq: true,
                                    isDrilldownTable: true,
                                    isCompetitionButtonReq: false,
                                    isLayoutButtonReq: true,
                                    isDownloadButtonReq: true,
                                    style: {
                                        height: "51vh",
                                        expandedHeight: "51vh",
                                    },
                                },
                            },
                            queryDimensions: [
                                "subcategory",
                                "subcategory_id",
                            ],
                            queryMetaInfo: {
                                resultKey: "subcat_overview_table_data",
                                sortMeasure: CommonWidgetMetrics.MARKET_SHARE,
                                recordKey: "subcategory_id",
                            },
                            infoMessage: "",
                            excludeFilter: ["brand_id"],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.OVERALL_SOV,
                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                CommonWidgetMetrics.ORGANIC_SOV,
                                CommonWidgetMetrics.PAID_SOV,
                                CommonWidgetMetrics.BRANDED_SEARCH,
                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                CommonWidgetMetrics.AVERAGE_RATING,
                                CommonWidgetMetrics.CONTENT_SCORE,
                                CommonWidgetMetrics.STOCK_IN_HAND,
                                CommonWidgetMetrics.FILL_RATE,
                                CommonWidgetMetrics.LISTING_STATUS,
                            ],
                        }
                    ],
                ]
                : [
                    [
                        {
                            identifier: "dd-po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            type: WidgetType.W1,
                            infoMessage:
                                REVSTACK_MEASURES[CommonWidgetMetrics.MARKET_SHARE].description,
                            measures: [CommonWidgetMetrics.MARKET_SHARE],
                            excludeFilter: ["brand_id"],
                        },
                        {
                            identifier: "dd-po-ov-sov",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Overall SOV",
                            type: WidgetType.W1,
                            infoMessage:
                                REVSTACK_MEASURES[CommonWidgetMetrics.OVERALL_SOV].description,
                            measures: [CommonWidgetMetrics.OVERALL_SOV],
                            excludeFilter: ["brand_id"],
                        },
                        {
                            identifier: "dd-po-wa",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Wt. Availability%",
                            type: WidgetType.W1,
                            infoMessage:
                                REVSTACK_MEASURES[CommonWidgetMetrics.WEIGHTED_AVAILABILITY].description,
                            measures: [CommonWidgetMetrics.WEIGHTED_AVAILABILITY],
                            excludeFilter: ["brand_id"],
                        },
                    ],
                    [
                        {
                            identifier: "dd-po-wa",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            type: WidgetType.W2,
                            title: "Traffic",
                            icon: <FiEye size={16} />,
                            config: {
                                drilldown: {
                                    title: "Top 3 laggards",
                                    dimensions: [
                                        WidgetDimensions.VALUE,
                                        WidgetDimensions.CHANGE,
                                        WidgetDimensions.NAME,
                                    ],
                                    limit: 3,
                                    redirectOnValueClick: true,
                                    redirectOnValueClickConfig: {
                                        platformToOpenOnValueClick: Platforms.PLANNING,
                                        moduleToOpenOnValueClick: RevstackDashboardModules.PRODUCT_ANALYSIS,
                                        type: "filters"
                                    },
                                    hasFilter: true,
                                },
                                filterOptions: [
                                    {
                                        label: "Overall SOV",
                                        value: CommonWidgetMetrics.OVERALL_SOV,
                                        excludeAsRow: true,
                                        unit: "%",
                                    },
                                    {
                                        label: "Organic SOV",
                                        value: CommonWidgetMetrics.ORGANIC_SOV,
                                        unit: "%",
                                    },
                                    {
                                        label: "Paid SOV",
                                        value: CommonWidgetMetrics.PAID_SOV,
                                        unit: "%",
                                    },
                                    {
                                        label: "Branded Search",
                                        value: CommonWidgetMetrics.BRANDED_SEARCH,
                                        unit: "%",
                                    },
                                ],
                                defaultFilterOption: {
                                    label: "Overall SOV",
                                    value: CommonWidgetMetrics.OVERALL_SOV,
                                    unit: "%",
                                },
                                drilldownEntryKey: "sku",
                            },
                            measures: [
                                CommonWidgetMetrics.OVERALL_SOV,
                                CommonWidgetMetrics.ORGANIC_SOV,
                                CommonWidgetMetrics.PAID_SOV,
                                CommonWidgetMetrics.BRANDED_SEARCH,
                            ],
                            queryDimensions: ["sku"],
                            excludeFilter: ["brand_id"],
                        },
                        {
                            identifier: "dd-po-wa",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            type: WidgetType.W2,
                            title: "Conversion",
                            icon: <BsBasket2 size={16} />,
                            config: {
                                drilldown: {
                                    title: "Top 3 laggards",
                                    dimensions: [
                                        WidgetDimensions.VALUE,
                                        WidgetDimensions.CHANGE,
                                        WidgetDimensions.NAME,
                                    ],
                                    limit: 3,
                                    redirectOnValueClick: true,
                                    redirectOnValueClickConfig: {
                                        platformToOpenOnValueClick: Platforms.PLANNING,
                                        moduleToOpenOnValueClick: RevstackDashboardModules.PRODUCT_ANALYSIS,
                                        type: "filters"
                                    },
                                    hasFilter: true,
                                },
                                filterOptions: [
                                    {
                                        label: "RPI",
                                        value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        unit: "",
                                    },
                                    {
                                        label: "Rating",
                                        value: CommonWidgetMetrics.AVERAGE_RATING,
                                        unit: "",
                                    },
                                    {
                                        label: "Content Score",
                                        value: CommonWidgetMetrics.CONTENT_SCORE,
                                        unit: "",
                                    },
                                ],
                                defaultFilterOption: {
                                    label: "RPI",
                                    value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    unit: "%",
                                },
                                drilldownEntryKey: "sku",
                            },
                            measures: [
                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                CommonWidgetMetrics.AVERAGE_RATING,
                                CommonWidgetMetrics.CONTENT_SCORE,
                            ],
                            queryDimensions: ["sku"],
                            excludeFilter: ["brand_id"],
                        },
                        {
                            identifier: "dd-po-wa",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            type: WidgetType.W2,
                            title: "Operations",
                            icon: <FiTruck size={16} />,
                            config: {
                                drilldown: {
                                    title: "Top 3 laggards",
                                    dimensions: [
                                        WidgetDimensions.VALUE,
                                        WidgetDimensions.CHANGE,
                                        WidgetDimensions.NAME,
                                    ],
                                    limit: 3,
                                    redirectOnValueClick: true,
                                    redirectOnValueClickConfig: {
                                        platformToOpenOnValueClick: Platforms.PLANNING,
                                        moduleToOpenOnValueClick: RevstackDashboardModules.PRODUCT_ANALYSIS,
                                        type: "filters"
                                    },
                                    hasFilter: true,
                                },
                                filterOptions: [
                                    {
                                        label: "Wt. Availability",
                                        value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        excludeAsRow: true,
                                    },
                                    {
                                        label: "Stock in Hand",
                                        value: CommonWidgetMetrics.STOCK_IN_HAND,
                                        unit: "Day(s)",
                                    },
                                    {
                                        label: "Fill Rate",
                                        value: CommonWidgetMetrics.FILL_RATE,
                                        unit: "%",
                                    },
                                    {
                                        label: "Listing Status",
                                        value: CommonWidgetMetrics.LISTING_STATUS,
                                        unit: "%",
                                    },
                                ],
                                defaultFilterOption: {
                                    label: "Wt. Availability",
                                    value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    unit: "%",
                                },
                                drilldownEntryKey: "sku",
                            },
                            measures: [
                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                CommonWidgetMetrics.LISTING_STATUS,
                                CommonWidgetMetrics.STOCK_IN_HAND,
                                CommonWidgetMetrics.FILL_RATE,
                            ],
                            queryDimensions: ["sku"],
                            excludeFilter: ["brand_id"],
                        },
                    ],
                ],
        },
        [RevstackCASectionIds.DDSBTA]: {
            icon: <></>,
            title: "Brand Analysis",
            tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
            moduleType: "table",
            sectionId: RevstackCASectionIds.DDSBTA,
            widget: RevstackTablePerformanceWidget,
            config: {
                widgetCard: {
                    isBodyPaddingReq: false,
                    isCardRequired: false,
                },
                isTableOnlyMode: true,
                isHeaderRequired: false,
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "subcategory",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as any[],
                                        defaultValue: [] as any[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                                // {
                                //     placement: "suffix",
                                //     type: "detailed-button",
                                //     props: {
                                //         name: "Detailed View",
                                //         onClick:()=>{},
                                //         className: "",
                                //         styles: {},
                                //     }

                                // }
                            ],
                        },
                    ],
                },
                tabs: {
                    isTrendsViewRequired: true,
                },
            },
            widgets: [
                [
                    {
                        identifier: "dd_ca_ov_sp_bd",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        title: "",
                        subTitle: "",
                        type: WidgetType.W7,
                        queryDimensions: ["brand", "brand_id"],
                        config: {
                            table: {
                                colDefs:
                                    getRevstackDDTableColDefs(filterToAppend)[
                                        "dd_ca_ov_sp_bd"
                                    ],
                                isGroupedTable: true,
                                isSearchReq: true,
                                isDrilldownTable: true,
                                isCompetitionButtonReq: true,
                                isLayoutButtonReq: true,
                                isDownloadButtonReq: true,
                                style: {
                                    height: "51vh",
                                    expandedHeight: "51vh",
                                },
                            },
                        },
                        queryMetaInfo: {
                            filterToSkip: "brand_id",
                            resultKey: "brand_table_data",
                            recordKey: "brand_id",
                        },
                        infoMessage: "",
                        isExcludeDimension: true,
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.AVERAGE_PRICE,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                        ],
                    },
                ],
            ],
        },
        [RevstackCASectionIds.DDSSTA]: {
            icon: <></>,
            title: "SKU Analysis",
            tabKey: CA_DD_MODULES.SKU_ANALYTICS,
            moduleType: "table",
            sectionId: RevstackCASectionIds.DDSSTA,
            widget: RevstackTablePerformanceWidget,
            config: {
                widgetCard: {
                    isBodyPaddingReq: false,
                    isCardRequired: false,
                },
                isTableOnlyMode: true,
                isHeaderRequired: false,
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "subcategory",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as any[],
                                        defaultValue: [] as any[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                                // {
                                //     placement: "suffix",
                                //     type: "detailed-button",
                                //     props: {
                                //         name: "Detailed View",
                                //         onClick:()=>{},
                                //         className: "",
                                //         styles: {},
                                //     }

                                // }
                            ],
                        },
                    ],
                },
                tabs: {
                    isTrendsViewRequired: true,
                },
            },
            widgets: [
                [
                    {
                        identifier: "dd_ca_ov_sp_sku",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        title: "",
                        subTitle: "",
                        type: WidgetType.W7,
                        config: {
                            table: {
                                colDefs:
                                    getRevstackDDTableColDefs(filterToAppend)[
                                        "dd_ca_ov_sp_sku"
                                    ],
                                isGroupedTable: true,
                                isSearchReq: true,
                                isDrilldownTable: true,
                                isCompetitionButtonReq: true,
                                isLayoutButtonReq: true,
                                isDownloadButtonReq: true,
                                style: {
                                    height: "51vh",
                                    expandedHeight: "51vh",
                                },
                            },
                        },
                        queryDimensions: [
                            "category",
                            "sku",
                            "sku_title",
                            "product_url",
                            "thumbnail",
                            "brand",
                            "brand_id",
                        ],
                        queryMetaInfo: {
                            filterToSkip: "brand_id",
                            resultKey: "sku_table_data",
                            recordKey: "sku",
                        },
                        isExcludeDimension: true,
                        infoMessage: "",
                        isMeasureAppendRequired: false,
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                        ],
                    },
                ],
            ],
        },
        [RevstackCASectionIds.DDSCRTA]: {
            icon: <></>,
            title: "Cross Region",
            tabKey: CA_DD_MODULES.CROSS_REGION,
            moduleType: "table",
            sectionId: RevstackCASectionIds.DDSCRTA,
            widget: RevstackTablePerformanceWidget,
            config: {
                widgetCard: {
                    isBodyPaddingReq: false,
                    isCardRequired: false,
                },
                isTableOnlyMode: true,
                isHeaderRequired: false,
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "subcategory",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as any[],
                                        defaultValue: [] as any[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                                // {
                                //     placement: "suffix",
                                //     type: "detailed-button",
                                //     props: {
                                //         name: "Detailed View",
                                //         onClick:()=>{},
                                //         className: "",
                                //         styles: {},
                                //     }

                                // }
                            ],
                        },
                    ],
                },
                tabs: {
                    isTrendsViewRequired: true,
                },
            },
            widgets: [
                [
                    {
                        identifier: "dd_ca_ov_sp_cr",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        title: "",
                        subTitle: "",
                        type: WidgetType.W7,
                        infoMessage: "",
                        isMeasureAppendRequired: false,
                        config: {
                            table: {
                                colDefs:
                                    getRevstackDDTableColDefs(filterToAppend)[
                                        "dd_ca_ov_sp_cr"
                                    ],
                                isGroupedTable: true,
                                isSearchReq: true,
                                isDrilldownTable: true,
                                isCompetitionButtonReq: false,
                                isLayoutButtonReq: true,
                                isDownloadButtonReq: true,
                                style: {
                                    height: "51vh",
                                    expandedHeight: "51vh",
                                },
                            },
                        },
                        queryDimensions: ["region"],
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                        ],
                        queryMetaInfo: {
                            resultKey: "region_table_data",
                            recordKey: "region",
                            sortConfig: {
                                sortKey: `market_share${filterToAppend}_change`,
                            },
                            contributionMeasures: [
                                `region_contribution${filterToAppend}`,
                            ],
                            dimValueToTruncate: "-Region",
                        },
                        excludeFilter: ["brand_id"],
                    },
                ],
            ],
        },
        [RevstackCASectionIds.DDSCCTA]: {
            icon: <></>,
            title: "Cross Channels",
            tabKey: CA_DD_MODULES.CROSS_CHANNEL,
            moduleType: "table",
            sectionId: RevstackCASectionIds.DDSCCTA,
            widget: RevstackTablePerformanceWidget,
            config: {
                widgetCard: {
                    isBodyPaddingReq: false,
                    isCardRequired: false,
                },
                isTableOnlyMode: true,
                isHeaderRequired: false,
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "subcategory",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as any[],
                                        defaultValue: [] as any[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                                // {
                                //     placement: "suffix",
                                //     type: "detailed-button",
                                //     props: {
                                //         name: "Detailed View",
                                //         onClick:()=>{},
                                //         className: "",
                                //         styles: {},
                                //     }

                                // }
                            ],
                        },
                    ],
                },
                tabs: {
                    isTrendsViewRequired: true,
                },
            },
            widgets: [
                [
                    {
                        identifier: "dd_ca_ov_sp_cc",
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        title: "",
                        subTitle: "",
                        type: WidgetType.W7,
                        config: {
                            table: {
                                colDefs:
                                    getRevstackDDTableColDefs(filterToAppend)[
                                        "dd_ca_ov_sp_cc"
                                    ],
                                isGroupedTable: true,
                                isDrilldownTable: true,
                                isSearchReq: true,
                                isCompetitionButtonReq: false,
                                isLayoutButtonReq: true,
                                isDownloadButtonReq: true,
                                style: {
                                    height: "51vh",
                                    expandedHeight: "51vh",
                                },
                            },
                        },
                        queryDimensions: ["channel_id"],
                        infoMessage: "",
                        isMeasureAppendRequired: false,
                        measures: [
                            CommonWidgetMetrics.MARKET_SHARE,
                            CommonWidgetMetrics.OVERALL_SOV,
                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                            CommonWidgetMetrics.ORGANIC_SOV,
                            CommonWidgetMetrics.PAID_SOV,
                            CommonWidgetMetrics.BRANDED_SEARCH,
                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                            CommonWidgetMetrics.AVERAGE_RATING,
                            CommonWidgetMetrics.CONTENT_SCORE,
                            CommonWidgetMetrics.STOCK_IN_HAND,
                            CommonWidgetMetrics.FILL_RATE,
                            CommonWidgetMetrics.LISTING_STATUS,
                        ],
                        excludeFilter: ["brand_id"],
                        queryMetaInfo: {
                            resultKey: "channel_table_data",
                            recordKey: "channel_id",
                            filterToSkip: "channel_id",
                        },
                    },
                ],
            ],
        },
    };

    const drilldownConfigSections: any = {
        [RevstackCASectionIds.SP]: {
            ...sections,
            [RevstackCASectionIds.DDSO]: {
                ...sections[RevstackCASectionIds.DDSO],
                module: RevstackDashboardModules.CATEGORY_ANALYSIS,
                config: {
                    ...sections[RevstackCASectionIds.DDSO].config,
                    tabs: {
                        ...sections[RevstackCASectionIds.DDSO].config.tabs,
                        hideTrendsTabSection: false,
                    },
                },
            },
        },
    };

    const trendsFiltersConfig = {
        primaryFilters: {
            filterKey: CommonWidgetMetrics.CATEGORY,
            filterDataKey: `${CommonWidgetMetrics.CATEGORY}_id`,
            relatedColDataKey: CommonWidgetMetrics.CATEGORY,
            filterValue: ["all"],
            allOptions: {
                label: "All Categories",
                value: OptionValues.ALL,
            },
        },
        secondaryFilters: [
            {
                filterKey: CommonWidgetMetrics.CHANNEL,
                filterDataKey: `${CommonWidgetMetrics.CHANNEL}_id`,
                filterValue: ["all"],
                relatedColDataKey: CommonWidgetMetrics.CHANNEL,
                allOptions: {
                    label: "All Channels",
                    value: OptionValues.ALL,
                },
            },
            {
                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                filterDataKey: CommonWidgetMetrics.REGION,
                filterValue: ["all"],
                fetchOptions: true,
                requestParams: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "any-id-can-be-added",
                    queryDimensions: [RevstackFilterKey.REGION],
                    isExcludeDimension: false,
                    excludeFilter: [RevstackFilterKey.BRAND],
                    filterToSkip: "",
                },
                allOptions: { label: "PAN India", value: OptionValues.ALL },
            },
        ],
    };

    if (module === RevstackDashboardModules.CATEGORY_ANALYSIS) {
        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSO]: {
                        ...sections[RevstackCASectionIds.DDSO],
                        config: {
                            ...sections[RevstackCASectionIds.DDSO].config,
                        },
                        widgets: isAllSubcatSelected
                            ? [
                                [
                                    {
                                        identifier: "dd_ca_traffic_sp_ov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "",
                                        subTitle: "",
                                        type: WidgetType.W7,
                                        queryDimensions: [
                                            "subcategory",
                                            "subcategory_id",
                                        ],
                                        queryMetaInfo: {
                                            resultKey:
                                                `overview_${subModule}_table_data`,
                                            recordKey: "subcategory_id",
                                            sortMeasure:
                                                CommonWidgetMetrics.MARKET_SHARE,
                                        },
                                        config: {
                                            table: {
                                                colDefs: getRevstackDDTableColDefs(filterToAppend)["dd_ca_traffic_sp_ov"],
                                                isGroupedTable: false,
                                                isSearchReq: true,
                                                isDrilldownTable: true,
                                                isCompetitionButtonReq: false,
                                                isLayoutButtonReq: true,
                                                isDownloadButtonReq: true,
                                                style: {
                                                    height: "51vh",
                                                    expandedHeight: "51vh",
                                                },
                                            }
                                        },
                                        infoMessage: "",
                                        isExcludeDimension: true,
                                        measures: [
                                            CommonWidgetMetrics.MARKET_SHARE,
                                            CommonWidgetMetrics.OVERALL_SOV,
                                            CommonWidgetMetrics.ORGANIC_SOV,
                                            CommonWidgetMetrics.PAID_SOV,
                                            CommonWidgetMetrics.PAID_IMPRESSION,
                                        ],
                                    },
                                ],
                            ]
                            : [
                                [
                                    {
                                        identifier: "to-olsov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "Overall SOV%",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.OVERALL_SOV].description,
                                        measures: [CommonWidgetMetrics.OVERALL_SOV],
                                        excludeFilter: ["brand_id"],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Overall SOV",
                                                    value: CommonWidgetMetrics.OVERALL_SOV,
                                                    unit: "%",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Overall SOV",
                                                value: CommonWidgetMetrics.OVERALL_SOV,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",
                                        },
                                    },
                                    {
                                        identifier: "to-pov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "Paid SOV%",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.PAID_SOV].description,
                                        measures: [CommonWidgetMetrics.PAID_SOV],
                                        excludeFilter: ["brand_id"],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Paid SOV",
                                                    value: CommonWidgetMetrics.PAID_SOV,
                                                    unit: "%",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Paid SOV",
                                                value: CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",
                                        },
                                    },
                                    {
                                        identifier: "to-osov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "Organic SOV%",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.ORGANIC_SOV].description,
                                        measures: [CommonWidgetMetrics.ORGANIC_SOV],
                                        excludeFilter: ["brand_id"],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Organic SOV",
                                                    value: CommonWidgetMetrics.ORGANIC_SOV,
                                                    unit: "%",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Organic SOV",
                                                value: CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",
                                        },
                                    },
                                ],
                            ],
                    },
                    [RevstackCASectionIds.DDSBTA]: {
                        ...sections[RevstackCASectionIds.DDSBTA],
                        config: {
                            ...sections[RevstackCASectionIds.DDSBTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_traffic_sp_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: ["brand", "brand_id"],
                                    queryMetaInfo: {
                                        resultKey: `brand_${subModule}_table_data`,
                                        recordKey: "brand_id",
                                        sortMeasure:
                                            CommonWidgetMetrics.MARKET_SHARE,
                                    },
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_traffic_sp_bd"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    infoMessage: "",
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.PAID_IMPRESSION,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSSTA]: {
                        ...sections[RevstackCASectionIds.DDSSTA],
                        config: {
                            ...sections[RevstackCASectionIds.DDSSTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_traffic_sp_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_traffic_sp_sku"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: [
                                        "category",
                                        "sku",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                        "brand",
                                        "brand_id",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: `sku_${subModule}_table_data`,
                                        recordKey: "sku",
                                    },
                                    isExcludeDimension: true,
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.PAID_IMPRESSION,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSKATA]: {
                        icon: <></>,
                        title: "Keyword Analysis",
                        tabKey: CA_DD_MODULES.KEYWORD_ANALYSIS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSKATA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "subcategory",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [] as any[],
                                                    defaultValue: [] as any[],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: true,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_traffic_sp_kwd",
                                    cubeId: RevstackCubeIds.KA_NATIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_traffic_sp_kwd"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    infoMessage: "",
                                    measures: [
                                        CommonWidgetMetrics.SEARCH_INDEX,
                                        CommonWidgetMetrics.KEYWORD_OVERALL_SOV,
                                        CommonWidgetMetrics.KEYWORD_ORGANIC_SOV,
                                        CommonWidgetMetrics.KEYWORD_PAID_SOV,
                                        CommonWidgetMetrics.KEYWORD_COUNT,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                    ],
                                    queryDimensions: ["keyword", "intent"],
                                    queryMetaInfo: {
                                        baseFilter: "keyword_overall_sov_value",
                                        resultKey: `keyword_dd_${subModule}_table_data`,
                                        recordKey: "keyword",
                                        sortConfig: {
                                            sortKey: `search_index_value`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCRTA]: {
                        ...sections[RevstackCASectionIds.DDSCRTA],
                        config: {
                            ...sections[RevstackCASectionIds.DDSCRTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_traffic_sp_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_traffic_sp_cr"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["region"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.PAID_IMPRESSION,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `region_${subModule}_table_data`,
                                        recordKey: "region",
                                        sortConfig: {
                                            sortKey: `market_share${filterToAppend}_change`,
                                        },
                                        contributionMeasures: [
                                            `region_contribution${filterToAppend}`,
                                        ],
                                        dimValueToTruncate: "-Region",
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCCTA]: {
                        ...sections[RevstackCASectionIds.DDSCCTA],
                        config: {
                            ...sections[RevstackCASectionIds.DDSCCTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_traffic_sp_cc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_traffic_sp_cc"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["channel_id"],
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.PAID_IMPRESSION,
                                    ],
                                    excludeFilter: ["brand_id"],
                                    queryMetaInfo: {
                                        resultKey: "channel_traffic_table_data",
                                        recordKey: "channel_id",
                                        filterToSkip: "channel_id",
                                    },
                                },
                            ],
                        ],
                    },
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDCSO]: {
                        ...sections[RevstackCASectionIds.DDSO],
                        sectionId: RevstackCASectionIds.DDCSO,
                        config: {
                            ...sections[RevstackCASectionIds.DDSO].config,
                        },
                        widgets: isAllSubcatSelected
                            ? [
                                [
                                    {
                                        identifier: "dd_ca_conversion_sp_ov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "",
                                        subTitle: "",
                                        type: WidgetType.W7,
                                        queryDimensions: [
                                            "subcategory",
                                            "subcategory_id",
                                        ],
                                        queryMetaInfo: {
                                            resultKey:
                                                `overview_${subModule}_table_data`,
                                            sortMeasure:
                                                CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            recordKey: "subcategory_id",
                                        },
                                        config: {
                                            table: {
                                                colDefs: getRevstackDDTableColDefs(filterToAppend)["dd_ca_conversion_sp_ov"],
                                                isGroupedTable: false,
                                                isSearchReq: true,
                                                isDrilldownTable: true,
                                                isCompetitionButtonReq: false,
                                                isLayoutButtonReq: true,
                                                isDownloadButtonReq: true,
                                            }
                                        },
                                        infoMessage: "",
                                        excludeFilter: ["brand_id"],
                                        measures: [
                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            CommonWidgetMetrics.AVERAGE_PRICE,
                                            CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                            CommonWidgetMetrics.AVERAGE_RATING,
                                            CommonWidgetMetrics.CONTENT_SCORE,
                                        ],
                                    },
                                ],
                            ]
                            : [
                                [
                                    {
                                        identifier: "dd-co-rpi",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "RPI",
                                        subTitle: "By PPU",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.RELATIVE_PRICE_INDEX].description,
                                        measures: [
                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        ],
                                        excludeFilter: ["brand_id"],
                                    },
                                    {
                                        identifier: "dd-co-ap",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "Avg Price",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.AVERAGE_PRICE].description,
                                        measures: [CommonWidgetMetrics.AVERAGE_PRICE],
                                        excludeFilter: ["brand_id"],
                                    },
                                    {
                                        identifier: "dd-co-wd",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "Wt. Discount",
                                        type: WidgetType.W1,
                                        infoMessage:
                                            REVSTACK_MEASURES[CommonWidgetMetrics.WEIGHTED_DISCOUNT].description,
                                        measures: [CommonWidgetMetrics.WEIGHTED_DISCOUNT],
                                        excludeFilter: ["brand_id"],
                                    },
                                ],
                                [
                                    {
                                        identifier: "dd-co-rpi",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        type: WidgetType.W1,
                                        title: "RPI",
                                        subTitle: "Overall",
                                        icon: <FiEye size={16} />,
                                        infoMessage: REVSTACK_MEASURES[CommonWidgetMetrics.RELATIVE_PRICE_INDEX].description,
                                        hasfilters: false,
                                        measures: [
                                            CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        ],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "RPI",
                                                    value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                    unit: "%",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "RPI",
                                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",
                                        },
                                        excludeFilter: ["brand_id"],
                                    },
                                    {
                                        identifier: "dd-co-ar",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        type: WidgetType.W1,
                                        title: "Avg. Rating",
                                        icon: <BsBasket2 size={16} />,
                                        infoMessage: REVSTACK_MEASURES[CommonWidgetMetrics.AVERAGE_RATING].description,
                                        hasfilters: false,
                                        measures: [CommonWidgetMetrics.AVERAGE_RATING],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Avg. Rating",
                                                    value: CommonWidgetMetrics.AVERAGE_RATING,
                                                    unit: "",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Avg. Rating",
                                                value: CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",

                                        },
                                        excludeFilter: ["brand_id"],
                                    },
                                    {
                                        identifier: "dd-co-cs",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        type: WidgetType.W1,
                                        title: "Content Score",
                                        icon: <FiTruck size={16} />,
                                        infoMessage: REVSTACK_MEASURES[CommonWidgetMetrics.CONTENT_SCORE].description,
                                        hasFilters: false,
                                        measures: [CommonWidgetMetrics.CONTENT_SCORE],
                                        config: {
                                            drilldown: {
                                                title: "Top 3 laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 3,
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Content Score",
                                                    value: CommonWidgetMetrics.CONTENT_SCORE,
                                                    unit: "",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Content Score",
                                                value: CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory"
                                        },
                                        excludeFilter: ["brand_id"],
                                    },
                                ],
                            ],
                    },
                    [RevstackCASectionIds.DDCSBTA]: {
                        ...sections[RevstackCASectionIds.DDSBTA],
                        sectionId: RevstackCASectionIds.DDCSBTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSBTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_conversion_sp_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: ["brand", "brand_id"],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_conversion_sp_bd"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: `brand_${subModule}_table_data`,
                                        recordKey: "brand_id",
                                    },
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDCSSTA]: {
                        ...sections[RevstackCASectionIds.DDSSTA],
                        sectionId: RevstackCASectionIds.DDCSSTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSSTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_conversion_sp_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_conversion_sp_sku"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },

                                    queryDimensions: [
                                        "category",
                                        "sku",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                        "brand",
                                        "brand_id",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: `sku_${subModule}_table_data`,
                                        recordKey: "sku",
                                    },
                                    isExcludeDimension: true,
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDCSCRTA]: {
                        ...sections[RevstackCASectionIds.DDSCRTA],
                        sectionId: RevstackCASectionIds.DDCSCRTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSCRTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_conversion_sp_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_conversion_sp_cr"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryDimensions: ["region"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `region_${subModule}_table_data`,
                                        recordKey: "region",
                                        sortConfig: {
                                            sortKey: `market_share${filterToAppend}_change`,
                                        },
                                        contributionMeasures: [
                                            `region_contribution${filterToAppend}`,
                                        ],
                                        dimValueToTruncate: "-Region",
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDCSCCTA]: {
                        ...sections[RevstackCASectionIds.DDSCCTA],
                        sectionId: RevstackCASectionIds.DDCSCCTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSCCTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_conversion_sp_cc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_conversion_sp_cc"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryDimensions: ["channel_id"],
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.WEIGHTED_DISCOUNT,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    excludeFilter: ["brand_id"],
                                    queryMetaInfo: {
                                        resultKey: `channel_${subModule}_table_data`,
                                        recordKey: "channel_id",
                                        filterToSkip: "channel_id",
                                    },
                                },
                            ],
                        ],
                    },
                },
            };
        }

        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDOSO]: {
                        ...sections[RevstackCASectionIds.DDSO],
                        sectionId: RevstackCASectionIds.DDOSO,
                        config: {
                            ...sections[RevstackCASectionIds.DDSO].config,
                        },
                        widgets: isAllSubcatSelected
                            ? [
                                [
                                    {
                                        identifier: "dd_ca_operations_sp_ov",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        title: "",
                                        subTitle: "",
                                        type: WidgetType.W7,
                                        queryDimensions: [
                                            "subcategory",
                                            "subcategory_id",
                                        ],
                                        queryMetaInfo: {
                                            resultKey:
                                                `subcat_overview_${subModule}_table_data`,
                                            sortMeasure:
                                                CommonWidgetMetrics.MARKET_SHARE,
                                            recordKey: "subcategory_id",
                                        },
                                        config: {
                                            table: {
                                                colDefs: getRevstackDDTableColDefs(filterToAppend)["dd_ca_operations_sp_ov"],
                                                isGroupedTable: false,
                                                isSearchReq: true,
                                                isDrilldownTable: true,
                                                isCompetitionButtonReq: false,
                                                isLayoutButtonReq: true,
                                                isDownloadButtonReq: true,
                                            }
                                        },
                                        excludeFilter: ["brand_id"],
                                        measures: [
                                            CommonWidgetMetrics.MARKET_SHARE,
                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            CommonWidgetMetrics.LISTING_STATUS,
                                            CommonWidgetMetrics.FILL_RATE,
                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                        ],
                                    },
                                ],
                            ]
                            : [
                                [
                                    [
                                        {
                                            identifier: "dd-op-wa",
                                            cubeId: RevstackCubeIds.PO_REGIONAL,
                                            title: "Wt. Availability",
                                            type: WidgetType.W1,
                                            infoMessage:
                                                REVSTACK_MEASURES[CommonWidgetMetrics.WEIGHTED_AVAILABILITY].description,

                                            measures: [
                                                CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            ],
                                            excludeFilter: ["brand_id"],
                                        },
                                        {
                                            identifier: "dd-op-ls",
                                            cubeId: RevstackCubeIds.PO_REGIONAL,
                                            title: "Listing Status",
                                            type: WidgetType.W1,
                                            infoMessage:
                                                REVSTACK_MEASURES[CommonWidgetMetrics.LISTING_STATUS].description,
                                            measures: [CommonWidgetMetrics.LISTING_STATUS],
                                            excludeFilter: ["brand_id"],
                                        },
                                    ],
                                    [
                                        {
                                            identifier: "dd-op-sih",
                                            cubeId: RevstackCubeIds.PO_REGIONAL,
                                            title: "Stock in Hand",
                                            type: WidgetType.W1,
                                            infoMessage:
                                                REVSTACK_MEASURES[CommonWidgetMetrics.STOCK_IN_HAND].description,
                                            measures: [CommonWidgetMetrics.STOCK_IN_HAND],
                                            excludeFilter: ["brand_id"],
                                        },
                                        {
                                            identifier: "dd-op-fr",
                                            cubeId: RevstackCubeIds.PO_REGIONAL,
                                            type: WidgetType.W1,
                                            infoMessage: REVSTACK_MEASURES[CommonWidgetMetrics.FILL_RATE].description,
                                            title: "Fill Rate",
                                            icon: <FiEye size={16} />,
                                            hasfilters: false,
                                            measures: [CommonWidgetMetrics.FILL_RATE],
                                            queryDimensions: ["sku"],
                                            laggardField: "sku",
                                            excludeFilter: ["brand_id"],
                                        },
                                    ]
                                ],
                                [
                                    {
                                        identifier: "dd-op-rpi",
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        type: WidgetType.W1,
                                        title: "RPI",
                                        infoMessage: REVSTACK_MEASURES[CommonWidgetMetrics.RELATIVE_PRICE_INDEX].description,
                                        subTitle: "Overall",
                                        icon: <FiEye size={16} />,
                                        hasfilters: false,
                                        measures: [
                                            CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            CommonWidgetMetrics.LISTING_STATUS,
                                            CommonWidgetMetrics.STOCK_IN_HAND,
                                            CommonWidgetMetrics.FILL_RATE,
                                        ],
                                        config: {
                                            enableMetricAndGraph: false,
                                            drilldown: {
                                                title: "Top laggards",
                                                dimensions: [
                                                    WidgetDimensions.VALUE,
                                                    WidgetDimensions.CHANGE,
                                                    WidgetDimensions.NAME,
                                                ],
                                                limit: 5,
                                                hasFilter: true
                                            },
                                            filterOptions: [
                                                {
                                                    label: "Wt. Availability",
                                                    value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                    unit: "%",
                                                },
                                                {
                                                    label: "Listing Status",
                                                    value: CommonWidgetMetrics.LISTING_STATUS,
                                                    unit: "%",
                                                },
                                                {
                                                    label: "Stock In Hand",
                                                    value: CommonWidgetMetrics.STOCK_IN_HAND,
                                                    unit: "%",
                                                },
                                                {
                                                    label: "Fill Rate",
                                                    value: CommonWidgetMetrics.FILL_RATE,
                                                    unit: "%",
                                                },
                                            ],
                                            defaultFilterOption: {
                                                label: "Wt. Availability",
                                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                unit: "%",
                                            },
                                            drilldownEntryKey: "subcategory",
                                        },
                                        excludeFilter: ["brand_id"],
                                    }
                                ]
                            ],
                    },
                    [RevstackCASectionIds.DDOSBTA]: {
                        ...sections[RevstackCASectionIds.DDSBTA],
                        sectionId: RevstackCASectionIds.DDOSBTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSBTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_operations_sp_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: ["brand", "brand_id"],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_operations_sp_bd"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: `brand_${subModule}_table_data`,
                                        recordKey: "brand_id",
                                    },
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDOSSTA]: {
                        ...sections[RevstackCASectionIds.DDSSTA],
                        sectionId: RevstackCASectionIds.DDOSSTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSSTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_operations_sp_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_operations_sp_sku"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryDimensions: [
                                        "category",
                                        "sku",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                        "brand",
                                        "brand_id",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: `sku_${subModule}_table_data`,
                                        recordKey: "sku",
                                    },
                                    isExcludeDimension: true,
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDOSCRTA]: {
                        ...sections[RevstackCASectionIds.DDSCRTA],
                        sectionId: RevstackCASectionIds.DDOSCRTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSCRTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_operations_sp_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_operations_sp_cr"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryDimensions: ["region"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: `region_${subModule}_table_data`,
                                        recordKey: "region",
                                        sortConfig: {
                                            sortKey: `market_share${filterToAppend}_change`,
                                        },
                                        contributionMeasures: [
                                            `region_contribution${filterToAppend}`,
                                        ],
                                        dimValueToTruncate: "-Region",
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDOSCCTA]: {
                        ...sections[RevstackCASectionIds.DDSCCTA],
                        sectionId: RevstackCASectionIds.DDOSCCTA,
                        config: {
                            ...sections[RevstackCASectionIds.DDSCCTA].config,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_ca_operations_sp_cc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_ca_operations_sp_cc"],
                                            isGroupedTable: false,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                        },
                                    },
                                    queryDimensions: ["channel_id"],
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                    ],
                                    excludeFilter: ["brand_id"],
                                    queryMetaInfo: {
                                        resultKey: `channel_${subModule}_table_data`,
                                        recordKey: "channel_id",
                                        filterToSkip: "channel_id",
                                    },
                                },
                            ],
                        ],
                    },
                },
            };
        }
    }

    if (module === RevstackDashboardModules.NAVIGATOR) {
        const trendsFiltersConfig = {
            primaryFilters: {
                filterKey: CommonWidgetMetrics.CATEGORY,
                filterDataKey: `${CommonWidgetMetrics.CATEGORY}_id`,
                relatedColDataKey: CommonWidgetMetrics.CATEGORY,
                filterValue: ["all"],
                allOptions: {
                    label: "All Categories",
                    value: OptionValues.ALL,
                },
            },
            secondaryFilters: [
                {
                    filterKey: CommonWidgetMetrics.CHANNEL,
                    filterDataKey: `${CommonWidgetMetrics.CHANNEL}_id`,
                    filterValue: ["all"],
                    relatedColDataKey: CommonWidgetMetrics.CHANNEL,
                    allOptions: {
                        label: "All Channels",
                        value: OptionValues.ALL,
                    },
                },
                {
                    filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                    filterDataKey: CommonWidgetMetrics.REGION,
                    filterValue: ["all"],
                    fetchOptions: true,
                    requestParams: {
                        cubeId: RevstackCubeIds.PO_REGIONAL,
                        queryId: "any-id-can-be-added",
                        queryDimensions: [RevstackFilterKey.REGION],
                        isExcludeDimension: false,
                        excludeFilter: [RevstackFilterKey.BRAND],
                        filterToSkip: "",
                    },
                    allOptions: { label: "National", value: OptionValues.ALL },
                },
            ],
        };
        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.CA]: {
                    [RevstackCASectionIds.DDSO]: {
                        icon: <></>,
                        title: "Overview",
                        tabKey: CA_DD_MODULES.OVERVIEW,
                        moduleType: "default",
                        sectionId: RevstackCASectionIds.DDSO,
                        widget: RevstackPoCategoryAnalysisWidget,
                        config: {
                            widgetCard: {
                                type: "drilldown",
                                isBodyPaddingReq: true,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: !isAllSubcatSelected,
                            module: RevstackDashboardModules.NAVIGATOR,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: true,
                            },
                            trendsFiltersConfig,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd-po-ms",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Market Share",
                                    subTitle: titleCase(metricType),
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.MARKET_SHARE
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-ov-sov",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Overall SOV",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.OVERALL_SOV
                                        ].description,
                                    measures: [CommonWidgetMetrics.OVERALL_SOV],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Wt. Availability%",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics
                                                .WEIGHTED_AVAILABILITY
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                            [
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Traffic",
                                    icon: <FiEye size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Overall SOV",
                                                value: CommonWidgetMetrics.OVERALL_SOV,
                                                excludeAsRow: true,
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                value: CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                value: CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                value: CommonWidgetMetrics.BRANDED_SEARCH,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Conversion",
                                    icon: <BsBasket2 size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "RPI",
                                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                label: "Rating",
                                                value: CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                value: CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Operations",
                                    icon: <FiTruck size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Wt. Availability",
                                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                excludeAsRow: true,
                                            },
                                            {
                                                label: "Stock in Hand",
                                                value: CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "Day(s)",
                                            },
                                            {
                                                label: "Fill Rate",
                                                value: CommonWidgetMetrics.FILL_RATE,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                value: CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCTA]: {
                        icon: <></>,
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sc"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "subcategory",
                                                        groupDataId:
                                                            "subcategory_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    queryDimensions: [
                                        "subcategory",
                                        "subcategory_id",
                                        "category",
                                        "channel_id",
                                    ],
                                    queryMetaInfo: {
                                        resultKey: "subcat_dd_summary",
                                        recordKey: "subcategory_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "subcategory",
                                                "subcategory_id",
                                                "category",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `subcat_dd_summary_consolidated`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSBTA]: {
                        icon: <></>,
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSBTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: [
                                        "brand",
                                        "brand_id",
                                        "channel_id",
                                    ],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_bd"],
                                            expandedColDefs:
                                                getTablePerformanceExpandedColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_bd"],
                                            expandedTableDataKey: "brand_id",
                                            // isExpandable: true,
                                            showExpandTableHeader: false,
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "brand",
                                                        groupDataId: "brand_id",
                                                    },
                                                ],
                                                expandedGroupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "subcategory",
                                                        groupDataId:
                                                            "subcategory_id",
                                                    },
                                                    {
                                                        groupDataKey: "brand",
                                                        groupDataId: "brand_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "brand_dd_summary",
                                        recordKey: "brand_id##channel_id",
                                        consolidatedQueryConfig: {
                                            isExcludeDimension: true,
                                            queryDimensions: [
                                                "brand",
                                                "brand_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `brand_dd_summary_consolidated`,
                                        },
                                    },
                                    infoMessage: "",
                                    universeDimensions: ["channel_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    expandRowQueryConfig: {
                                        queryDimensions: [
                                            "brand_id",
                                            "subcategory_id",
                                            "subcategory",
                                            "channel_id",
                                        ],
                                        queryMetaInfo: {
                                            filterToSkip: "brand_id",
                                            resultKey: `brand_dd_summary_expanded`,
                                            recordKey:
                                                "brand_id##subcategory_id##channel_id",
                                            consolidatedQueryConfig: {
                                                queryDimensions: [
                                                    "brand_id",
                                                    "subcategory_id",
                                                    "subcategory",
                                                ],
                                                consolidatedColumnValue: 0,
                                                isExcludeDimension: true,
                                                resultKey: `brand_dd_summary_expanded_consolidated`,
                                            },
                                        },
                                        universeDimensions: [
                                            "channel_id",
                                            "subcategory_id",
                                        ],
                                    },
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSSTA]: {
                        icon: <></>,
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSSTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sku"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "cross_channel_sku_id",
                                                        groupDataId:
                                                            "cross_channel_sku_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: [
                                        "cross_channel_sku_id",
                                        "channel_id",
                                        "category",
                                        "brand",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "sku_dd_summary",
                                        recordKey:
                                            "cross_channel_sku_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "cross_channel_sku_id",
                                                "brand_id",
                                            ],
                                            isExcludeDimension: true,
                                            consolidatedColumnValue: 0,
                                            resultKey: `sku_dd_summary_consolidated`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                    universeDimensions: ["channel_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_cr"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            isDownloadButtonReq: true,
                                            colVisibilityMode: "group",
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "region",
                                                        groupDataId: "region",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["region", "channel_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: "region_dd_summary",
                                        recordKey: "region##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: ["region"],
                                            consolidatedColumnValue: 0,
                                            resultKey: `region_dd_summary_consolidated`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.SA]: {
                    [RevstackCASectionIds.DDSO]: {
                        icon: <></>,
                        title: "Overview",
                        tabKey: CA_DD_MODULES.OVERVIEW,
                        moduleType: "default",
                        sectionId: RevstackCASectionIds.DDSO,
                        widget: RevstackPoCategoryAnalysisWidget,
                        config: {
                            widgetCard: {
                                type: "drilldown",
                                isBodyPaddingReq: true,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: !isAllSubcatSelected,
                            module: RevstackDashboardModules.NAVIGATOR,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "subcategory",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: true,
                            },
                            trendsFiltersConfig,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd-po-ms",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Market Share",
                                    subTitle: titleCase(metricType),
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.MARKET_SHARE
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-ov-sov",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Overall SOV",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.OVERALL_SOV
                                        ].description,
                                    measures: [CommonWidgetMetrics.OVERALL_SOV],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Wt. Availability%",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics
                                                .WEIGHTED_AVAILABILITY
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                            [
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Traffic",
                                    icon: <FiEye size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Overall SOV",
                                                value: CommonWidgetMetrics.OVERALL_SOV,
                                                excludeAsRow: true,
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                value: CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                value: CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                value: CommonWidgetMetrics.BRANDED_SEARCH,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Conversion",
                                    icon: <BsBasket2 size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "RPI",
                                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                label: "Rating",
                                                value: CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                value: CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Operations",
                                    icon: <FiTruck size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Wt. Availability",
                                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                excludeAsRow: true,
                                            },
                                            {
                                                label: "Stock in Hand",
                                                value: CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "Day(s)",
                                            },
                                            {
                                                label: "Fill Rate",
                                                value: CommonWidgetMetrics.FILL_RATE,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                value: CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSBTA]: {
                        icon: <></>,
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSBTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "subcategory",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: [
                                        "brand",
                                        "brand_id",
                                        "channel_id",
                                    ],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_bd"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "brand",
                                                        groupDataId: "brand_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "brand_dd_subcat_summary",
                                        recordKey: "brand_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "brand",
                                                "brand_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `brand_dd_subcat_summary_consolidated`,
                                            isExcludeDimension: true,
                                        },
                                    },
                                    infoMessage: "",
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSSTA]: {
                        icon: <></>,
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSSTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "subcategory",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sku"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "cross_channel_sku_id",
                                                        groupDataId:
                                                            "cross_channel_sku_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                        },
                                    },
                                    queryDimensions: [
                                        "cross_channel_sku_id",
                                        "channel_id",
                                        "category",
                                        "brand",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "sku_dd_subcat_summary",
                                        recordKey:
                                            "cross_channel_sku_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "cross_channel_sku_id",
                                                "brand_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `sku_dd_subcat_summary_consolidated`,
                                            isExcludeDimension: true,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "subcategory",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_cr"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "region",
                                                        groupDataId: "region",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["region", "channel_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: "region_dd_subcat_summary",
                                        recordKey: "region##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: ["region"],
                                            consolidatedColumnValue: 0,
                                            resultKey: `region_dd_subcat_summary_consolidated`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.PA]: {
                    [RevstackCASectionIds.BANNER]: {
                        icon: <></>,
                        title: "Product Analytics",
                        sectionId: RevstackCASectionIds.BANNER,
                        widget: RevstackGenericParentContainer,
                        config: {
                            layout: {
                                type: "container",
                                rowSpan: 1,
                                colSpan: 24,
                                className: "mb-3",
                            },
                            isTrendsButtonRequired: false,
                            module: RevstackDashboardModules.NAVIGATOR,
                            trendsFiltersConfig,
                        },
                        widgets: [
                            {
                                identifier: "po-ms",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Market Share",
                                subTitle: titleCase(metricType),
                                relativeTabKey: CA_DD_MODULES.OVERVIEW,
                                type: WidgetType.W0,
                                config: {
                                    currencySymbol: currencySymbol,
                                    banner: {
                                        formatterParams: {
                                            isCrossChannelComponent: true,
                                            metricsToAvg: [
                                                `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                                `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                                `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                            ],
                                            metricsToSum: ["reviews_count"],
                                        },
                                    },
                                },
                                queryDimensions: [
                                    "cross_channel_sku_id",
                                    "channel_id",
                                    "category",
                                    "brand",
                                    "sku",
                                    "sku_title",
                                    "product_url",
                                    "thumbnail",
                                ],
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.AVERAGE_MRP,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                isExcludeDimension: true,
                                queryMetaInfo: {
                                    resultKey: `sku_banner_data`,
                                    recordKey:
                                        "cross_channel_sku_id##channel_id",
                                },
                            },
                        ],
                    },
                    [RevstackCASectionIds.DDSO]: {
                        icon: <></>,
                        title: "Overview",
                        tabKey: CA_DD_MODULES.OVERVIEW,
                        moduleType: "default",
                        sectionId: RevstackCASectionIds.DDSO,
                        widget: RevstackPoCategoryAnalysisWidget,
                        config: {
                            widgetCard: {
                                type: "drilldown",
                                isBodyPaddingReq: true,
                                isCardRequired: false,
                            },
                            isHeaderRequired: !isAllSubcatSelected,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                props: {
                                                    value: "",
                                                    additionalProps: {
                                                        className: "",
                                                        placeholder: "",
                                                        styles: {},
                                                        subTitle: "" as any,
                                                    },
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: true,
                            },
                            trendsFiltersConfig,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd-po-ms",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Market Share",
                                    subTitle: titleCase(metricType),
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.MARKET_SHARE
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    ],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                {
                                    identifier: "dd-po-ov-sov",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Overall SOV",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.OVERALL_SOV
                                        ].description,
                                    measures: [CommonWidgetMetrics.OVERALL_SOV],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Wt. Availability%",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics
                                                .WEIGHTED_AVAILABILITY
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    ],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            ],
                            [
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Traffic",
                                    icon: <FiEye size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Overall SOV",
                                                value: CommonWidgetMetrics.OVERALL_SOV,
                                                excludeAsRow: true,
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                value: CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                value: CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                value: CommonWidgetMetrics.BRANDED_SEARCH,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Conversion",
                                    icon: <BsBasket2 size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "RPI",
                                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                label: "Rating",
                                                value: CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                value: CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Operations",
                                    icon: <FiTruck size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "Channel Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Wt. Availability",
                                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                excludeAsRow: true,
                                            },
                                            {
                                                label: "Stock in Hand",
                                                value: CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "Day(s)",
                                            },
                                            {
                                                label: "Fill Rate",
                                                value: CommonWidgetMetrics.FILL_RATE,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                value: CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "channel_id",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                    ],
                                    queryDimensions: ["channel_id"],
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSSTA]: {
                        icon: <></>,
                        title: "Competitor Analysis",
                        tabKey: CA_DD_MODULES.COMPETITOR_ANALYSIS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSSTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                props: {
                                                    value: "",
                                                    additionalProps: {
                                                        className: "",
                                                        placeholder: "",
                                                        styles: {},
                                                        subTitle: "" as any,
                                                    },
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sku"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                isDrilldownTable: true,
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "cross_channel_sku_id",
                                                        groupDataId:
                                                            "cross_channel_sku_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: [
                                        "cross_channel_sku_id",
                                        "channel_id",
                                        "category",
                                        "subcategory",
                                        "category_id",
                                        "brand",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        filtersToIgnore: [
                                            "cross_channel_sku_id",
                                        ],
                                        resultKey: "sku_dd_table_data",
                                        recordKey:
                                            "cross_channel_sku_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "cross_channel_sku_id",
                                                "brand_id",
                                                "subcategory_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `sku_dd_table_data_consolidated`,
                                            isExcludeDimension: true,
                                        },
                                    },
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                props: {
                                                    value: "",
                                                    additionalProps: {
                                                        className: "",
                                                        placeholder: "",
                                                        styles: {},
                                                        subTitle: "" as any,
                                                    },
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_cr",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_cr"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "region",
                                                        groupDataId: "region",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["region", "channel_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    ],
                                    queryMetaInfo: {
                                        resultKey: "sku_dd_region_table_data",
                                        recordKey: "region##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: ["region"],
                                            consolidatedColumnValue: 0,
                                            resultKey: `sku_dd_region_table_data_consolidated`,
                                        },
                                    },
                                    excludeFilter: [
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.RA]: {
                    [RevstackCASectionIds.DDSO]: {
                        icon: <></>,
                        title: "Overview",
                        tabKey: CA_DD_MODULES.OVERVIEW,
                        moduleType: "default",
                        sectionId: RevstackCASectionIds.DDSO,
                        widget: RevstackPoCategoryAnalysisWidget,
                        config: {
                            widgetCard: {
                                type: "drilldown",
                                isBodyPaddingReq: true,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: !isAllSubcatSelected,
                            module: RevstackDashboardModules.NAVIGATOR,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: true,
                            },
                            trendsFiltersConfig,
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd-po-ms",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Market Share",
                                    subTitle: titleCase(metricType),
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.MARKET_SHARE
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-ov-sov",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Overall SOV",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics.OVERALL_SOV
                                        ].description,
                                    measures: [CommonWidgetMetrics.OVERALL_SOV],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "Wt. Availability%",
                                    type: WidgetType.W1,
                                    infoMessage:
                                        REVSTACK_MEASURES[
                                            CommonWidgetMetrics
                                                .WEIGHTED_AVAILABILITY
                                        ].description,
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    ],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                            [
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Traffic",
                                    icon: <FiEye size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "City Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Overall SOV",
                                                value: CommonWidgetMetrics.OVERALL_SOV,
                                                excludeAsRow: true,
                                                unit: "%",
                                            },
                                            {
                                                label: "Organic SOV",
                                                value: CommonWidgetMetrics.ORGANIC_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Paid SOV",
                                                value: CommonWidgetMetrics.PAID_SOV,
                                                unit: "%",
                                            },
                                            {
                                                label: "Branded Search",
                                                value: CommonWidgetMetrics.BRANDED_SEARCH,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "city",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                    queryDimensions: ["city"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Conversion",
                                    icon: <BsBasket2 size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "City Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "RPI",
                                                value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                                unit: "",
                                            },
                                            {
                                                label: "Rating",
                                                value: CommonWidgetMetrics.AVERAGE_RATING,
                                                unit: "",
                                            },
                                            {
                                                label: "Content Score",
                                                value: CommonWidgetMetrics.CONTENT_SCORE,
                                                unit: "",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "city",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                    ],
                                    queryDimensions: ["city"],
                                    excludeFilter: ["brand_id"],
                                },
                                {
                                    identifier: "dd-po-wa",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    type: WidgetType.W2,
                                    title: "Operations",
                                    icon: <FiTruck size={16} />,
                                    config: {
                                        drilldown: {
                                            title: "City Performance",
                                            dimensions: [
                                                WidgetDimensions.VALUE,
                                                WidgetDimensions.CHANGE,
                                                WidgetDimensions.NAME,
                                            ],
                                            limit: 3,
                                            hasFilter: true,
                                        },
                                        filterOptions: [
                                            {
                                                label: "Wt. Availability",
                                                value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                                excludeAsRow: true,
                                            },
                                            {
                                                label: "Stock in Hand",
                                                value: CommonWidgetMetrics.STOCK_IN_HAND,
                                                unit: "Day(s)",
                                            },
                                            {
                                                label: "Fill Rate",
                                                value: CommonWidgetMetrics.FILL_RATE,
                                                unit: "%",
                                            },
                                            {
                                                label: "Listing Status",
                                                value: CommonWidgetMetrics.LISTING_STATUS,
                                                unit: "%",
                                            },
                                        ],
                                        defaultFilterOption: {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            unit: "%",
                                        },
                                        drilldownEntryKey: "city",
                                    },
                                    measures: [
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                    ],
                                    queryDimensions: ["city"],
                                    excludeFilter: ["brand_id"],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCTA]: {
                        icon: <></>,
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: [
                                        "subcategory",
                                        "subcategory_id",
                                        "category",
                                        "channel_id",
                                    ],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sc"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "subcategory",
                                                        groupDataId:
                                                            "subcategory_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryMetaInfo: {
                                        resultKey: "subcat_dd_region_summary",
                                        recordKey: "subcategory_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "subcategory",
                                                "subcategory_id",
                                                "category",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `subcat_dd_region_summary_consolidated`,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSBTA]: {
                        icon: <></>,
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSBTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_bd",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    queryDimensions: [
                                        "brand",
                                        "brand_id",
                                        "channel_id",
                                    ],
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_bd"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey: "brand",
                                                        groupDataId: "brand_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "brand_dd_region_summary",
                                        recordKey: "brand_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "brand",
                                                "brand_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `brand_dd_region_summary_consolidated`,
                                            isExcludeDimension: true,
                                        },
                                    },
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.AVERAGE_PRICE,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSSTA]: {
                        icon: <></>,
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSSTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_sku",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_sku"],
                                            isGroupedTable: true,
                                            isSearchReq: true,
                                            isDrilldownTable: true,
                                            isCompetitionButtonReq: true,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            formatterParams: {
                                                groupDataParams: [
                                                    {
                                                        groupDataKey:
                                                            "cross_channel_sku_id",
                                                        groupDataId:
                                                            "cross_channel_sku_id",
                                                    },
                                                ],
                                                consolidationDataKey:
                                                    "channels",
                                                consolidationDataId:
                                                    "channel_id",
                                                consolidationAggregateDataKey:
                                                    "aggregates",
                                                consolidateDataIdentifier: 0,
                                            },
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: [
                                        "cross_channel_sku_id",
                                        "channel_id",
                                        "category",
                                        "brand",
                                        "sku_title",
                                        "product_url",
                                        "thumbnail",
                                    ],
                                    queryMetaInfo: {
                                        filterToSkip: "brand_id",
                                        resultKey: "sku_dd_region_summary",
                                        recordKey:
                                            "cross_channel_sku_id##channel_id",
                                        consolidatedQueryConfig: {
                                            queryDimensions: [
                                                "cross_channel_sku_id",
                                                "brand_id",
                                            ],
                                            consolidatedColumnValue: 0,
                                            resultKey: `sku_dd_region_summary_consolidated`,
                                            isExcludeDimension: true,
                                        },
                                    },
                                    excludeFilter: ["brand_id"],
                                    isExcludeDimension: true,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                },
                            ],
                        ],
                    },
                    [RevstackCASectionIds.DDSCCTA]: {
                        icon: <></>,
                        title: "Cross Channels",
                        tabKey: CA_DD_MODULES.CROSS_CHANNEL,
                        moduleType: "table",
                        sectionId: RevstackCASectionIds.DDSCCTA,
                        widget: RevstackTablePerformanceWidget,
                        config: {
                            widgetCard: {
                                isBodyPaddingReq: false,
                                isCardRequired: false,
                            },
                            isTableOnlyMode: true,
                            isHeaderRequired: false,
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                props: {
                                                    value: "",
                                                    additionalProps: {
                                                        className: "",
                                                        placeholder: "",
                                                        styles: {},
                                                        subTitle: "" as any,
                                                    },
                                                },
                                            },
                                            // {
                                            //     placement: "suffix",
                                            //     type: "detailed-button",
                                            //     props: {
                                            //         name: "Detailed View",
                                            //         onClick:()=>{},
                                            //         className: "",
                                            //         styles: {},
                                            //     }

                                            // }
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                isTrendsViewRequired: false,
                            },
                        },
                        widgets: [
                            [
                                {
                                    identifier: "dd_mi_ov_ps_cc",
                                    cubeId: RevstackCubeIds.PO_REGIONAL,
                                    title: "",
                                    subTitle: "",
                                    type: WidgetType.W7,
                                    config: {
                                        table: {
                                            colDefs:
                                                getRevstackDDTableColDefs(
                                                    filterToAppend
                                                )["dd_mi_ov_ps_cc"],
                                            isGroupedTable: true,
                                            isDrilldownTable: true,
                                            isSearchReq: true,
                                            isCompetitionButtonReq: false,
                                            isLayoutButtonReq: true,
                                            colVisibilityMode: "group",
                                            isDownloadButtonReq: true,
                                            style: {
                                                height: "51vh",
                                                expandedHeight: "51vh",
                                            },
                                        },
                                    },
                                    queryDimensions: ["channel_id"],
                                    infoMessage: "",
                                    isMeasureAppendRequired: false,
                                    measures: [
                                        CommonWidgetMetrics.MARKET_SHARE,
                                        CommonWidgetMetrics.OVERALL_SOV,
                                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        CommonWidgetMetrics.ORGANIC_SOV,
                                        CommonWidgetMetrics.PAID_SOV,
                                        CommonWidgetMetrics.BRANDED_SEARCH,
                                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        CommonWidgetMetrics.AVERAGE_RATING,
                                        CommonWidgetMetrics.CONTENT_SCORE,
                                        CommonWidgetMetrics.STOCK_IN_HAND,
                                        CommonWidgetMetrics.FILL_RATE,
                                        CommonWidgetMetrics.LISTING_STATUS,
                                    ],
                                    excludeFilter: [
                                        "brand_id",
                                        "cross_channel_sku_id",
                                    ],
                                    queryMetaInfo: {
                                        resultKey: "channel_table_data",
                                        recordKey: "channel_id",
                                        filterToSkip: "channel_id",
                                    },
                                },
                            ],
                        ],
                    },
                },
            };
        }
    }

    if (module === RevstackDashboardModules.PRODUCT_ANALYSIS) {
        const productAnaylsisDrillDown = {
            [RevstackCASectionIds.RWP]: {
                [RevstackCASectionIds.BANNER]: {
                    icon: <></>,
                    title: "Product Analytics",
                    sectionId: RevstackCASectionIds.BANNER,
                    widget: RevstackGenericParentContainer,
                    config: {
                        layout: {
                            type: "container",
                            rowSpan: 1,
                            colSpan: 24,
                            className: "mb-3",
                        },
                        isTrendsButtonRequired: false,
                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                        trendsFiltersConfig,
                    },
                    widgets: [
                        {
                            identifier: "po-ms",
                            cubeId: RevstackCubeIds.PO_REGIONAL,
                            title: "Market Share",
                            subTitle: titleCase(metricType),
                            relativeTabKey: CA_DD_MODULES.OVERVIEW,
                            type: WidgetType.W0,
                            config: {
                                currencySymbol: currencySymbol,
                                banner: {
                                    formatterParams: {
                                        isCrossChannelComponent: true,
                                        metricsToAvg: [
                                            `${CommonWidgetMetrics.AVERAGE_RATING}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_PRICE}_value`,
                                            `${CommonWidgetMetrics.AVERAGE_MRP}_value`,
                                        ],
                                        metricsToSum: ["reviews_count"],
                                    },
                                },
                            },
                            queryDimensions: [
                                "cross_channel_sku_id",
                                "channel_id",
                                "category",
                                "brand",
                                "sku",
                                "sku_title",
                                "product_url",
                                "thumbnail",
                            ],
                            measures: [
                                CommonWidgetMetrics.MARKET_SHARE,
                                CommonWidgetMetrics.AVERAGE_PRICE,
                                CommonWidgetMetrics.AVERAGE_MRP,
                                CommonWidgetMetrics.AVERAGE_RATING,
                            ],
                            excludeFilter: [
                                CommonWidgetMetrics.BRAND_ID,
                                CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                            ],
                            isExcludeDimension: true,
                            queryMetaInfo: {
                                resultKey: `sku_banner_data`,
                                recordKey: "cross_channel_sku_id##channel_id",
                            },
                        },
                    ],
                },
                [RevstackCASectionIds.DDPOA]: {
                    icon: <></>,
                    title: "Overview",
                    tabKey: CA_DD_MODULES.OVERVIEW,
                    moduleType: "default",
                    sectionId: RevstackCASectionIds.DDPOA,
                    widget: RevstackPoCategoryAnalysisWidget,
                    config: {
                        widgetCard: {
                            type: "drilldown",
                            isBodyPaddingReq: true,
                            isCardRequired: false,
                        },
                        isHeaderRequired: !isAllSubcatSelected,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            props: {
                                                value: "",
                                                additionalProps: {
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                        },
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            type: RevstackFilterTypes.SELECT,
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            props: {
                                                onClick: () => {},
                                                onChange: () => {},
                                                onOpenChange: () => {},
                                                options: [] as any[],
                                                defaultValue: [] as any[],
                                                value: "",
                                                mode: "",
                                                styles: {},
                                                className: "",
                                                placeholder: "",
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                        tabs: {
                            isTrendsViewRequired: false,
                        },
                        trendsFiltersConfig,
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd-po-ms",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Market Share",
                                subTitle: titleCase(metricType),
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.MARKET_SHARE
                                    ].description,
                                measures: [CommonWidgetMetrics.MARKET_SHARE],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "dd-po-ov-sov",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Overall SOV",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics.OVERALL_SOV
                                    ].description,
                                measures: [CommonWidgetMetrics.OVERALL_SOV],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "dd-po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "Availability",
                                type: WidgetType.W1,
                                infoMessage:
                                    REVSTACK_MEASURES[
                                        CommonWidgetMetrics
                                            .WEIGHTED_AVAILABILITY
                                    ].description,
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                        [
                            {
                                identifier: "dd-po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Traffic",
                                icon: <FiEye size={16} />,
                                config: {
                                    drilldown: {
                                        title: "City Performance",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        hasFilter: true,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Overall SOV",
                                            value: CommonWidgetMetrics.OVERALL_SOV,
                                            excludeAsRow: true,
                                            unit: "%",
                                        },
                                        {
                                            label: "Organic SOV",
                                            value: CommonWidgetMetrics.ORGANIC_SOV,
                                            unit: "%",
                                        },
                                        {
                                            label: "Paid SOV",
                                            value: CommonWidgetMetrics.PAID_SOV,
                                            unit: "%",
                                        },
                                        {
                                            label: "Branded Search",
                                            value: CommonWidgetMetrics.BRANDED_SEARCH,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Overall SOV",
                                        value: CommonWidgetMetrics.OVERALL_SOV,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "city",
                                },
                                measures: [
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                ],
                                queryDimensions: ["city"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "dd-po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Conversion",
                                icon: <BsBasket2 size={16} />,
                                config: {
                                    drilldown: {
                                        title: "City Performance",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        hasFilter: true,
                                    },
                                    filterOptions: [
                                        {
                                            label: "RPI",
                                            value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                            unit: "",
                                        },
                                        {
                                            label: "Rating",
                                            value: CommonWidgetMetrics.AVERAGE_RATING,
                                            unit: "",
                                        },
                                        {
                                            label: "Content Score",
                                            value: CommonWidgetMetrics.CONTENT_SCORE,
                                            unit: "",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "RPI",
                                        value: CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "city",
                                },
                                measures: [
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                ],
                                queryDimensions: ["city"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                            {
                                identifier: "dd-po-wa",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                type: WidgetType.W2,
                                title: "Operations",
                                icon: <FiTruck size={16} />,
                                config: {
                                    drilldown: {
                                        title: "City Performance",
                                        dimensions: [
                                            WidgetDimensions.VALUE,
                                            WidgetDimensions.CHANGE,
                                            WidgetDimensions.NAME,
                                        ],
                                        limit: 3,
                                        hasFilter: true,
                                    },
                                    filterOptions: [
                                        {
                                            label: "Wt. Availability",
                                            value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                            excludeAsRow: true,
                                        },
                                        {
                                            label: "Stock in Hand",
                                            value: CommonWidgetMetrics.STOCK_IN_HAND,
                                            unit: "Day(s)",
                                        },
                                        {
                                            label: "Fill Rate",
                                            value: CommonWidgetMetrics.FILL_RATE,
                                            unit: "%",
                                        },
                                        {
                                            label: "Listing Status",
                                            value: CommonWidgetMetrics.LISTING_STATUS,
                                            unit: "%",
                                        },
                                    ],
                                    defaultFilterOption: {
                                        label: "Wt. Availability",
                                        value: CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                        unit: "%",
                                    },
                                    drilldownEntryKey: "city",
                                },
                                measures: [
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                                queryDimensions: ["city"],
                                excludeFilter: [
                                    CommonWidgetMetrics.BRAND_ID,
                                    CommonWidgetMetrics.SKU,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                },
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.DDPCTA]: {
                    icon: <></>,
                    title: "Competitor Analysis",
                    tabKey: CA_DD_MODULES.COMPETITOR_ANALYSIS,
                    moduleType: "table",
                    sectionId: RevstackCASectionIds.DDPCTA,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        widgetCard: {
                            isBodyPaddingReq: false,
                            isCardRequired: false,
                        },
                        isTableOnlyMode: true,
                        isHeaderRequired: false,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            props: {
                                                value: "",
                                                additionalProps: {
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                        },
                                        // {
                                        //     placement: "suffix",
                                        //     type: "detailed-button",
                                        //     props: {
                                        //         name: "Detailed View",
                                        //         onClick:()=>{},
                                        //         className: "",
                                        //         styles: {},
                                        //     }

                                        // }
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            type: RevstackFilterTypes.SELECT,
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            props: {
                                                onClick: () => {},
                                                onChange: () => {},
                                                onOpenChange: () => {},
                                                options: [] as any[],
                                                defaultValue: [] as any[],
                                                value: "",
                                                mode: "",
                                                styles: {},
                                                className: "",
                                                placeholder: "",
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                        tabs: {
                            isTrendsViewRequired: false,
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd_po_cta",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        colDefs:
                                            getRevstackDDTableColDefs(
                                                filterToAppend
                                            )["dd_po_cta"],
                                        isGroupedTable: true,
                                        isSearchReq: true,
                                        isDrilldownTable: true,
                                        isCompetitionButtonReq: false,
                                        isLayoutButtonReq: true,
                                        isDownloadButtonReq: true,
                                        style: {
                                            height: "51vh",
                                            expandedHeight: "51vh",
                                        },
                                    },
                                },
                                queryDimensions: [
                                    CommonWidgetMetrics.CATEGORY,
                                    CommonWidgetMetrics.SUB_CATEGORY,
                                    CommonWidgetMetrics.CATEGORY_ID,
                                    CommonWidgetMetrics.BRAND,
                                    CommonWidgetMetrics.BRAND,
                                    CommonWidgetMetrics.SKU_TITLE,
                                    CommonWidgetMetrics.SKU,
                                    CommonWidgetMetrics.PRODUCT_URL,
                                    CommonWidgetMetrics.THUMBNAIL,
                                ],
                                queryMetaInfo: {
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.SKU,
                                        CommonWidgetMetrics.BRAND_ID,
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                    ],
                                    resultKey: "pa_sku_dd_table_data",
                                    recordKey: "sku",
                                },
                                isExcludeDimension: true,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.DDPOCA]: {
                    icon: <></>,
                    title: "Cross Region",
                    tabKey: CA_DD_MODULES.CROSS_REGION,
                    moduleType: "table",
                    sectionId: RevstackCASectionIds.DDPOCA,
                    widget: RevstackTablePerformanceWidget,
                    config: {
                        widgetCard: {
                            isBodyPaddingReq: false,
                            isCardRequired: false,
                        },
                        isTableOnlyMode: true,
                        isHeaderRequired: false,
                        header: {
                            components: [
                                {
                                    section: "main",
                                    elements: [
                                        {
                                            type: "text",
                                            props: {
                                                value: "",
                                                additionalProps: {
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                },
                                            },
                                        },
                                        // {
                                        //     placement: "suffix",
                                        //     type: "detailed-button",
                                        //     props: {
                                        //         name: "Detailed View",
                                        //         onClick:()=>{},
                                        //         className: "",
                                        //         styles: {},
                                        //     }

                                        // }
                                    ],
                                },
                                {
                                    section: "filters",
                                    elements: [
                                        {
                                            type: RevstackFilterTypes.SELECT,
                                            filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                            props: {
                                                onClick: () => {},
                                                onChange: () => {},
                                                onOpenChange: () => {},
                                                options: [] as any[],
                                                defaultValue: [] as any[],
                                                value: "",
                                                mode: "",
                                                styles: {},
                                                className: "",
                                                placeholder: "",
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                        tabs: {
                            isTrendsViewRequired: false,
                        },
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd_po_cra",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W7,
                                infoMessage: "",
                                isMeasureAppendRequired: false,
                                config: {
                                    table: {
                                        colDefs:
                                            getRevstackDDTableColDefs(
                                                filterToAppend
                                            )["dd_po_cra"],
                                        isGroupedTable: true,
                                        isSearchReq: true,
                                        isDrilldownTable: true,
                                        isCompetitionButtonReq: false,
                                        isLayoutButtonReq: true,
                                        isDownloadButtonReq: true,
                                        style: {
                                            height: "51vh",
                                            expandedHeight: "51vh",
                                        },
                                    },
                                },
                                queryDimensions: ["region"],
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                                queryMetaInfo: {
                                    resultKey: "pa_sku_dd_region_table_data",
                                    recordKey: "region",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                        CommonWidgetMetrics.REGION,
                                        CommonWidgetMetrics.BRAND_ID,
                                    ],
                                },
                                excludeFilter: [CommonWidgetMetrics.SKU],
                            },
                        ],
                    ],
                },
                [RevstackCASectionIds.DDPOCCA]: {
                    ...sections[RevstackCASectionIds.DDSCCTA],
                    sectionId: RevstackCASectionIds.DDPOCCA,
                    config: {
                        ...sections[RevstackCASectionIds.DDSCCTA].config,
                    },
                    widgets: [
                        [
                            {
                                identifier: "dd_po_cca",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W7,
                                config: {
                                    table: {
                                        colDefs:
                                            getRevstackDDTableColDefs(
                                                filterToAppend
                                            )["dd_po_cca"],
                                        isGroupedTable: false,
                                        isSearchReq: true,
                                        isDrilldownTable: true,
                                        isCompetitionButtonReq: false,
                                        isLayoutButtonReq: true,
                                        isDownloadButtonReq: true,
                                        style: {
                                            height: "51vh",
                                            expandedHeight: "51vh",
                                        },
                                    },
                                },
                                queryDimensions: ["channel_id"],
                                infoMessage: "",
                                isMeasureAppendRequired: false,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                    CommonWidgetMetrics.BRANDED_SEARCH,
                                    CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.TOTAL_ORGANIC_IMPRESSIONS,
                                    CommonWidgetMetrics.AVERAGE_PRICE,
                                    CommonWidgetMetrics.AVERAGE_RATING,
                                    CommonWidgetMetrics.CONTENT_SCORE,
                                    CommonWidgetMetrics.LISTING_STATUS,
                                    CommonWidgetMetrics.STOCK_IN_HAND,
                                    CommonWidgetMetrics.FILL_RATE,
                                ],
                                excludeFilter: [
                                    CommonWidgetMetrics.CROSS_CHANNEL_SKU_ID,
                                ],
                                queryMetaInfo: {
                                    resultKey: "pa_sku_dd_channel_table_data",
                                    recordKey: "channel_id",
                                    filtersToIgnore: [
                                        CommonWidgetMetrics.SKU,
                                        CommonWidgetMetrics.CHANNEL_ID,
                                        CommonWidgetMetrics.BRAND_ID,
                                    ],
                                },
                            },
                        ],
                    ],
                },
            },
        };
        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                ...productAnaylsisDrillDown,
            };
        }

        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                ...productAnaylsisDrillDown,
            };
        }

        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                ...productAnaylsisDrillDown,
            };
        }

        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                ...productAnaylsisDrillDown,
            };
        }
    }

    return drilldownConfigSections;
};

const createBaseDdTrendsModalConfig = (
    filterToAppend: string,
    metricType: string,
    isAllSubcatSelected: boolean,
    module: RevstackDashboardModules,
    subModule = RevstackDashboardSubModules.OVERVIEW
) => {
    const sections = {
        [RevstackCASectionIds.DDTSOA]: {
            icon: <></>,
            title: "Overview",
            tabKey: CA_DD_MODULES.OVERVIEW,
            moduleType: "default",
            sectionId: RevstackCASectionIds.DDTSOA,
            widget: RevstackGenericChartParentContainer,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasFilterDropDown: true,
                    hasChartPillsFilters: true,
                },
                // for the chart dropDown options
                chartPillFiltersDatakeys: {
                    title: "subcategory",
                    subTitle: "category",
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "subcategory",
                },
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: "text",
                                    id: `${RevstackCASectionIds.DDTSOA}-title`,
                                },
                            ],
                        },
                    ],
                },
                filters: [
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: titleCase(
                            CommonWidgetMetrics.MARKET_SHARE
                        ),
                        dataKey: "yAxisFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: capitalizeSpecificSubstring(
                            titleCase(CommonWidgetMetrics.OVERALL_SOV),
                            "sov",
                            true
                        ),
                        dataKey: "yAxisOppositeFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                ],
                isRemovablePillsRequired: true,
                addPillsBtnTitle: "Add Subcategories",
                maxPillsFiltersAllowed: 5,
                capitalizePillNames: false,
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                formatForChannelId: true,
                disableYaxisOppositeOnPillCount: 1,
                sortByFiltersData: true,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                dropdownFilterPlaceHolder: "Search Subcategories",
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "cat_subcat_dropdown",
                    queryDimensions: ["category", "subcategory"],
                    excludeFilter: ["brand_id"],
                },
                queryMetaInfo: {
                    resultKey: "category_trends_modal_overview_data",
                    primaryDimension: "subcategory",
                },
            },
            widgets: [
                {
                    identifier: "dd-o-t",
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    title: "",
                    subTitle: "",
                    type: WidgetType.W3,
                    queryDimensions: ["subcategory"],
                    infoMessage: "",
                    isMeasureAppendRequired: false,
                    measures: [
                        CommonWidgetMetrics.MARKET_SHARE,
                        CommonWidgetMetrics.OVERALL_SOV,
                        CommonWidgetMetrics.ORGANIC_SOV,
                        CommonWidgetMetrics.PAID_SOV,
                        CommonWidgetMetrics.AVERAGE_PRICE,
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                        CommonWidgetMetrics.CONTENT_SCORE,
                        CommonWidgetMetrics.STOCK_IN_HAND,
                        CommonWidgetMetrics.LISTING_STATUS,
                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                        // CommonWidgetMetrics.BRANDED_SEARCH,
                    ],
                    queryMetaInfo: {
                        resultKey: "category_trends_modal_overview_data",
                        primaryDimension: "subcategory",
                    },
                    excludeFilter: ["brand_id"],
                    plotFields: [
                        {
                            field: `market_share${filterToAppend}`,
                            type: "measure",
                        },
                        {
                            field: `overall_sov`,
                            type: "measure",
                        },
                    ],
                    relativePeriodConfig: {
                        granularity: "day",
                        value: 30,
                    },
                    config: {
                        metricKey: "name",
                        filterYaxisAttribute: "name",
                        filterYaxisOppositeAttribute: "name",
                        ...trendsChartConfig(filterToAppend),
                    },
                },
            ],
        },
        [RevstackCASectionIds.DDTSBA]: {
            icon: <></>,
            title: "Brand Analysis",
            tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
            moduleType: "default",
            sectionId: RevstackCASectionIds.DDTSBA,
            widget: RevstackGenericChartParentContainer,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasFilterDropDown: true,
                    hasChartPillsFilters: true,
                },
                chartPillFiltersDatakeys: {
                    title: "brand",
                    subTitle: "subcategory",
                    metricDatakey: "name",
                    filterDatakey: titleCase(CommonWidgetMetrics.MARKET_SHARE),
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "brand",
                },
                filters: [
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: titleCase(
                            CommonWidgetMetrics.MARKET_SHARE
                        ),
                        dataKey: "yAxisFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: capitalizeSpecificSubstring(
                            titleCase(CommonWidgetMetrics.OVERALL_SOV),
                            "sov",
                            true
                        ),
                        dataKey: "yAxisOppositeFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                ],
                sortByFiltersData: true,
                isRemovablePillsRequired: true,
                addPillsBtnTitle: "Add Brands",
                maxPillsFiltersAllowed: 5,
                capitalizePillNames: false,
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                disableYaxisOppositeOnPillCount: 1,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                dropdownFilterPlaceHolder: "Search Brands",
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "brand_subcat_dropdown",
                    queryDimensions: ["brand", "brand_id"],
                    isExcludeDimension: true,
                    filterToSkip: "brand_id",
                },
                queryMetaInfo: {
                    primaryDimension: "brand",
                    resultKey: "category_trends_modal_brands_data",
                },
            },
            widgets: [
                {
                    identifier: "dd-b-t",
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    title: "",
                    subTitle: "",
                    type: WidgetType.W3,
                    queryDimensions: ["brand", "brand_id"],
                    infoMessage: "",
                    isMeasureAppendRequired: false,
                    measures: [
                        CommonWidgetMetrics.MARKET_SHARE,
                        CommonWidgetMetrics.OVERALL_SOV,
                        CommonWidgetMetrics.ORGANIC_SOV,
                        CommonWidgetMetrics.PAID_SOV,
                        CommonWidgetMetrics.AVERAGE_PRICE,
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                        CommonWidgetMetrics.CONTENT_SCORE,
                        CommonWidgetMetrics.STOCK_IN_HAND,
                        CommonWidgetMetrics.LISTING_STATUS,
                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                    ],
                    queryMetaInfo: {
                        filterToSkip: "brand_id",
                        primaryDimension: "brand",
                        resultKey: "category_trends_modal_brands_data",
                        avoidSkippedFilterGrouping: true,
                    },
                    plotFields: [
                        {
                            field: `market_share${filterToAppend}`,
                            type: "measure",
                        },
                        {
                            field: `overall_sov`,
                            type: "measure",
                        },
                    ],
                    relativePeriodConfig: {
                        granularity: "day",
                        value: 30,
                    },
                    isExcludeDimension: true,
                    config: {
                        metricKey: "name",
                        filterYaxisAttribute: "name",
                        filterYaxisOppositeAttribute: "name",
                        ...trendsChartConfig(filterToAppend),
                    },
                },
            ],
        },
        [RevstackCASectionIds.DDTSSA]: {
            icon: <></>,
            title: "SKU Analysis",
            tabKey: CA_DD_MODULES.SKU_ANALYTICS,
            moduleType: "default",
            sectionId: RevstackCASectionIds.DDTSSA,
            widget: RevstackGenericChartParentContainer,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasFilterDropDown: true,
                    hasChartPillsFilters: true,
                },
                chartPillFiltersDatakeys: {
                    title: "sku_title",
                    subTitle: "brand",
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "sku_title",
                },
                filters: [
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: titleCase(
                            CommonWidgetMetrics.MARKET_SHARE
                        ),
                        dataKey: "yAxisFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: capitalizeSpecificSubstring(
                            titleCase(CommonWidgetMetrics.OVERALL_SOV),
                            "sov",
                            true
                        ),
                        dataKey: "yAxisOppositeFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                ],
                sortByFiltersData: true,
                isRemovablePillsRequired: true,
                capitalizePillNames: false,
                addPillsBtnTitle: "Add Skus",
                maxPillsFiltersAllowed: 5,
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                disableYaxisOppositeOnPillCount: 1,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                dropdownFilterPlaceHolder: "Search Skus",
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "sku_subcat_dropdown",
                    queryDimensions: ["sku_title"],
                    isExcludeDimension: true,
                    excludeFilter: ["brand_id"],
                    filterToSkip: "brand_id",
                },
                queryMetaInfo: {
                    resultKey: "category_trends_modal_sku_analysis_data",
                    primaryDimension: "sku_title",
                },
            },
            widgets: [
                {
                    identifier: "dd-ss-t",
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    title: "",
                    subTitle: "",
                    type: WidgetType.W3,
                    queryDimensions: ["sku_title"],
                    infoMessage: "",
                    measures: [
                        CommonWidgetMetrics.MARKET_SHARE,
                        CommonWidgetMetrics.OVERALL_SOV,
                        CommonWidgetMetrics.ORGANIC_SOV,
                        CommonWidgetMetrics.PAID_SOV,
                        CommonWidgetMetrics.AVERAGE_PRICE,
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                        CommonWidgetMetrics.CONTENT_SCORE,
                        CommonWidgetMetrics.STOCK_IN_HAND,
                        CommonWidgetMetrics.LISTING_STATUS,
                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                    ],
                    queryMetaInfo: {
                        filterToSkip: "brand_id",
                        resultKey: "category_trends_modal_sku_analysis_data",
                        primaryDimension: "sku_title",
                        avoidSkippedFilterGrouping: true,
                    },
                    plotFields: [
                        {
                            field: `market_share${filterToAppend}`,
                            type: "measure",
                        },
                        {
                            field: `overall_sov`,
                            type: "measure",
                        },
                    ],
                    isExcludeDimension: true,
                    relativePeriodConfig: {
                        granularity: "day",
                        value: 30,
                    },
                    config: {
                        metricKey: "name",
                        filterYaxisAttribute: "name",
                        filterYaxisOppositeAttribute: "name",
                        ...trendsChartConfig(filterToAppend),
                    },
                },
            ],
        },
        [RevstackCASectionIds.DDTSCRA]: {
            icon: <></>,
            title: "Cross Region",
            tabKey: CA_DD_MODULES.CROSS_REGION,
            moduleType: "default",
            sectionId: RevstackCASectionIds.DDTSCRA,
            widget: RevstackGenericChartParentContainer,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasFilterDropDown: true,
                    hasChartPillsFilters: true,
                },
                chartPillFiltersDatakeys: {
                    title: "region",
                    subTitle: "",
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "region",
                },
                filters: [
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: titleCase(
                            CommonWidgetMetrics.MARKET_SHARE
                        ),
                        dataKey: "yAxisFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: capitalizeSpecificSubstring(
                            titleCase(CommonWidgetMetrics.OVERALL_SOV),
                            "sov",
                            true
                        ),
                        dataKey: "yAxisOppositeFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                ],
                sortByFiltersData: true,
                isRemovablePillsRequired: true,
                capitalizePillNames: false,
                maxPillsFiltersAllowed: 6,
                addPillsBtnTitle: "Add Regions",
                dropdownFilterPlaceHolder: "Search Regions",
                disableYaxisOppositeOnPillCount: 1,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "region_subcat_dropdown",
                    queryDimensions: ["region"],
                    excludeFilter: ["brand_id"],
                },
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                queryMetaInfo: {
                    resultKey: "region_trends_modal_data",
                    primaryDimension: "region",
                    dimValueToTruncate: "-Region",
                },
            },
            widgets: [
                {
                    identifier: "dd-scr-t",
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    title: "",
                    subTitle: "",
                    type: WidgetType.W3,
                    queryDimensions: ["region"],
                    infoMessage: "",
                    isMeasureAppendRequired: false,
                    measures: [
                        CommonWidgetMetrics.MARKET_SHARE,
                        CommonWidgetMetrics.OVERALL_SOV,
                        CommonWidgetMetrics.ORGANIC_SOV,
                        CommonWidgetMetrics.PAID_SOV,
                        CommonWidgetMetrics.AVERAGE_PRICE,
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                        CommonWidgetMetrics.CONTENT_SCORE,
                        CommonWidgetMetrics.STOCK_IN_HAND,
                        CommonWidgetMetrics.LISTING_STATUS,
                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                    ],
                    queryMetaInfo: {
                        resultKey: "region_trends_modal_data",
                        recordKey: "region",
                        dimValueToTruncate: "-Region",
                        primaryDimension: "region",
                    },
                    excludeFilter: ["brand_id"],
                    plotFields: [
                        {
                            field: `market_share${filterToAppend}`,
                            type: "measure",
                        },
                        {
                            field: `overall_sov`,
                            type: "measure",
                        },
                    ],
                    relativePeriodConfig: {
                        granularity: "day",
                        value: 30,
                    },
                    config: {
                        metricKey: "name",
                        filterYaxisAttribute: "name",
                        filterYaxisOppositeAttribute: "name",
                        ...trendsChartConfig(filterToAppend),
                    },
                },
            ],
        },
        [RevstackCASectionIds.DDTSCCA]: {
            icon: <></>,
            title: "Cross Channels",
            tabKey: CA_DD_MODULES.CROSS_CHANNEL,
            moduleType: "default",
            sectionId: RevstackCASectionIds.DDTSCCA,
            widget: RevstackGenericChartParentContainer,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasFilterDropDown: true,
                    hasChartPillsFilters: true,
                },
                filters: [
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: titleCase(
                            CommonWidgetMetrics.MARKET_SHARE
                        ),
                        dataKey: "yAxisFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                    {
                        type: RevstackFilterTypes.SELECT,
                        defaultFilter: capitalizeSpecificSubstring(
                            titleCase(CommonWidgetMetrics.OVERALL_SOV),
                            "sov",
                            true
                        ),
                        dataKey: "yAxisOppositeFilter",
                        resultKey: "category_trends_modal_overview_data",
                        attributeKey: "name",
                        width: 150,
                    },
                ],
                chartPillFiltersDatakeys: {
                    title: "channel_id",
                    subTitle: "",
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "channel_id",
                },
                formatForChannelId: true,
                sortByFiltersData: true,
                isRemovablePillsRequired: true,
                capitalizePillNames: false,
                addPillsBtnTitle: "Add Channels",
                maxPillsFiltersAllowed: 5,
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                disableYaxisOppositeOnPillCount: 1,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                dropdownFilterPlaceHolder: "Search Channels",
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "channel_subcat_dropdown",
                    queryDimensions: ["channel_id"],
                    excludeFilter: ["brand_id"],
                    filterToSkip: "channel_id",
                    formatForChannelId: true,
                },
                queryMetaInfo: {
                    resultKey: "channel_trends_modal_data",
                    primaryDimension: "channel_id",
                },
            },
            widgets: [
                {
                    identifier: "dd-scc-t",
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    title: "",
                    subTitle: "",
                    type: WidgetType.W3,
                    queryDimensions: ["channel_id"],
                    infoMessage: "",
                    isMeasureAppendRequired: false,
                    measures: [
                        CommonWidgetMetrics.MARKET_SHARE,
                        CommonWidgetMetrics.OVERALL_SOV,
                        CommonWidgetMetrics.ORGANIC_SOV,
                        CommonWidgetMetrics.PAID_SOV,
                        CommonWidgetMetrics.AVERAGE_PRICE,
                        CommonWidgetMetrics.WEIGHTED_AVAILABILITY,
                        CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
                        CommonWidgetMetrics.CONTENT_SCORE,
                        CommonWidgetMetrics.STOCK_IN_HAND,
                        CommonWidgetMetrics.LISTING_STATUS,
                        CommonWidgetMetrics.WEIGHTED_DISCOUNT
                    ],
                    excludeFilter: ["brand_id"],
                    queryMetaInfo: {
                        resultKey: "channel_trends_modal_data",
                        recordKey: "channel_id",
                        filterToSkip: "channel_id",
                        primaryDimension: "channel_id",
                        avoidSkippedFilterGrouping: true,
                    },
                    plotFields: [
                        {
                            field: `market_share${filterToAppend}`,
                            type: "measure",
                        },
                        {
                            field: `overall_sov`,
                            type: "measure",
                        },
                    ],
                    relativePeriodConfig: {
                        granularity: "day",
                        value: 30,
                    },
                    config: {
                        metricKey: "name",
                        filterYaxisAttribute: "name",
                        filterYaxisOppositeAttribute: "name",
                        formatForChannelId: true,
                        ...trendsChartConfig(filterToAppend),
                    },
                },
            ],
        },
    };

    if (module === RevstackDashboardModules.CATEGORY_ANALYSIS) {
        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.SP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                [RevstackCASectionIds.SP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                [RevstackCASectionIds.SP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                [RevstackCASectionIds.SP]: sections,
            };
        }
    }

    if (module === RevstackDashboardModules.NAVIGATOR) {
        const navigatorMultiRowTooltipConfig = (
            nameField: string | string[] = "name"
        ) => {
            // nameField can be a single field name or an array of field names to try in order
            const nameFields = Array.isArray(nameField)
                ? nameField
                : [nameField];

            return {
                // Keep shared=false so hovering one series doesn't highlight others
                shared: false,
                useHTML: true,
                backgroundColor: "#2D2D2D",
                borderRadius: 12,
                borderWidth: 0,
                padding: 10,
                formatter: function (
                    this: Highcharts.TooltipFormatterContextObject
                ): string {
                    const date = this.x !== undefined ? this.x : "";
                    const dateDisplay =
                        typeof date === "string" ? date : String(date);

                    // Build rows from all visible series using the hovered point index
                    const pointIndex = (this as any).point
                        ? (this as any).point.index
                        : 0;
                    const chart = (this.series as any).chart;
                    let chartSeries = chart?.series || [];

                    // Filter visible series first
                    chartSeries = chartSeries.filter(
                        (s: any) => s && s.visible
                    );

                    // Get sorting config from chart userOptions (injected at runtime)
                    const sortingConfig = chart?.userOptions?.sortingConfig;

                    // Sort the series if sorting config is available
                    if (
                        sortingConfig &&
                        sortingConfig.sortByFiltersData &&
                        sortingConfig.dropDownFiltersData &&
                        sortingConfig.dropDownFiltersData.length > 0
                    ) {
                        const { dropDownFiltersData, primaryDimension } =
                            sortingConfig;

                        // Create a map of filters data order for quick lookup
                        const filtersOrderMap = new Map();
                        dropDownFiltersData.forEach(
                            (filterItem: any, index: number) => {
                                const filterValue =
                                    filterItem[primaryDimension];
                                if (filterValue) {
                                    filtersOrderMap.set(filterValue, index);
                                }
                            }
                        );

                        // Helper function to extract value from series for sorting
                        const getSeriesValue = (series: any): string => {
                            if (series.userOptions) {
                                for (const field of nameFields) {
                                    if (series.userOptions[field]) {
                                        return series.userOptions[field];
                                    }
                                }
                            }
                            return series.name || "";
                        };

                        // Sort series based on the order in filters data
                        chartSeries = chartSeries.sort((a: any, b: any) => {
                            const aValue = getSeriesValue(a);
                            const bValue = getSeriesValue(b);
                            const aOrder = filtersOrderMap.has(aValue)
                                ? filtersOrderMap.get(aValue)
                                : Number.MAX_SAFE_INTEGER;
                            const bOrder = filtersOrderMap.has(bValue)
                                ? filtersOrderMap.get(bValue)
                                : Number.MAX_SAFE_INTEGER;
                            return aOrder - bOrder;
                        });
                    }
                    const rows = chartSeries
                        .map((s: any) => {
                            const y = Array.isArray(s.yData)
                                ? s.yData[pointIndex]
                                : undefined;
                            if (y === null || y === undefined) return null;
                            const unit =
                                (s.userOptions && s.userOptions.unit) || "";

                            // Try each field in order until we find a value
                            let name = "";
                            if (s.userOptions) {
                                for (const field of nameFields) {
                                    if (s.userOptions[field]) {
                                        name = s.userOptions[field];
                                        break;
                                    }
                                }
                            }
                            // Fallback to s.name if no field found
                            if (!name) {
                                name = s.name || "";
                            }
                            const value =
                                typeof y === "number"
                                    ? `${y.toFixed(1)} ${unit}`
                                    : "-";
                            return `
                                <div style="display:flex;align-items:center;justify-content:space-between;margin:4px 0;min-width:180px;">
                                    <div style="display:flex;align-items:center;color:#fff;font-size:11px;font-weight:500;max-width:140px;overflow:hidden;">
                                        <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${titleCase(
                                            String(name)
                                        )}</span>
                                    </div>
                                    <div style="color:#fff;font-size:11px;font-weight:600;">${value}</div>
                                </div>
                            `;
                        })
                        .filter(Boolean)
                        .join("");

                    return `
                        <div style="text-align:left;">
                            <div style="text-align:center;font-size:10px;margin-bottom:6px;color:#ccc;font-family:'Manrope',sans-serif;">${dateDisplay}</div>
                            ${
                                rows ||
                                '<div style="color:#ccc;font-size:10px;">No data</div>'
                            }
                        </div>
                    `;
                },
                positioner: function (
                    this: Highcharts.Tooltip,
                    labelWidth: number,
                    labelHeight: number,
                    point: Highcharts.Point
                ): { x: number; y: number } {
                    const chart = this.chart;
                    const anchorX =
                        (point as any)?.plotX ?? chart.plotWidth / 2;
                    const x = Math.min(
                        Math.max(
                            anchorX + chart.plotLeft - labelWidth / 2,
                            chart.plotLeft
                        ),
                        chart.plotLeft + chart.plotWidth - labelWidth
                    );
                    const y = chart.plotTop + 10;
                    return { x, y };
                },
            };
        };

        const navigatorTrendsConfig = {
            dateFormat: "D/MM/YY",
            isChartLoadingRequired: true,
            chartDimensions: {
                minHeight: 100,
                maxHeight: 370,
                // The heightPercent controls the chart's height in the above sepcified min and max height,
                // taking the viewPort height into account
                heightPercent: 0.25,
            },
            chartType: "areaspline",
            isCustomLegendRequired: false,
            isChartActionsRequired: true,
            isAdvanceTrendsBtnRequired: false,
            enableAdvanceTrendsBtn: true,
            graphAttributes: {
                yAxisTitle: "Market Share in %",
                min: 0,
                height: 400,
                hideXAxisTitle: true,
                xAxis: {
                    tickmarkPlacement: "on",
                    labels: {
                        enabled: false,
                        rotation: 40,
                    },
                    crosshair: {
                        width: 1,
                        color: "#605BFF",
                        dashStyle: "Dot",
                        zIndex: 5,
                    },
                },
                tooltip: {
                    shared: false,
                    useHTML: true,
                    backgroundColor: "#030229",
                    borderRadius: 25,
                    borderWidth: 0,
                    formatter: function (
                        this: Highcharts.TooltipFormatterContextObject
                    ): string {
                        const unit =
                            (this.series as any).userOptions.unit || "";

                        // Get the date from the x-axis category
                        const date = this.x !== undefined ? this.x : "";
                        const dateDisplay =
                            typeof date === "string" ? date : String(date);
                        return `
                    <div style="text-align:center;">
                        <div style="font-size:10px; font-weight:600; margin-bottom:4px; color: #ccc; font-style:normal; font-family: 'Manrope', sans-serif;">${dateDisplay}</div>
                        <div style="font-size:12px; font-weight:600; margin-bottom:2px; color: #fff; font-style:normal; font-family: 'Manrope', sans-serif;">${
                            this.series.name
                        }</div>
                        <div style="font-size:16px; font-weight:600; font-family: 'Nunito', sans-serif; color: #fff;">
                        ${this.y?.toFixed(1)} ${unit}
                        </div>
                    </div>
                    `;
                    },
                },
                plotOptions: {
                    series: {
                        marker: { enabled: false },
                        lineWidth: 1.5,
                        pointPlacement: "on",
                    },
                },
                minorTicksLabelLimit: 3,
            },
            graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
        };

        const baseChannelTrendWidget = {
            identifier: "dd-scc-t",
            cubeId: RevstackCubeIds.PO_REGIONAL,
            title: "",
            subTitle: "",
            type: WidgetType.W3,
            queryDimensions: ["channel_id"],
            infoMessage: "",
            isMeasureAppendRequired: false,
            measures: [
                CommonWidgetMetrics.MARKET_SHARE,
                CommonWidgetMetrics.OVERALL_SOV,
                CommonWidgetMetrics.ORGANIC_SOV,
                CommonWidgetMetrics.PAID_SOV,
            ],
            excludeFilter: ["brand_id"],
            queryMetaInfo: {
                resultKey: "channel_trends_modal_data",
                recordKey: "channel_id",
                primaryDimension: "channel_id",
                avoidSkippedFilterGrouping: true,
            },
            plotFields: [
                {
                    field: `market_share${filterToAppend}`,
                    type: "measure",
                },
                {
                    field: `overall_sov`,
                    type: "measure",
                },
            ],
            relativePeriodConfig: {
                granularity: "day",
                value: 30,
            },
            config: {
                metricKey: "name",
                formatForChannelId: true,
                isWidgetFilterRequired: true,
                widgetFilterConfig: {
                    defaultValue: capitalizeSpecificSubstring(
                        titleCase(CommonWidgetMetrics.OVERALL_SOV),
                        "sov",
                        true
                    ),
                },
                ...navigatorTrendsConfig,
                graphAttributes: {
                    ...navigatorTrendsConfig.graphAttributes,
                    tooltip: navigatorMultiRowTooltipConfig("channel_id"),
                },
            },
        };

        const baseNavigatorDDCrossChannelTrendsConfig = {
            title: "Overview",
            tabKey: CA_DD_MODULES.OVERVIEW,
            moduleType: "default",
            sectionId: RevstackCASectionIds.NDDTOA,
            config: {
                layout: {
                    type: "card",
                    isBodyPaddingReq: false,
                    isTitleandIconRequired: false,
                    hasChartPillsFilters: true,
                    hasFilterDropDown: true,
                },
                queryMetaInfo: {
                    resultKey: "channel_trends_modal_data",
                    primaryDimension: "channel_id",
                },
                formatForChannelId: true,
                sortByFiltersData: true,
                isRemovablePillsRequired: true,
                capitalizePillNames: false,
                addPillsBtnTitle: "Add Channels",
                maxPillsFiltersAllowed: 5,
                graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                disableYaxisOppositeOnPillCount: 1,
                // Exclude the selected yAxis metric from yAxisOpposite options
                excludeYAxisFromOpposite: true,
                // Enable tooltip synchronization across all four charts
                enableTooltipSync: true,
                pillsDropDownConfig: {
                    cubeId: RevstackCubeIds.PO_REGIONAL,
                    queryId: "channel_subcat_dropdown",
                    queryDimensions: ["channel_id"],
                    excludeFilter: ["brand_id"],
                    formatForChannelId: true,
                },
                chartPillFiltersDatakeys: {
                    title: "channel_id",
                    subTitle: "",
                    metricToDisplay: "Org SOV",
                    metricUnit: "%",
                    metricValue: `organic_sov_value`,
                    value: "channel_id",
                },
                header: {
                    components: [
                        {
                            section: "main",
                            elements: [
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: "category",
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [] as {
                                            label: string;
                                            value: string;
                                        }[],
                                        defaultValue: [] as string[],
                                        value: "",
                                        mode: "",
                                        className: "",
                                        placeholder: "",
                                        styles: {},
                                        subTitle: "" as any,
                                    },
                                },
                            ],
                        },
                        {
                            section: "filters",
                            elements: [
                                {
                                    type: RevstackFilterTypes.MULTI_SELECT,
                                    defaultFilter: [OptionValues.ALL],
                                    filterKey: CommonWidgetMetrics.CHANNEL,
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        showAllOption: false,
                                        defaultValue: [] as string[],
                                        useTags: true,
                                        value: "",
                                        styles: {},
                                        className: "",
                                        placeholder: "",
                                        width: 140,
                                    },
                                },
                                {
                                    type: RevstackFilterTypes.SELECT,
                                    filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                    props: {
                                        onClick: () => {},
                                        onChange: () => {},
                                        onOpenChange: () => {},
                                        options: [
                                            { label: "NATIONAL", value: "all" },
                                        ] as { label: string; value: string }[],
                                        defaultValue: [] as string[],
                                        value: "",
                                        mode: "",
                                        styles: {},
                                        className: "",
                                        placeholder: "",
                                        width: 140,
                                    },
                                },
                            ],
                        },
                    ],
                },
                tabs: {
                    hideTabsSection: false,
                    defaultTab: CA_DD_MODULES.OVERVIEW,
                },
            },
            widget: RevstackGenericChartParentContainer,
            widgets: [
                [
                    [
                        {
                            ...baseChannelTrendWidget,
                            config: {
                                ...baseChannelTrendWidget.config,
                                chartId: "dd-scc-t-0",
                                widgetFilterConfig: {
                                    ...baseChannelTrendWidget.config
                                        .widgetFilterConfig,
                                    defaultValue: titleCase(
                                        CommonWidgetMetrics.MARKET_SHARE
                                    ),
                                },
                            },
                        },
                        {
                            ...baseChannelTrendWidget,
                            config: {
                                ...baseChannelTrendWidget.config,
                                chartId: "dd-scc-t-1",
                                widgetFilterConfig: {
                                    ...baseChannelTrendWidget.config
                                        .widgetFilterConfig,
                                    defaultValue: capitalizeSpecificSubstring(
                                        titleCase(
                                            CommonWidgetMetrics.OVERALL_SOV
                                        ),
                                        "sov",
                                        true
                                    ),
                                },
                            },
                        },
                    ],
                    [
                        {
                            ...baseChannelTrendWidget,
                            config: {
                                chartId: "dd-scc-t-2",
                                ...baseChannelTrendWidget.config,
                                widgetFilterConfig: {
                                    ...baseChannelTrendWidget.config
                                        .widgetFilterConfig,
                                    defaultValue: capitalizeSpecificSubstring(
                                        titleCase(
                                            CommonWidgetMetrics.ORGANIC_SOV
                                        ),
                                        "sov",
                                        true
                                    ),
                                },
                            },
                        },
                        {
                            ...baseChannelTrendWidget,
                            config: {
                                ...baseChannelTrendWidget.config,
                                chartId: "dd-scc-t-3",
                                widgetFilterConfig: {
                                    ...baseChannelTrendWidget.config
                                        .widgetFilterConfig,
                                    defaultValue: capitalizeSpecificSubstring(
                                        titleCase(CommonWidgetMetrics.PAID_SOV),
                                        "sov",
                                        true
                                    ),
                                },
                            },
                        },
                    ],
                ],
            ],
        };

        const baseNavigatorDDSubCatTrendsWidget = {
            ...sections[RevstackCASectionIds.DDTSOA].widgets[0],
            cubeId: RevstackCubeIds.PO_REGIONAL,
            queryDimensions: ["subcategory", "channel_id"],
            infoMessage: "",
            isMeasureAppendRequired: false,
            measures: [
                CommonWidgetMetrics.MARKET_SHARE,
                CommonWidgetMetrics.OVERALL_SOV,
                CommonWidgetMetrics.ORGANIC_SOV,
                CommonWidgetMetrics.PAID_SOV,
                // CommonWidgetMetrics.BRANDED_SEARCH,
                // CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
            ],
            queryMetaInfo: {
                resultKey: "navigator_trends_modal_subcat_data",
                primaryDimension: "subcategory",
            },
            excludeFilter: ["brand_id"],
            plotFields: [
                {
                    field: `market_share${filterToAppend}`,
                    type: "measure",
                },
                {
                    field: `overall_sov`,
                    type: "measure",
                },
            ],
            relativePeriodConfig: {
                granularity: "day",
                value: 30,
            },
            config: {
                ...navigatorTrendsConfig,
                metricKey: "name",
                filterYaxisAttribute: "name",
                chartId: "nddtssa-0",
                isWidgetFilterRequired: true,
                isNestedChart: true,
                widgetFilterConfig: {
                    defaultValue: titleCase(CommonWidgetMetrics.MARKET_SHARE),
                },
                graphAttributes: {
                    ...navigatorTrendsConfig.graphAttributes,
                    tooltip: navigatorMultiRowTooltipConfig("subcategory"),
                },
            },
        };

        const baseNavigatorDDBrandTrendsWidget = {
            ...sections[RevstackCASectionIds.DDTSBA].widgets[0],
            cubeId: RevstackCubeIds.PO_REGIONAL,
            queryDimensions: ["brand", "brand_id"],
            infoMessage: "",
            isMeasureAppendRequired: false,
            measures: [
                CommonWidgetMetrics.MARKET_SHARE,
                CommonWidgetMetrics.OVERALL_SOV,
                CommonWidgetMetrics.ORGANIC_SOV,
                CommonWidgetMetrics.PAID_SOV,
                // CommonWidgetMetrics.BRANDED_SEARCH,
                // CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
            ],
            queryMetaInfo: {
                filterToSkip: "brand_id",
                primaryDimension: "brand",
                resultKey: "navigator_trends_modal_brand_data",
                avoidSkippedFilterGrouping: true,
            },
            excludeFilter: ["brand_id"],
            plotFields: [
                {
                    field: `market_share${filterToAppend}`,
                    type: "measure",
                },
                {
                    field: `overall_sov`,
                    type: "measure",
                },
            ],
            relativePeriodConfig: {
                granularity: "day",
                value: 30,
            },
            config: {
                ...navigatorTrendsConfig,
                metricKey: "name",
                filterYaxisAttribute: "name",
                chartId: "nddtba-0",
                isNestedChart: true,
                isWidgetFilterRequired: true,
                widgetFilterConfig: {
                    defaultValue: titleCase(CommonWidgetMetrics.MARKET_SHARE),
                },
                graphAttributes: {
                    ...navigatorTrendsConfig.graphAttributes,
                    tooltip: navigatorMultiRowTooltipConfig("brand"),
                },
                sortByFiltersData: true,
            },
        };

        const baseNavigatorDDSkuAnalysisTrendsWidget = {
            ...sections[RevstackCASectionIds.DDTSSA].widgets[0],
            cubeId: RevstackCubeIds.PO_REGIONAL,
            queryDimensions: ["sku_title"],
            infoMessage: "",
            isMeasureAppendRequired: false,
            measures: [
                CommonWidgetMetrics.MARKET_SHARE,
                CommonWidgetMetrics.OVERALL_SOV,
                CommonWidgetMetrics.ORGANIC_SOV,
                CommonWidgetMetrics.PAID_SOV,
                // CommonWidgetMetrics.BRANDED_SEARCH,
                // CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
            ],
            queryMetaInfo: {
                filterToSkip: "brand_id",
                resultKey: "navigator_trends_modal_sku_analysis_data",
                primaryDimension: "sku_title",
                avoidSkippedFilterGrouping: true,
            },
            excludeFilter: ["brand_id"],
            plotFields: [
                {
                    field: `market_share${filterToAppend}`,
                    type: "measure",
                },
                {
                    field: `overall_sov`,
                    type: "measure",
                },
            ],
            relativePeriodConfig: {
                granularity: "day",
                value: 30,
            },
            config: {
                ...navigatorTrendsConfig,
                metricKey: "name",
                filterYaxisAttribute: "name",
                chartId: "nddtssa-0",
                isNestedChart: true,
                isWidgetFilterRequired: true,
                widgetFilterConfig: {
                    defaultValue: titleCase(CommonWidgetMetrics.MARKET_SHARE),
                },
                graphAttributes: {
                    ...navigatorTrendsConfig.graphAttributes,
                    tooltip: navigatorMultiRowTooltipConfig("sku_title"),
                },
                sortByFiltersData: true,
            },
        };

        const baseNavigatorDDCrossRegionTrendsWidget = {
            ...sections[RevstackCASectionIds.DDTSCRA].widgets[0],
            cubeId: RevstackCubeIds.PO_REGIONAL,
            queryDimensions: ["region"],
            infoMessage: "",
            isMeasureAppendRequired: false,
            measures: [
                CommonWidgetMetrics.MARKET_SHARE,
                CommonWidgetMetrics.OVERALL_SOV,
                CommonWidgetMetrics.ORGANIC_SOV,
                CommonWidgetMetrics.PAID_SOV,
                // CommonWidgetMetrics.BRANDED_SEARCH,
                // CommonWidgetMetrics.RELATIVE_PRICE_INDEX,
            ],
            queryMetaInfo: {
                resultKey: "navigator_trends_modal_cross_region_data",
                recordKey: "region",
                dimValueToTruncate: "-Region",
                primaryDimension: "region",
            },
            excludeFilter: ["brand_id"],
            plotFields: [
                {
                    field: `market_share${filterToAppend}`,
                    type: "measure",
                },
                {
                    field: `overall_sov`,
                    type: "measure",
                },
            ],
            relativePeriodConfig: {
                granularity: "day",
                value: 30,
            },
            config: {
                ...navigatorTrendsConfig,
                metricKey: "name",
                filterYaxisAttribute: "name",
                chartId: "nddtca-0",
                isWidgetFilterRequired: true,
                isNestedChart: true,
                widgetFilterConfig: {
                    defaultValue: titleCase(CommonWidgetMetrics.MARKET_SHARE),
                },
                graphAttributes: {
                    ...navigatorTrendsConfig.graphAttributes,
                    tooltip: navigatorMultiRowTooltipConfig("region"),
                },
            },
        };

        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.PO]: {
                    [RevstackCASectionIds.TPOA]: {
                        ...sections[RevstackCASectionIds.DDTSCCA],
                        config: {
                            ...sections[RevstackCASectionIds.DDTSCCA].config,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "channel_subcat_dropdown",
                                queryDimensions: ["channel_id"],
                                excludeFilter: ["brand_id"],
                                filterToSkip: "channel_id",
                                formatForChannelId: true,
                            },
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                id: `${RevstackCASectionIds.TPOA}-title`,
                                            },
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [] as any[],
                                                    defaultValue: [] as any[],
                                                    value: "",
                                                    mode: "",
                                                    className: "",
                                                    placeholder: "",
                                                    styles: {},
                                                    subTitle: "" as any,
                                                    width: 140,
                                                },
                                            },
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [] as any[],
                                                    defaultValue: [] as any[],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                    width: 140,
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                hideTabsSection: true,
                                defaultTab: CA_DD_MODULES.CROSS_CHANNEL,
                            },
                        },
                        widgets: [
                            {
                                identifier: "dd-scc-t",
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                title: "",
                                subTitle: "",
                                type: WidgetType.W3,
                                queryDimensions: ["channel_id"],
                                infoMessage: "",
                                isMeasureAppendRequired: false,
                                measures: [
                                    CommonWidgetMetrics.MARKET_SHARE,
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    CommonWidgetMetrics.PAID_SOV,
                                ],
                                excludeFilter: ["brand_id"],
                                queryMetaInfo: {
                                    resultKey: "channel_trends_modal_data",
                                    recordKey: "channel_id",
                                    filterToSkip: "channel_id",
                                    primaryDimension: "channel_id",
                                    avoidSkippedFilterGrouping: true,
                                },
                                plotFields: [
                                    {
                                        field: `market_share${filterToAppend}`,
                                        type: "measure",
                                    },
                                    {
                                        field: `overall_sov`,
                                        type: "measure",
                                    },
                                ],
                                relativePeriodConfig: {
                                    granularity: "day",
                                    value: 30,
                                },
                                config: {
                                    metricKey: "name",
                                    filterYaxisAttribute: "name",
                                    filterYaxisOppositeAttribute: "name",
                                    formatForChannelId: true,
                                    ...trendsChartConfig(filterToAppend),
                                },
                            },
                        ],
                    },
                },
                [RevstackCASectionIds.COMV]: {
                    [RevstackCASectionIds.TCOMVA]: {
                        tabKey: CA_DD_MODULES.OVERVIEW,
                        moduleType: "default",
                        sectionId: RevstackCASectionIds.TCOMVA,
                        config: {
                            layout: {
                                type: "card",
                                isBodyPaddingReq: false,
                                isTitleandIconRequired: false,
                                hasChartPillsFilters: true,
                                hasFilterDropDown: true,
                            },
                            queryMetaInfo: {
                                resultKey: "channel_trends_modal_data",
                                primaryDimension: "channel_id",
                            },
                            formatForChannelId: true,
                            sortByFiltersData: true,
                            isRemovablePillsRequired: true,
                            capitalizePillNames: false,
                            addPillsBtnTitle: "Add Channels",
                            maxPillsFiltersAllowed: 5,
                            graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
                            disableYaxisOppositeOnPillCount: 1,
                            // Exclude the selected yAxis metric from yAxisOpposite options
                            excludeYAxisFromOpposite: true,
                            // Enable tooltip synchronization across all four charts
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "channel_subcat_dropdown",
                                queryDimensions: ["channel_id"],
                                excludeFilter: ["brand_id"],
                                filterToSkip: "channel_id",
                                formatForChannelId: true,
                            },
                            chartPillFiltersDatakeys: {
                                title: "channel_id",
                                subTitle: "",
                                metricToDisplay: "Org SOV",
                                metricUnit: "%",
                                metricValue: `organic_sov_value`,
                                value: "channel_id",
                            },
                            header: {
                                components: [
                                    {
                                        section: "main",
                                        elements: [
                                            {
                                                type: "text",
                                                id: `${RevstackCASectionIds.TCOMVA}-title`,
                                            },
                                        ],
                                    },
                                    {
                                        section: "filters",
                                        elements: [
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: "category",
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [
                                                        {
                                                            label: "All Categories",
                                                            value: "all",
                                                        },
                                                    ],
                                                    defaultValue:
                                                        [] as string[],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                    width: 140,
                                                },
                                            },
                                            {
                                                type: RevstackFilterTypes.SELECT,
                                                filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                props: {
                                                    onClick: () => {},
                                                    onChange: () => {},
                                                    onOpenChange: () => {},
                                                    options: [
                                                        {
                                                            label: "NATIONAL",
                                                            value: "all",
                                                        },
                                                    ],
                                                    defaultValue: [],
                                                    value: "",
                                                    mode: "",
                                                    styles: {},
                                                    className: "",
                                                    placeholder: "",
                                                    width: 140,
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            tabs: {
                                hideTabsSection: true,
                                defaultTab: CA_DD_MODULES.OVERVIEW,
                            },
                        },
                        widget: RevstackGenericChartParentContainer,
                        widgets: [
                            [
                                [
                                    {
                                        ...baseChannelTrendWidget,
                                        config: {
                                            ...baseChannelTrendWidget.config,
                                            chartId: "dd-scc-t-0",
                                            widgetFilterConfig: {
                                                ...baseChannelTrendWidget.config
                                                    .widgetFilterConfig,
                                                defaultValue: titleCase(
                                                    CommonWidgetMetrics.MARKET_SHARE
                                                ),
                                            },
                                            isNestedChart: true,
                                        },
                                    },
                                    {
                                        ...baseChannelTrendWidget,
                                        config: {
                                            ...baseChannelTrendWidget.config,
                                            chartId: "dd-scc-t-1",
                                            widgetFilterConfig: {
                                                ...baseChannelTrendWidget.config
                                                    .widgetFilterConfig,
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                            isNestedChart: true,
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseChannelTrendWidget,
                                        config: {
                                            chartId: "dd-scc-t-2",
                                            ...baseChannelTrendWidget.config,
                                            widgetFilterConfig: {
                                                ...baseChannelTrendWidget.config
                                                    .widgetFilterConfig,
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                            isNestedChart: true,
                                        },
                                    },
                                    {
                                        ...baseChannelTrendWidget,
                                        config: {
                                            ...baseChannelTrendWidget.config,
                                            chartId: "dd-scc-t-3",
                                            widgetFilterConfig: {
                                                ...baseChannelTrendWidget.config
                                                    .widgetFilterConfig,
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                            isNestedChart: true,
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                },
                // DrillDown Trends
                [RevstackCASectionIds.CA]: {
                    [RevstackCASectionIds.NDDTOA]: {
                        ...baseNavigatorDDCrossChannelTrendsConfig,
                    },
                    // Subcategories
                    [RevstackCASectionIds.NDDTSSA]: {
                        ...sections[RevstackCASectionIds.DDTSOA],
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        sectionId: RevstackCASectionIds.NDDTSSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSOA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "cat_subcat_dropdown",
                                queryDimensions: ["category", "subcategory"],
                                excludeFilter: ["brand_id"],
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey: "navigator_trends_modal_subcat_data",
                                primaryDimension: "subcategory",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSubCatTrendsWidget,
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Brand
                    [RevstackCASectionIds.NDDTBA]: {
                        ...sections[RevstackCASectionIds.DDTSBA],
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTBA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSBA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "brand_subcat_dropdown",
                                queryDimensions: ["brand", "brand_id"],
                                isExcludeDimension: true,
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                primaryDimension: "brand",
                                resultKey: "navigator_trends_modal_brand_data",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDBrandTrendsWidget,
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-1",
                                            extractPillsData: true,
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-2",
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-3",
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // SKU
                    [RevstackCASectionIds.NDDTSA]: {
                        ...sections[RevstackCASectionIds.DDTSSA],
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSSA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "sku_subcat_dropdown",
                                queryDimensions: ["sku_title"],
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_sku_analysis_data",
                                primaryDimension: "sku_title",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSkuAnalysisTrendsWidget,
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-1",
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-2",
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-3",
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Cross Region
                    [RevstackCASectionIds.NDDTCA]: {
                        ...sections[RevstackCASectionIds.DDTSCRA],
                        sectionId: RevstackCASectionIds.NDDTCA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSCRA].config,
                            enableTooltipSync: true,
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_cross_region_data",
                                primaryDimension: "region",
                            },
                            filters: [] as any[],
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDCrossRegionTrendsWidget,
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-2",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.ORGANIC_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-3",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.PAID_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.SA]: {
                    [RevstackCASectionIds.NDDTOA]: {
                        ...baseNavigatorDDCrossChannelTrendsConfig,
                    },
                    // Subcategories
                    [RevstackCASectionIds.NDDTSSA]: {
                        ...sections[RevstackCASectionIds.DDTSOA],
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        sectionId: RevstackCASectionIds.NDDTSSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSOA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "cat_subcat_dropdown",
                                queryDimensions: ["category", "subcategory"],
                                excludeFilter: ["brand_id"],
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey: "navigator_trends_modal_subcat_data",
                                primaryDimension: "subcategory",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSubCatTrendsWidget,
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Brand
                    [RevstackCASectionIds.NDDTBA]: {
                        ...sections[RevstackCASectionIds.DDTSBA],
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTBA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSBA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "brand_subcat_dropdown",
                                queryDimensions: ["brand", "brand_id"],
                                isExcludeDimension: true,
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                primaryDimension: "brand",
                                resultKey: "navigator_trends_modal_brand_data",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDBrandTrendsWidget,
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-1",
                                            extractPillsData: true,
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // SKU
                    [RevstackCASectionIds.NDDTSA]: {
                        ...sections[RevstackCASectionIds.DDTSSA],
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSSA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "sku_subcat_dropdown",
                                queryDimensions: ["sku_title"],
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_sku_analysis_data",
                                primaryDimension: "sku_title",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSkuAnalysisTrendsWidget,
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Cross Region
                    [RevstackCASectionIds.NDDTCA]: {
                        ...sections[RevstackCASectionIds.DDTSCRA],
                        sectionId: RevstackCASectionIds.NDDTCA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSCRA].config,
                            enableTooltipSync: true,
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_cross_region_data",
                                primaryDimension: "region",
                            },
                            filters: [] as any[],
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDCrossRegionTrendsWidget,
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-1",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.OVERALL_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-2",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.ORGANIC_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-3",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.PAID_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.PA]: {
                    [RevstackCASectionIds.NDDTOA]: {
                        ...baseNavigatorDDCrossChannelTrendsConfig,
                    },
                    // Subcategories
                    [RevstackCASectionIds.NDDTSSA]: {
                        ...sections[RevstackCASectionIds.DDTSOA],
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        sectionId: RevstackCASectionIds.NDDTSSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSOA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "cat_subcat_dropdown",
                                queryDimensions: ["category", "subcategory"],
                                excludeFilter: ["brand_id"],
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey: "navigator_trends_modal_subcat_data",
                                primaryDimension: "subcategory",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSubCatTrendsWidget,
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Brand
                    [RevstackCASectionIds.NDDTBA]: {
                        ...sections[RevstackCASectionIds.DDTSBA],
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTBA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSBA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "brand_subcat_dropdown",
                                queryDimensions: ["brand", "brand_id"],
                                isExcludeDimension: true,
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                primaryDimension: "brand",
                                resultKey: "navigator_trends_modal_brand_data",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDBrandTrendsWidget,
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-1",
                                            extractPillsData: true,
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // SKU
                    [RevstackCASectionIds.NDDTSA]: {
                        ...sections[RevstackCASectionIds.DDTSSA],
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSSA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "sku_subcat_dropdown",
                                queryDimensions: ["sku_title"],
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_sku_analysis_data",
                                primaryDimension: "sku_title",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSkuAnalysisTrendsWidget,
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Cross Region
                    [RevstackCASectionIds.NDDTCA]: {
                        ...sections[RevstackCASectionIds.DDTSCRA],
                        sectionId: RevstackCASectionIds.NDDTCA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSCRA].config,
                            enableTooltipSync: true,
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_cross_region_data",
                                primaryDimension: "region",
                            },
                            filters: [] as any[],
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDCrossRegionTrendsWidget,
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-1",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.OVERALL_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-2",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.ORGANIC_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-3",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.PAID_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                },
                [RevstackCASectionIds.RA]: {
                    [RevstackCASectionIds.NDDTOA]: {
                        ...baseNavigatorDDCrossChannelTrendsConfig,
                    },
                    // Subcategories
                    [RevstackCASectionIds.NDDTSSA]: {
                        ...sections[RevstackCASectionIds.DDTSOA],
                        title: "Sub Categories",
                        tabKey: CA_DD_MODULES.SUB_CATEGORIES,
                        sectionId: RevstackCASectionIds.NDDTSSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSOA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "cat_subcat_dropdown",
                                queryDimensions: ["category", "subcategory"],
                                excludeFilter: ["brand_id"],
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey: "navigator_trends_modal_subcat_data",
                                primaryDimension: "subcategory",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSubCatTrendsWidget,
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSubCatTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSubCatTrendsWidget.config,
                                            chartId: "nddtssa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Brand
                    [RevstackCASectionIds.NDDTBA]: {
                        ...sections[RevstackCASectionIds.DDTSBA],
                        title: "Brand Analysis",
                        tabKey: CA_DD_MODULES.BRAND_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTBA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSBA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "brand_subcat_dropdown",
                                queryDimensions: ["brand", "brand_id"],
                                isExcludeDimension: true,
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                primaryDimension: "brand",
                                resultKey: "navigator_trends_modal_brand_data",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDBrandTrendsWidget,
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-1",
                                            extractPillsData: true,
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDBrandTrendsWidget,
                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                        config: {
                                            ...baseNavigatorDDBrandTrendsWidget.config,
                                            chartId: "nddtba-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // SKU
                    [RevstackCASectionIds.NDDTSA]: {
                        ...sections[RevstackCASectionIds.DDTSSA],
                        title: "SKU Analysis",
                        tabKey: CA_DD_MODULES.SKU_ANALYTICS,
                        sectionId: RevstackCASectionIds.NDDTSA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSSA].config,
                            enableTooltipSync: true,
                            pillsDropDownConfig: {
                                cubeId: RevstackCubeIds.PO_REGIONAL,
                                queryId: "sku_subcat_dropdown",
                                queryDimensions: ["sku_title"],
                                isExcludeDimension: true,
                                excludeFilter: ["brand_id"],
                                filterToSkip: "brand_id",
                            },
                            filters: [] as any[],
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_sku_analysis_data",
                                primaryDimension: "sku_title",
                            },
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDSkuAnalysisTrendsWidget,
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-1",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.OVERALL_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-2",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.ORGANIC_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDSkuAnalysisTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDSkuAnalysisTrendsWidget.config,
                                            chartId: "nddtsa-3",
                                            widgetFilterConfig: {
                                                defaultValue:
                                                    capitalizeSpecificSubstring(
                                                        titleCase(
                                                            CommonWidgetMetrics.PAID_SOV
                                                        ),
                                                        "sov",
                                                        true
                                                    ),
                                            },
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                    // Cross Region
                    [RevstackCASectionIds.NDDTCA]: {
                        ...sections[RevstackCASectionIds.DDTSCRA],
                        sectionId: RevstackCASectionIds.NDDTCA,
                        config: {
                            ...sections[RevstackCASectionIds.DDTSCRA].config,
                            enableTooltipSync: true,
                            queryMetaInfo: {
                                resultKey:
                                    "navigator_trends_modal_cross_region_data",
                                primaryDimension: "region",
                            },
                            filters: [] as any[],
                        },
                        widgets: [
                            [
                                [
                                    baseNavigatorDDCrossRegionTrendsWidget,
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-1",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.OVERALL_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                                [
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-2",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.ORGANIC_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                    {
                                        ...baseNavigatorDDCrossRegionTrendsWidget,
                                        config: {
                                            ...baseNavigatorDDCrossRegionTrendsWidget.config,
                                            chartId: "nddtca-3",
                                        },
                                        widgetFilterConfig: {
                                            defaultValue:
                                                capitalizeSpecificSubstring(
                                                    titleCase(
                                                        CommonWidgetMetrics.PAID_SOV
                                                    ),
                                                    "sov",
                                                    true
                                                ),
                                        },
                                    },
                                ],
                            ],
                        ],
                    },
                },
            };
        }
    }

    if (module === RevstackDashboardModules.PRODUCT_ANALYSIS) {
        if (subModule === RevstackDashboardSubModules.OVERVIEW) {
            return {
                [RevstackCASectionIds.RWP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.TRAFFIC) {
            return {
                [RevstackCASectionIds.RWP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.CONVERSION) {
            return {
                [RevstackCASectionIds.RWP]: sections,
            };
        }
        if (subModule === RevstackDashboardSubModules.OPERATIONS) {
            return {
                [RevstackCASectionIds.RWP]: sections,
            };
        }
    }
};

//!Note[DND]: Channel-specific overrides - replace entire sections
const CHANNEL_OVERRIDES: Record<string, any> = {
    [Channels.AMAZON]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.NAVIGATOR]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
    },
    [Channels.SHOPEE]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.NAVIGATOR]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
    },
    // [Channels.NOON_MINUTES]: {
    //     [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         traffic: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         conversion: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         operations: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    //     [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    //     [RevstackDashboardModules.NAVIGATOR]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    // },
    // [Channels.TALABAT_REVSTACK]: {
    //     [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         traffic: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         conversion: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //         operations: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    //     [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    //     [RevstackDashboardModules.NAVIGATOR]: {
    //         overview: {
    //             [RevstackCASectionIds.RWP]: undefined,
    //         },
    //     },
    // },
    [Channels.TIKTOK]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.NAVIGATOR]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
    },
    [Channels.LAZADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.NAVIGATOR]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
    },
    [Channels.HEPSIBURADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            traffic: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            conversion: {
                [RevstackCASectionIds.RWP]: undefined,
            },
            operations: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.PRODUCT_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
        [RevstackDashboardModules.NAVIGATOR]: {
            overview: {
                [RevstackCASectionIds.RWP]: undefined,
            },
        },
    },
};

//!Note[DND]: Drilldown channel-specific overrides
const CHANNEL_OVERRIDES_DD: Record<string, any> = {
    [Channels.AMAZON]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.SHOPEE]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.NOON_MINUTES]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.TALABAT_REVSTACK]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.TIKTOK]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.LAZADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.HEPSIBURADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDSCRTA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
};

//!Note[DND]: Drilldown Trends Modal channel-specific overrides
const CHANNEL_OVERRIDES_DD_TRENDS_MODAL: Record<string, any> = {
    [Channels.AMAZON]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.SHOPEE]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.NOON_MINUTES]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.TALABAT_REVSTACK]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.TIKTOK]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.LAZADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
    [Channels.HEPSIBURADA]: {
        [RevstackDashboardModules.CATEGORY_ANALYSIS]: {
            overview: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            traffic: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            conversion: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDTSCRA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
            operations: {
                [RevstackCASectionIds.SP]: {
                    [RevstackCASectionIds.DDTSCRA]: {
                        icon: <></>,
                        title: "Cross Region",
                        tabKey: CA_DD_MODULES.CROSS_REGION,
                        sectionId: RevstackCASectionIds.DDSCRTA,
                        widget: RevstackTablePerformanceWidget,
                        disabled: true,
                        config: {},
                        widgets: [],
                    },
                },
            },
        },
    },
};

//!Note[DND]: Simple merge utility - replace at section level
const mergeConfig = (baseConfig: any, overrides: any): any => {
    if (!overrides) return baseConfig;

    const result = { ...baseConfig };

    for (const sectionId in overrides) {
        if (overrides[sectionId] === undefined) {
            delete result[sectionId];
        } else {
            result[sectionId] = overrides[sectionId];
        }
    }

    return result;
};

//!Note[DND]: Drilldown-specific merge utility - handles nested structure
const mergeDrilldownConfig = (baseConfig: any, overrides: any): any => {
    if (!overrides) return baseConfig;

    const result = { ...baseConfig };

    for (const parentSectionId in overrides) {
        if (overrides[parentSectionId] === undefined) {
            delete result[parentSectionId];
        } else {
            // Ensure parent section exists in result
            if (!result[parentSectionId]) {
                result[parentSectionId] = {};
            } else {
                // Copy the parent section to avoid mutation
                result[parentSectionId] = { ...result[parentSectionId] };
            }

            // Handle the nested structure for drilldown configs
            for (const childSectionId in overrides[parentSectionId]) {
                if (overrides[parentSectionId][childSectionId] === undefined) {
                    delete result[parentSectionId][childSectionId];
                } else {
                    // Complete section replacement - no deep merging
                    result[parentSectionId][childSectionId] =
                        overrides[parentSectionId][childSectionId];
                }
            }
        }
    }

    return result;
};

export const generateChannelConfig = (
    channel: Channels | Channels[],
    filterToAppend: string,
    metricType: string,
    module: RevstackDashboardModules | SubModule,
    subModule: RevstackDashboardSubModules,
    currencySymbol: string,
    selectedSku: string,
    selectedDateRange: TSelectedDateRange
) => {
    const baseConfig = createBaseConfig(
        filterToAppend,
        metricType,
        module,
        subModule,
        currencySymbol,
        selectedSku,
        selectedDateRange
    );
    if (Array.isArray(channel)) {
        return {
            [subModule]: baseConfig,
        };
    }
    const overrides = CHANNEL_OVERRIDES[channel]?.[module]?.[subModule];
    const mergedConfig = mergeConfig(baseConfig, overrides);

    return {
        [subModule]: mergedConfig,
    };
};

export const generateDrilldownConfig = (
    channel: Channels | Channels[],
    filterToAppend: string,
    metricType: string,
    isAllSubcatSelected: boolean,
    module: RevstackDashboardModules,
    subModule: RevstackDashboardSubModules,
    currencySymbol: string
) => {
    const baseConfig = createBaseDrilldownConfig(
        filterToAppend,
        metricType,
        isAllSubcatSelected,
        module,
        subModule,
        currencySymbol
    );
    if (Array.isArray(channel)) {
        return {
            [subModule]: baseConfig,
        };
    }
    const overrides = CHANNEL_OVERRIDES_DD[channel]?.[module]?.[subModule];
    const mergedConfig = mergeDrilldownConfig(baseConfig, overrides);

    return {
        [subModule]: mergedConfig,
    };
};

export const generateDdTrendsModalConfig = (
    channel: Channels | Channels[],
    filterToAppend: string,
    metricType: string,
    isAllSubcatSelected: boolean,
    module: RevstackDashboardModules,
    subModule: RevstackDashboardSubModules
) => {
    const baseConfig = createBaseDdTrendsModalConfig(
        filterToAppend,
        metricType,
        isAllSubcatSelected,
        module,
        subModule
    );
    if (Array.isArray(channel)) {
        return {
            [subModule]: baseConfig,
        };
    }
    const overrides =
        CHANNEL_OVERRIDES_DD_TRENDS_MODAL[channel as Channels]?.[module]?.[
            subModule
        ];
    const mergedConfig = mergeDrilldownConfig(baseConfig, overrides);

    return {
        [subModule]: mergedConfig,
    };
};
